import JournalMarkdown from './JournalMarkdown';
import { printJournal } from './printJournal';
import { renderToStaticMarkup } from 'react-dom/server';
import RecordingWorkspace from './recording/RecordingWorkspace';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  query,
  runTransaction,
  updateDoc,
  deleteDoc,
  onSnapshot,
  where
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes
} from 'firebase/storage';
import {
  signInWithCustomToken,
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  LayoutDashboard,
  Home,
  BookOpen,
  UserCheck,
  ClipboardList,
  Plus,
  Trash2,
  X,
  Cloud,
  RefreshCw,
  Users,
  Calendar,
  Bell,
  CheckCircle,
  AlertCircle,
  Award,
  FileText,
  UserPlus,
  Grid,
  HelpCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Square,
  CheckSquare,
  MessageSquare,
  Reply,
  SendHorizontal,
  Pencil,
  Link2,
  Copy,
  Maximize2,
  Minimize2,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  ImagePlus,
  Sparkles,
  Mic,
  StopCircle,
  Download,
  Moon,
  Sun,
  GripVertical
} from 'lucide-react';
import { auth as firebaseAuth, db, functions as firebaseFunctions, appId, isFirebaseEnabled, storage } from './firebase.js';

const DEFAULT_CLASSES = [
  {
    id: 'class-1',
    name: 'Grade 10 Math',
    students: [
      { id: 's1', name: 'Alice Smith' },
      { id: 's2', name: 'Benjamin Carter' },
      { id: 's3', name: 'Chloe Davis' },
      { id: 's4', name: 'Daniel Evans' },
      { id: 's5', name: 'Emily Foster' }
    ]
  },
  {
    id: 'class-2',
    name: 'Grade 11 Physics',
    students: [
      { id: 's6', name: 'George Harris' },
      { id: 's7', name: 'Hannah Jackson' },
      { id: 's8', name: 'Ian Miller' },
      { id: 's9', name: 'Julia Roberts' }
    ]
  },
  {
    id: 'class-3',
    name: 'Grade 9 Chemistry',
    students: [
      { id: 's10', name: 'Kevin Thompson' },
      { id: 's11', name: 'Laura White' },
      { id: 's12', name: 'Michael Young' },
      { id: 's13', name: 'Natalie Green' },
      { id: 's14', name: 'Oliver Baker' }
    ]
  }
];

const DEFAULT_EVENTS = [
  { id: 'evt-1', title: 'Math Pop Quiz', time: '09:00 AM', description: 'Covers Algebra foundations' },
  { id: 'evt-2', title: 'Science Lab Session', time: '11:30 AM', description: 'Acid-base neutralization experiments' },
  { id: 'evt-3', title: 'Parent-Teacher Meeting', time: '03:00 PM', description: 'Zoom conferences for mid-term review' }
];

const DEFAULT_NOTES = [
  { id: 'note-1', text: 'Remember to collect permission slips for the science museum trip!', author: 'Principal Office', date: 'May 18', audiences: ['teachers', 'all-students'] },
  { id: 'note-2', text: 'Math homework submission deadline extended to Friday morning.', author: 'Mrs. Davis', date: 'May 17', audiences: ['teachers', 'all-students'] }
];

function getTodayDateValue() {
  return toDateKey(new Date());
}

function resizeTextareaToContent(textarea) {
  if (!textarea) return;
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
}

function handleTextareaInput(event) {
  resizeTextareaToContent(event.currentTarget);
}

function normalizeCardListShortcuts(value) {
  // Use a visual bullet in editable card text while retaining list semantics
  // wherever the content is displayed.
  return String(value || '').replace(/(^|\n)[*-]\s+(?=\S)/g, '$1• ');
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result || '').split(',')[1] || ''));
    reader.addEventListener('error', () => reject(reader.error || new Error('Could not read the audio recording.')));
    reader.readAsDataURL(blob);
  });
}

const DUPLICATED_LESSON_TOPIC_PLACEHOLDER = 'Topic pending';

function getLessonTopicLabel(topic) {
  return String(topic || '').trim() || DUPLICATED_LESSON_TOPIC_PLACEHOLDER;
}

function isLessonTopicPlaceholder(topic) {
  return getLessonTopicLabel(topic) === DUPLICATED_LESSON_TOPIC_PLACEHOLDER;
}

function getWeekStartDate(baseDate = new Date()) {
  const day = baseDate.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(baseDate);
  monday.setDate(baseDate.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function createEmptyLessonForm() {
  return {
    subject: '',
    topic: '',
    date: '',
    startTime: '',
    endTime: '',
    objectives: '',
    materials: '',
    notesForStudents: '',
    notesForTeachers: '',
    exitTicketId: '',
    recurrenceFrequency: 'none',
    recurrenceEndDate: ''
  };
}

function addDaysToDateValue(dateValue, dayOffset) {
  const [year, month, day] = String(dateValue || '').split('-').map(Number);
  if (!year || !month || !day) return '';
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + dayOffset);
  const nextYear = date.getFullYear();
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
}

function getRecurringLessonDates({ startDate, endDate, frequency }) {
  if (!startDate) return [];
  if (frequency === 'none') return [startDate];

  const stepDays = frequency === 'biweekly' ? 14 : 7;
  const finalDate = endDate || startDate;
  const dates = [];
  let currentDate = startDate;

  while (currentDate && currentDate <= finalDate && dates.length < 80) {
    dates.push(currentDate);
    currentDate = addDaysToDateValue(currentDate, stepDays);
  }

  return dates;
}

function getLessonDurationMs(lesson) {
  const [startHour, startMinute] = String(lesson?.startTime || '').split(':').map(Number);
  const [endHour, endMinute] = String(lesson?.endTime || '').split(':').map(Number);
  if (![startHour, startMinute, endHour, endMinute].every(Number.isFinite)) return 0;
  const minutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
  return Math.max(0, minutes * 60 * 1000);
}

function formatRecordingTime(milliseconds = 0) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function normalizeDashboardEventTime(value = '') {
  const input = String(value || '').trim();
  const twelveHourMatch = input.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelveHourMatch) {
    let hour = Number(twelveHourMatch[1]) % 12;
    if (twelveHourMatch[3].toUpperCase() === 'PM') hour += 12;
    return `${String(hour).padStart(2, '0')}:${twelveHourMatch[2]}`;
  }
  const twentyFourHourMatch = input.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (twentyFourHourMatch) {
    return `${String(Number(twentyFourHourMatch[1])).padStart(2, '0')}:${twentyFourHourMatch[2]}`;
  }
  return '';
}

function getWeekOfYear(dateValue) {
  const date = new Date(dateValue);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - ((date.getDay() + 6) % 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

function toDateKey(dateValue) {
  const date = new Date(dateValue);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey) {
  if (!dateKey) return '';
  const [y, m, day] = dateKey.split('-').map(Number);
  if (!y || !m || !day) return dateKey;
  return new Date(y, m - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function createEmptyJournalDraft() {
  return {
    title: '',
    date: getTodayDateValue(),
    tags: '',
    body: '',
    bodyHtml: '',
    transcriptChunks: [],
    journalAiSummary: ''
  };
}

function normalizeJournalTags(value = '') {
  return Array.from(new Set(
    value
      .split(',')
      .map((tag) => tag.trim().replace(/^#+/, '').toLowerCase())
      .filter(Boolean)
  ));
}

function splitInlineLatexSegments(text) {
  const input = String(text || '');
  const segments = [];
  let cursor = 0;

  while (cursor < input.length) {
    const open = input.indexOf('$', cursor);
    if (open === -1) {
      segments.push({ type: 'text', content: input.slice(cursor).replace(/\\\$/g, '$') });
      break;
    }

    if (open > 0 && input[open - 1] === '\\') {
      const prefix = input.slice(cursor, open - 1);
      if (prefix) {
        segments.push({ type: 'text', content: `${prefix}$` });
      } else {
        segments.push({ type: 'text', content: '$' });
      }
      cursor = open + 1;
      continue;
    }

    if (open > cursor) {
      segments.push({ type: 'text', content: input.slice(cursor, open).replace(/\\\$/g, '$') });
    }

    let close = open + 1;
    while (close < input.length) {
      if (input[close] === '$' && input[close - 1] !== '\\') {
        break;
      }
      close += 1;
    }

    if (close >= input.length) {
      segments.push({ type: 'text', content: input.slice(open).replace(/\\\$/g, '$') });
      break;
    }

    const latexContent = input.slice(open + 1, close).trim();
    if (latexContent) {
      segments.push({ type: 'math', content: latexContent });
    } else {
      segments.push({ type: 'text', content: '$$' });
    }

    cursor = close + 1;
  }

  return segments;
}

function renderTextWithInlineLatex(text, keyPrefix) {
  const segments = splitInlineLatexSegments(text);
  const nodes = [];

  segments.forEach((segment, index) => {
    if (!segment.content) return;

    if (segment.type === 'math') {
      const html = katex.renderToString(segment.content, {
        throwOnError: false,
        displayMode: false
      });

      nodes.push(
        <span
          key={`${keyPrefix}-math-${index}`}
          className="inline-katex"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
      return;
    }

    nodes.push(segment.content);
  });

  return nodes.length > 0 ? nodes : text;
}

function toExternalUrl(url) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function linkifyEditorHtml(value) {
  const urlPattern = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
  let cursor = 0;
  let match;
  let html = '';
  const source = String(value || '');

  while ((match = urlPattern.exec(source)) !== null) {
    html += escapeHtml(source.slice(cursor, match.index)).replace(/\n/g, '<br>');
    const rawUrl = match[0];
    const punctuationMatch = rawUrl.match(/^(.+?)([.,!?;:]+)$/);
    const url = punctuationMatch ? punctuationMatch[1] : rawUrl;
    html += `<a href="${escapeHtml(toExternalUrl(url))}" class="text-blue-600 underline hover:text-blue-800 break-all" target="_blank" rel="noreferrer">${escapeHtml(url)}</a>`;
    html += escapeHtml(punctuationMatch ? punctuationMatch[2] : '');
    cursor = match.index + rawUrl.length;
  }

  return html + escapeHtml(source.slice(cursor)).replace(/\n/g, '<br>');
}

function getRichEditorText(element) {
  return String(element?.innerText || '').replace(/\r/g, '').replace(/\n$/, '');
}

function LinkifiedTextEditor({ value, onChange, onBlur, onClick, onKeyDown, className, placeholder, rows = 3, ...props }) {
  const editorRef = useRef(null);

  useEffect(() => {
    const editor = editorRef.current;
    // Updating innerHTML while a user is typing resets the browser selection,
    // which makes subsequent characters appear at the start of the field.
    if (editor && document.activeElement === editor) return;
    if (!editor || getRichEditorText(editor) === String(value || '')) return;
    editor.innerHTML = linkifyEditorHtml(value);
  }, [value]);

  return (
    <div
      {...props}
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      data-placeholder={placeholder}
      onInput={(event) => onChange(getRichEditorText(event.currentTarget))}
      onBlur={(event) => {
        event.currentTarget.innerHTML = linkifyEditorHtml(getRichEditorText(event.currentTarget));
        onBlur?.(event);
      }}
      onClick={(event) => {
        const link = event.target.closest('a');
        if (link) {
          event.preventDefault();
          event.stopPropagation();
          window.open(link.href, '_blank', 'noopener,noreferrer');
          return;
        }
        onClick?.(event);
      }}
      onKeyDown={onKeyDown}
      className={`${className} cursor-text whitespace-pre-wrap break-words empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400`}
      style={{ minHeight: `${rows * 1.5}em` }}
    />
  );
}

function renderInlineMarkdown(text, keyPrefix) {
  const pattern = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|~~([^~]+)~~|\*([^*]+)\*|_([^_]+)_|(https?:\/\/[^\s<)]+|www\.[^\s<)]+)|(\$(?:\\.|[^$\\])+\$))/g;
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(...renderTextWithInlineLatex(text.slice(lastIndex, match.index), `${keyPrefix}-text-${match.index}`));
    }

    if (match[11] && (match.index === 0 || text[match.index - 1] !== '\\')) {
      nodes.push(...renderTextWithInlineLatex(match[11], `${keyPrefix}-latex-${match.index}`));
    } else if (match[11]) {
      nodes.push(match[11]);
    } else if (match[2] && match[3]) {
      nodes.push(
        <a key={`${keyPrefix}-link-${match.index}`} href={match[3]} target="_blank" rel="noreferrer" className="text-blue-600 underline hover:text-blue-800">
          {renderTextWithInlineLatex(match[2], `${keyPrefix}-link-text-${match.index}`)}
        </a>
      );
    } else if (match[4]) {
      nodes.push(
        <code key={`${keyPrefix}-code-${match.index}`} className="rounded bg-slate-200 px-1 py-0.5 font-mono text-[10px] text-slate-700">
          {match[4]}
        </code>
      );
    } else if (match[5]) {
      nodes.push(<strong key={`${keyPrefix}-bold-${match.index}`}>{renderTextWithInlineLatex(match[5], `${keyPrefix}-bold-text-${match.index}`)}</strong>);
    } else if (match[6]) {
      nodes.push(<strong key={`${keyPrefix}-bold-alt-${match.index}`}>{renderTextWithInlineLatex(match[6], `${keyPrefix}-bold-alt-text-${match.index}`)}</strong>);
    } else if (match[7]) {
      nodes.push(<del key={`${keyPrefix}-strike-${match.index}`}>{renderTextWithInlineLatex(match[7], `${keyPrefix}-strike-text-${match.index}`)}</del>);
    } else if (match[8] || match[9]) {
      const italicText = match[8] || match[9];
      nodes.push(<em key={`${keyPrefix}-italic-${match.index}`}>{renderTextWithInlineLatex(italicText, `${keyPrefix}-italic-text-${match.index}`)}</em>);
    } else if (match[10]) {
      const rawUrl = match[10];
      const trailingPunctuationMatch = rawUrl.match(/^(.+?)([.,!?;:]+)$/);
      const url = trailingPunctuationMatch ? trailingPunctuationMatch[1] : rawUrl;
      const trailingPunctuation = trailingPunctuationMatch ? trailingPunctuationMatch[2] : '';

      nodes.push(
        <a key={`${keyPrefix}-bare-link-${match.index}`} href={toExternalUrl(url)} target="_blank" rel="noreferrer" className="text-blue-600 underline hover:text-blue-800 break-all">
          {url}
        </a>
      );

      if (trailingPunctuation) {
        nodes.push(trailingPunctuation);
      }
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(...renderTextWithInlineLatex(text.slice(lastIndex), `${keyPrefix}-tail`));
  }

  return nodes.length > 0 ? nodes : text;
}

function injectInlineLatexIntoRichHtml(html = '') {
  const sourceHtml = String(html || '');
  if (!sourceHtml.includes('$') || typeof window === 'undefined') {
    return sourceHtml;
  }

  const container = document.createElement('div');
  container.innerHTML = sourceHtml;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const replacements = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    const textValue = currentNode.nodeValue || '';
    if (textValue.includes('$')) {
      const segments = splitInlineLatexSegments(textValue);
      if (segments.some((segment) => segment.type === 'math')) {
        replacements.push({ node: currentNode, segments });
      }
    }
    currentNode = walker.nextNode();
  }

  replacements.forEach(({ node, segments }) => {
    const fragment = document.createDocumentFragment();

    segments.forEach((segment) => {
      if (!segment.content) return;

      if (segment.type === 'math') {
        const span = document.createElement('span');
        span.className = 'inline-katex';
        span.innerHTML = katex.renderToString(segment.content, {
          throwOnError: false,
          displayMode: false
        });
        fragment.appendChild(span);
        return;
      }

      fragment.appendChild(document.createTextNode(segment.content));
    });

    node.parentNode?.replaceChild(fragment, node);
  });

  return container.innerHTML;
}

function renderMarkdown(text) {
  const lines = (text || '').split('\n');
  const nodes = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      nodes.push(<div key={`spacer-${index}`} className="h-2" />);
      continue;
    }

    if (/^```/.test(trimmed)) {
      const codeLines = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index].trim())) {
        codeLines.push(lines[index]);
        index += 1;
      }
      nodes.push(<pre key={`code-block-${index}`} className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100"><code>{codeLines.join('\n')}</code></pre>);
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      nodes.push(<hr key={`rule-${index}`} className="border-slate-200" />);
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      nodes.push(
        <blockquote key={`quote-${index}`} className="border-l-4 border-slate-300 pl-3 italic text-slate-600">
          {renderInlineMarkdown(trimmed.replace(/^>\s?/, ''), `quote-${index}`)}
        </blockquote>
      );
      continue;
    }

    const imageMatch = trimmed.match(/^!\[([^\]]*)\]\(((?:https?:\/\/|blob:|data:image\/)[^\s)]+)\)$/);
    if (imageMatch) {
      nodes.push(
        <img
          key={`img-${index}`}
          src={imageMatch[2]}
          alt={imageMatch[1] || 'Inline image'}
          className="max-h-64 max-w-full rounded border border-slate-200 bg-white object-contain"
        />
      );
      continue;
    }

    if (/^#{1,6}\s+/.test(trimmed)) {
      const level = trimmed.match(/^#+/)[0].length;
      const content = trimmed.replace(/^#{1,6}\s+/, '').replace(/\s+#+$/, '');

      if (level === 1) {
        nodes.push(<h1 key={`h1-${index}`} className="text-lg font-bold text-slate-900">{renderInlineMarkdown(content, `h1-${index}`)}</h1>);
      } else if (level === 2) {
        nodes.push(<h2 key={`h2-${index}`} className="text-base font-bold text-slate-900">{renderInlineMarkdown(content, `h2-${index}`)}</h2>);
      } else {
        nodes.push(<h3 key={`h3-${index}`} className="text-sm font-bold uppercase tracking-wider text-slate-700">{renderInlineMarkdown(content, `h3-${index}`)}</h3>);
      }
      continue;
    }

    if (/^[-*•]\s+(?:\[[ xX]\]\s+)?/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^[-*•]\s+(?:\[[ xX]\]\s+)?/.test(lines[index].trim())) {
        const sourceItem = lines[index].trim();
        const taskMatch = sourceItem.match(/^[-*•]\s+\[([ xX])\]\s+/);
        const itemText = sourceItem.replace(/^[-*•]\s+(?:\[[ xX]\]\s+)?/, '');
        items.push(
          <li key={`ul-${index}`} className="text-sm text-slate-700 leading-relaxed">
            {taskMatch ? <span className="mr-1.5" aria-hidden="true">{taskMatch[1].toLowerCase() === 'x' ? '☑' : '☐'}</span> : null}
            {renderInlineMarkdown(itemText, `ul-${index}`)}
          </li>
        );
        index += 1;
      }
      index -= 1;
      nodes.push(<ul key={`list-${index}`} className="list-disc space-y-1 pl-5">{items}</ul>);
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
        const itemText = lines[index].trim().replace(/^\d+\.\s+/, '');
        items.push(
          <li key={`ol-${index}`} className="text-sm text-slate-700 leading-relaxed">
            {renderInlineMarkdown(itemText, `ol-${index}`)}
          </li>
        );
        index += 1;
      }
      index -= 1;
      nodes.push(<ol key={`ordered-list-${index}`} className="list-decimal space-y-1 pl-5">{items}</ol>);
      continue;
    }

    nodes.push(
      <p key={`p-${index}`} className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
        {renderInlineMarkdown(trimmed, `p-${index}`)}
      </p>
    );
  }

  return nodes;
}

function getTeacherNotesExcerpt(notes, maxLength = 150) {
  const plainText = String(notes || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[#>*_~`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plainText) return 'No teacher notes.';
  return plainText.length > maxLength ? `${plainText.slice(0, maxLength).trimEnd()}…` : plainText;
}

function LessonNotePreview({ value, onChange }) {
  const dialogRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const excerpt = String(value || '').replace(/\s+/g, ' ').trim();

  const openNote = () => {
    setIsEditing(!excerpt);
    dialogRef.current?.showModal();
  };

  return (
    <>
      <button
        type="button"
        onClick={openNote}
        aria-label="Open note full screen"
        className="mt-2 w-full rounded border border-slate-200 bg-slate-50 px-2.5 py-2 text-left text-xs leading-relaxed text-slate-700 hover:border-slate-400 focus-visible:outline-indigo-500"
      >
        <span className="line-clamp-3 break-words">{excerpt ? `${excerpt.slice(0, 150)}${excerpt.length > 150 ? '…' : ''}` : 'Add a note…'}</span>
        <span className="mt-2 block text-[9px] font-bold uppercase tracking-wider text-slate-400">Open note</span>
      </button>
      <dialog
        ref={dialogRef}
        aria-label="Lesson planner note"
        className="fixed inset-0 m-0 h-[100dvh] max-h-none w-screen max-w-none bg-white p-0 text-slate-800 backdrop:bg-black/40"
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-6">
            <h2 className="text-sm font-bold">Lesson planner note</h2>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setIsEditing(!isEditing)} className="rounded border border-slate-300 px-3 py-2 text-xs font-semibold hover:bg-slate-50">
                {isEditing ? 'View note' : 'Edit note'}
              </button>
              <button type="button" onClick={() => dialogRef.current?.close()} aria-label="Close note" className="rounded p-2 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {isEditing ? (
              <textarea
                autoFocus
                aria-label="Note text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder="Type your note..."
                className="mx-auto block h-full min-h-64 w-full max-w-5xl resize-none rounded border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed focus:border-indigo-500 focus:outline-none"
              />
            ) : (
              <div className="mx-auto max-w-5xl space-y-3 break-words text-sm leading-relaxed">{value ? renderMarkdown(value) : <p className="text-slate-500">No note yet.</p>}</div>
            )}
          </div>
        </div>
      </dialog>
    </>
  );
}

function TeacherNotes({ lesson, draft, onDraftChange, onSave }) {
  const [isEditing, setIsEditing] = useState(true);
  const value = draft ?? lesson.notesForTeachers ?? '';

  const showPreview = () => {
    onSave();
    setIsEditing(false);
  };

  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Teacher Notes</p>
        <button
          type="button"
          onClick={() => isEditing ? showPreview() : setIsEditing(true)}
          className="rounded border border-indigo-200 bg-white px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-indigo-700 transition-colors hover:border-indigo-400"
        >
          {isEditing ? 'View notes' : 'Edit notes'}
        </button>
      </div>
      {isEditing ? (
        <>
          <textarea
            aria-label="Teacher notes in Markdown"
            rows="8"
            value={value}
            onChange={(event) => onDraftChange(event.target.value)}
            onBlur={onSave}
            placeholder="Private teaching notes…"
            className="mt-3 w-full resize-y rounded border border-indigo-200 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-200"
          />
          <p className="mt-2 text-[10px] leading-relaxed text-indigo-700">Formatting: **bold**, *italic*, # heading, - list, [link](https://…), and inline math with $x^2$.</p>
        </>
      ) : value ? (
        <div className="mt-3 space-y-2 text-xs text-slate-700">{renderMarkdown(value)}</div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">No private teacher notes provided.</p>
      )}
    </section>
  );
}

function ExpandedImageViewer({ image, onClose }) {
  const [zoom, setZoom] = useState(1);
  if (!image) return null;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-slate-950/95" role="dialog" aria-modal="true" aria-label="Expanded submission image" onClick={onClose}>
      <div className="flex shrink-0 items-center justify-between border-b border-white/15 px-4 py-3 text-white" onClick={(event) => event.stopPropagation()}>
        <p className="text-xs font-bold uppercase tracking-wider">{image.alt || 'Submission image'}</p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setZoom((current) => Math.max(0.5, current - 0.25))} className="rounded border border-white/30 px-3 py-1 text-sm font-bold hover:bg-white/10" aria-label="Zoom out">−</button>
          <span className="min-w-12 text-center text-xs tabular-nums">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((current) => Math.min(3, current + 0.25))} className="rounded border border-white/30 px-3 py-1 text-sm font-bold hover:bg-white/10" aria-label="Zoom in">+</button>
          <button type="button" onClick={onClose} className="ml-2 rounded p-1.5 hover:bg-white/10" aria-label="Close expanded image"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6" onClick={onClose}>
        <img
          src={image.src}
          alt={image.alt || 'Submission image'}
          onClick={(event) => event.stopPropagation()}
          style={{ transform: `scale(${zoom})` }}
          className="max-h-[80vh] max-w-[90vw] origin-center rounded bg-white object-contain transition-transform"
        />
      </div>
    </div>
  );
}

function dedupeStudentMemberships(memberships) {
  const seen = new Map();
  (memberships || []).forEach((membership) => {
    if (!membership?.classId) return;
    const key = `${membership.teacherId || ''}:${membership.classId}`;
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, membership);
      return;
    }
    const existingScore = Number(Boolean(existing.studentId)) + Number(Boolean(existing.studentName)) + Number(Boolean(existing.email));
    const nextScore = Number(Boolean(membership.studentId)) + Number(Boolean(membership.studentName)) + Number(Boolean(membership.email));
    if (nextScore > existingScore) {
      seen.set(key, { ...existing, ...membership });
    }
  });
  return Array.from(seen.values());
}

function getTimeSortValue(label = '') {
  const value = String(label || '').trim();
  if (!value) return Number.MAX_SAFE_INTEGER;

  const meridiemMatch = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (meridiemMatch) {
    let hours = Number(meridiemMatch[1]) % 12;
    const minutes = Number(meridiemMatch[2]);
    if (meridiemMatch[3].toUpperCase() === 'PM') {
      hours += 12;
    }
    return (hours * 60) + minutes;
  }

  const twentyFourHourMatch = value.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourHourMatch) {
    return (Number(twentyFourHourMatch[1]) * 60) + Number(twentyFourHourMatch[2]);
  }

  return Number.MAX_SAFE_INTEGER;
}

function normalizeNoteAudiences(value) {
  if (!Array.isArray(value) || value.length === 0) {
    return ['teachers', 'all-students'];
  }

  const uniqueValues = Array.from(new Set(value.filter((entry) => typeof entry === 'string' && entry.trim())));
  return uniqueValues.length > 0 ? uniqueValues : ['teachers', 'all-students'];
}

function normalizeForumEmails(value = '') {
  return Array.from(new Set(
    String(value)
      .split(/[\n,;\s]+/)
      .map((entry) => entry.trim().toLowerCase())
      .filter((entry) => entry && entry.includes('@'))
  ));
}

function getForumPreviewText(text, maxChars = 50) {
  const normalized = String(text || '').trim();
  if (!normalized) return 'No content.';
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, maxChars)}...`;
}

function getPlainTextFromRichHtml(html = '') {
  const normalizedHtml = String(html || '')
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6|blockquote)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ');

  if (typeof window !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = normalizedHtml;
    return (container.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  return normalizedHtml
    .replace(/<[^>]*>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function convertPlainTextToRichHtml(text = '') {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}

// Keep image sources when moving journal content through the text-only editor.
function convertJournalRichHtmlToMarkdown(html = '') {
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('[data-pocket-source]').forEach((block) => {
    block.replaceWith(document.createTextNode(`\n\n${block.getAttribute('data-pocket-source')}\n\n`));
  });
  container.querySelectorAll('img').forEach((image) => {
    const source = image.getAttribute('src');
    if (!source) return;
    const alt = (image.getAttribute('alt') || 'Pasted image').replace(/[\[\]\r\n]/g, ' ');
    const url = source.replace(/\(/g, '%28').replace(/\)/g, '%29').replace(/\s/g, encodeURIComponent);
    image.replaceWith(document.createTextNode(`\n![${alt}](${url})\n`));
  });
  // Convert each list as a unit so nested items and ordered starts survive.
  const listToMarkdown = (list, depth = 0) => {
    let number = Number(list.getAttribute('start') || 1);
    return Array.from(list.children).filter((child) => child.tagName === 'LI').map((item) => {
      if (item.hasAttribute('value')) number = Number(item.getAttribute('value'));
      const marker = list.tagName === 'OL' ? `${number++}. ` : '- ';
      const copy = item.cloneNode(true);
      const nestedLists = Array.from(copy.querySelectorAll('ol, ul')).filter((nested) => !nested.parentElement.closest('ol, ul'));
      const nestedText = nestedLists.map((nested) => listToMarkdown(nested, depth + 1));
      nestedLists.forEach((nested) => nested.remove());
      const text = getPlainTextFromRichHtml(copy.innerHTML).replace(/\n/g, `\n${'    '.repeat(depth + 1)}`);
      return `${'    '.repeat(depth)}${marker}${text}${nestedText.length ? '\n' + nestedText.join('\n') : ''}`;
    }).join('\n');
  };
  Array.from(container.querySelectorAll('ol, ul')).filter((list) => !list.parentElement.closest('ol, ul')).forEach((list) => {
    list.replaceWith(document.createTextNode(`\n\n${listToMarkdown(list)}\n\n`));
  });
  return getPlainTextFromRichHtml(container.innerHTML);
}

function convertJournalMarkdownToRichHtml(markdown = '') {
  return renderToStaticMarkup(<JournalMarkdown>{String(markdown)}</JournalMarkdown>);
}

function insertTextAtSelection(source, start, end, insertedText) {
  const rawSource = String(source || '');
  const safeStart = Number.isInteger(start) ? Math.max(0, Math.min(start, rawSource.length)) : rawSource.length;
  const safeEnd = Number.isInteger(end) ? Math.max(safeStart, Math.min(end, rawSource.length)) : safeStart;
  return `${rawSource.slice(0, safeStart)}${insertedText}${rawSource.slice(safeEnd)}`;
}

function hasMarkdownImage(value) {
  return /!\[[^\]]*]\(https?:\/\/[^\s)]+\)/.test(String(value || ''));
}

function formatNoteAudienceSummary(audiences, classes) {
  const classNameById = new Map((classes || []).map((cls) => [cls.id, cls.name]));
  return normalizeNoteAudiences(audiences).map((audience) => {
    if (audience === 'teachers') return 'Teachers';
    if (audience === 'all-students') return 'All students';
    if (audience.startsWith('class:')) {
      return classNameById.get(audience.replace('class:', '')) || 'Selected class';
    }
    return audience;
  }).join(', ');
}

function getStudentPlannerEntryId(teacherId, lessonId) {
  return `${teacherId}__${lessonId}`;
}

function createExitTicketQuestion(type = 'mcq') {
  return {
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    prompt: '',
    options: type === 'mcq' ? ['', '', '', ''] : [],
    correctOptionIndex: 0,
    answerKey: '',
    allowDrawing: false,
    feedbackCorrect: 'Great work.',
    feedbackIncorrect: 'Review this concept and try again.',
    followUpQuestion: ''
  };
}

const DEFAULT_REFLECTION_PROMPTS = {
  learned: 'One thing you learnt this lesson',
  wantToKnow: 'One thing you’d like more information on',
  revisit: 'One thing we need to go over again'
};

function normalizeReflectionPrompts(prompts) {
  return Object.fromEntries(Object.entries(DEFAULT_REFLECTION_PROMPTS).map(([field, fallback]) => [
    field,
    String(prompts?.[field] || fallback).trim() || fallback
  ]));
}

function createEmptyExitTicket() {
  return {
    enabled: false,
    title: 'Exit Ticket',
    instructions: '',
    aiSystemPrompt: '',
    reflectionEnabled: false,
    reflectionOnly: false,
    reflectionPrompts: { ...DEFAULT_REFLECTION_PROMPTS },
    questions: []
  };
}

function createEmptyExitTicketTemplateForm() {
  return {
    topic: '',
    title: '',
    instructions: '',
    aiSystemPrompt: '',
    reflectionEnabled: true,
    reflectionOnly: false,
    reflectionPrompts: { ...DEFAULT_REFLECTION_PROMPTS },
    questions: []
  };
}

function normalizeExitTicketConfig(ticket) {
  const fallback = createEmptyExitTicket();
  if (!ticket || typeof ticket !== 'object') {
    return fallback;
  }

  const normalizedQuestions = Array.isArray(ticket.questions)
    ? ticket.questions
      .filter((question) => question && typeof question === 'object')
      .map((question) => {
        const type = ['mcq', 'fib', 'short'].includes(question.type) ? question.type : 'mcq';
        const options = type === 'mcq'
          ? (Array.isArray(question.options) ? question.options.slice(0, 6) : ['', '', '', ''])
            .map((option) => String(option || ''))
          : [];
        return {
          id: question.id || `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type,
          prompt: String(question.prompt || ''),
          options,
          correctOptionIndex: Number.isInteger(question.correctOptionIndex) ? question.correctOptionIndex : 0,
          answerKey: String(question.answerKey || ''),
          allowDrawing: type === 'short' ? Boolean(question.allowDrawing) : false,
          feedbackCorrect: String(question.feedbackCorrect || 'Great work.'),
          feedbackIncorrect: String(question.feedbackIncorrect || 'Review this concept and try again.'),
          followUpQuestion: String(question.followUpQuestion || '')
        };
      })
    : [];

  return {
    enabled: Boolean(ticket.enabled),
    title: String(ticket.title || fallback.title),
    instructions: String(ticket.instructions || ''),
    aiSystemPrompt: String(ticket.aiSystemPrompt || ''),
    reflectionEnabled: ticket.reflectionEnabled !== false,
    reflectionOnly: Boolean(ticket.reflectionOnly),
    reflectionPrompts: normalizeReflectionPrompts(ticket.reflectionPrompts),
    questions: normalizedQuestions
  };
}

function sanitizeExitTicketConfig(ticket) {
  const normalized = normalizeExitTicketConfig(ticket);
  const cleanedQuestions = normalized.questions
    .map((question) => {
      const prompt = String(question.prompt || '').trim();
      if (!prompt) return null;

      if (question.type === 'mcq') {
        const cleanedOptions = (question.options || []).map((option) => String(option || '').trim()).filter(Boolean);
        if (cleanedOptions.length < 2) return null;
        const boundedIndex = Math.max(0, Math.min(Number(question.correctOptionIndex) || 0, cleanedOptions.length - 1));
        return {
          ...question,
          prompt,
          options: cleanedOptions,
          correctOptionIndex: boundedIndex,
          answerKey: cleanedOptions[boundedIndex] || ''
        };
      }

      return {
        ...question,
        prompt,
        options: [],
        answerKey: String(question.answerKey || '').trim()
      };
    })
    .filter(Boolean);

  return {
    ...normalized,
    enabled: normalized.enabled && (cleanedQuestions.length > 0 || (normalized.reflectionOnly && normalized.reflectionEnabled)),
    title: String(normalized.title || 'Exit Ticket').trim() || 'Exit Ticket',
    instructions: String(normalized.instructions || '').trim(),
    aiSystemPrompt: String(normalized.aiSystemPrompt || '').trim(),
    reflectionEnabled: Boolean(normalized.reflectionEnabled),
    reflectionOnly: Boolean(normalized.reflectionOnly),
    reflectionPrompts: normalizeReflectionPrompts(normalized.reflectionPrompts),
    questions: cleanedQuestions
  };
}

function normalizeAnswerText(value) {
  return String(value || '').trim().toLowerCase();
}

function isExitTicketQuestionCorrect(question, answer) {
  if (!question) return false;
  if (question.type === 'mcq') {
    const expected = String(question.options?.[Number(question.correctOptionIndex)] || '').trim();
    return normalizeAnswerText(answer) === normalizeAnswerText(expected);
  }

  const normalizedAnswer = normalizeAnswerText(answer);
  if (!normalizedAnswer) return false;
  const acceptedAnswers = String(question.answerKey || '')
    .split('|')
    .map((item) => normalizeAnswerText(item))
    .filter(Boolean);
  if (acceptedAnswers.length === 0) return false;
  return acceptedAnswers.includes(normalizedAnswer);
}

function getExitTicketAnswerFinalCorrect(answerItem) {
  if (typeof answerItem?.manualIsCorrect === 'boolean') {
    return answerItem.manualIsCorrect;
  }
  if (typeof answerItem?.finalIsCorrect === 'boolean') {
    return answerItem.finalIsCorrect;
  }
  if (typeof answerItem?.isCorrect === 'boolean') {
    return answerItem.isCorrect;
  }
  return false;
}

function getExitTicketFeedbackText(answerItem) {
  if (!answerItem) return '';
  if (answerItem.type === 'short' && answerItem.aiFeedbackStatus === 'pending') {
    return 'AI feedback is being generated...';
  }
  return answerItem.teacherFeedback || answerItem.aiSuggestion?.feedback || answerItem.feedback || '';
}

function ExitTicketDrawingCanvas({ value, onChange, disabled = false }) {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);

  const paintBlankCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));

    const context = canvas.getContext('2d');
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 3;
    context.strokeStyle = '#0f172a';
    paintBlankCanvas();

    if (value) {
      const image = new Image();
      image.onload = () => {
        paintBlankCanvas();
        context.drawImage(image, 0, 0, rect.width, rect.height);
      };
      image.src = value;
    }
  }, [value]);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const saveDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    onChange(canvas.toDataURL('image/png'));
  };

  const handlePointerDown = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    isDrawingRef.current = true;
    lastPointRef.current = getPoint(event);
  };

  const handlePointerMove = (event) => {
    if (disabled || !isDrawingRef.current) return;
    event.preventDefault();

    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    const lastPoint = lastPointRef.current;
    const nextPoint = getPoint(event);
    if (!canvas || !context || !lastPoint || !nextPoint) return;

    context.beginPath();
    context.moveTo(lastPoint.x, lastPoint.y);
    context.lineTo(nextPoint.x, nextPoint.y);
    context.stroke();
    lastPointRef.current = nextPoint;
  };

  const handlePointerUp = (event) => {
    if (!isDrawingRef.current) return;
    event.preventDefault();
    isDrawingRef.current = false;
    lastPointRef.current = null;
    saveDrawing();
  };

  return (
    <div className="space-y-1.5">
      <canvas
        ref={canvasRef}
        className={`h-36 w-full rounded border border-amber-200 bg-white ${disabled ? 'opacity-70' : 'cursor-crosshair'}`}
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      {!disabled ? (
        <button
          type="button"
          onClick={() => {
            paintBlankCanvas();
            onChange?.('');
          }}
          className="text-[9px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900"
        >
          Clear Drawing
        </button>
      ) : null}
    </div>
  );
}

function summarizeExitTicketAnswers(answerItems = []) {
  const totalQuestions = answerItems.length;
  const correctCount = answerItems.reduce((count, answerItem) => (
    getExitTicketAnswerFinalCorrect(answerItem) ? count + 1 : count
  ), 0);
  const pendingManualCount = answerItems.filter(
    (answerItem) => answerItem?.needsManualReview && answerItem?.manualReviewStatus !== 'completed'
  ).length;

  return {
    totalQuestions,
    correctCount,
    pendingManualCount,
    scorePercent: totalQuestions ? Math.round((correctCount / totalQuestions) * 100) : 0
  };
}

function getDisplayEventDescription(description) {
  const normalized = String(description || '').trim();
  if (!normalized) return '';
  if (normalized.toLowerCase() === 'no description provided') return '';
  return normalized;
}

const FORUM_AI_POSITIVE_TERMS = [
  'good', 'great', 'clear', 'helpful', 'thanks', 'thank', 'agree', 'improve', 'solution', 'correct', 'well', 'strong'
];

const FORUM_AI_NEGATIVE_TERMS = [
  'bad', 'wrong', 'confusing', 'useless', 'stupid', 'hate', 'angry', 'annoying', 'never', 'impossible', 'dumb'
];

const FORUM_AI_CONSTRUCTIVE_TERMS = [
  'because', 'therefore', 'for example', 'evidence', 'suggest', 'recommend', 'consider', 'explain', 'reason'
];

function clampForumAiScore(value, min = 0, max = 100) {
  return Math.min(max, Math.max(min, value));
}

function getForumAiQualityBand(score) {
  if (score >= 80) return 'Excellent';
  if (score >= 65) return 'Strong';
  if (score >= 50) return 'Developing';
  return 'Needs support';
}

function countForumAiTermHits(text, terms) {
  const normalized = String(text || '').toLowerCase();
  if (!normalized) return 0;
  return terms.reduce((total, term) => {
    if (term.includes(' ')) {
      return total + (normalized.includes(term) ? 1 : 0);
    }
    const matches = normalized.match(new RegExp(`\\b${term}\\b`, 'g'));
    return total + (matches ? matches.length : 0);
  }, 0);
}

function getForumAiWordCount(text) {
  const words = String(text || '').trim().match(/\b[\w'-]+\b/g);
  return words ? words.length : 0;
}

const STUDENT_EMAIL_DOMAINS = ['@students.edu.sg', '@myhub.test'];
const STUDENT_EMAIL_DOMAIN_LABEL = STUDENT_EMAIL_DOMAINS.join(' or ');
const DEFAULT_HOMEWORK_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1eOVvZnwSeIjkz3MkZAxeTIAhQDltW5nyznYIrfb3cnU/edit?gid=0';

function isAllowedStudentEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return STUDENT_EMAIL_DOMAINS.some((domain) => normalized.length > domain.length && normalized.endsWith(domain));
}

function compareAssignmentsAlphabetically(a, b) {
  const titleDiff = String(a?.title || '').localeCompare(String(b?.title || ''), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
  if (titleDiff !== 0) return titleDiff;
  const classDiff = String(a?.className || '').localeCompare(String(b?.className || ''), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
  if (classDiff !== 0) return classDiff;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

export default function App() {


  const [user, setUser] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const savedTheme = window.localStorage.getItem('phyhub-theme');
    return savedTheme ? savedTheme === 'dark' : window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  });
  const [activeTab, setActiveTab] = useState('lessons');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [journals, setJournals] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [studentPlannerEntries, setStudentPlannerEntries] = useState([]);
  const [studentPlannerEvents, setStudentPlannerEvents] = useState([]);
  const [lessonTodos, setLessonTodos] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [homework, setHomework] = useState([]);
  const [toast, setToast] = useState(null);
  const [isSyncing, setIsSyncing] = useState(true);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(() => getTodayDateValue());
  const [homeworkForm, setHomeworkForm] = useState({ title: '', dueDate: '', maxPoints: '10' });
  const [homeworkBatchStatus, setHomeworkBatchStatus] = useState({});
  const [homeworkScorePaste, setHomeworkScorePaste] = useState({});
  const [homeworkSheetUrl, setHomeworkSheetUrl] = useState(DEFAULT_HOMEWORK_SHEET_URL);
  const [homeworkSheetCsv, setHomeworkSheetCsv] = useState('');
  const [homeworkSheetImportLog, setHomeworkSheetImportLog] = useState('');
  const [homeworkSheetImportErrors, setHomeworkSheetImportErrors] = useState([]);
  const [isImportingHomeworkSheet, setIsImportingHomeworkSheet] = useState(false);
  const [selectedHomeworkEditId, setSelectedHomeworkEditId] = useState('');
  const [homeworkEditForm, setHomeworkEditForm] = useState({ title: '', dueDate: '', maxPoints: '10', trackingMode: 'score' });
  const [lessonForm, setLessonForm] = useState(createEmptyLessonForm);
  const [exitTickets, setExitTickets] = useState([]);
  const [selectedExitTicketLibraryId, setSelectedExitTicketLibraryId] = useState('');
  const [exitTicketLibraryForm, setExitTicketLibraryForm] = useState(createEmptyExitTicketTemplateForm);
  const exitTicketEditorRef = useRef(null);
  const exitTicketTopicInputRef = useRef(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedLessonSectionIndex, setSelectedLessonSectionIndex] = useState(0);
  const [editingLessonId, setEditingLessonId] = useState('');
  const [duplicatingLessonDate, setDuplicatingLessonDate] = useState('');
  const [lessonUploadFiles, setLessonUploadFiles] = useState([]);
  const [removedAttachmentUrls, setRemovedAttachmentUrls] = useState([]);
  const [lessonTodoInputByDate, setLessonTodoInputByDate] = useState({});
  const [lessonTodoDrafts, setLessonTodoDrafts] = useState({});
  const [lessonNoteDrafts, setLessonNoteDrafts] = useState({});
  const [googleCalendarUrl, setGoogleCalendarUrl] = useState('');
  const [isImportingGoogleCalendar, setIsImportingGoogleCalendar] = useState(false);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [googleCalendarLastSynced, setGoogleCalendarLastSynced] = useState(null);
  const [googleCalendarSyncError, setGoogleCalendarSyncError] = useState('');
  const googleCalendarSyncRef = useRef(false);
  const [isRemovingGoogleCalendarLessons, setIsRemovingGoogleCalendarLessons] = useState(false);
  const [studentEventInputByDate, setStudentEventInputByDate] = useState({});
  const [showStudentEventModal, setShowStudentEventModal] = useState(false);
  const [editingStudentEventId, setEditingStudentEventId] = useState('');
  const [studentEventModalForm, setStudentEventModalForm] = useState({
    date: getTodayDateValue(),
    title: '',
    time: '',
    description: ''
  });
  const [draggingTodoId, setDraggingTodoId] = useState('');
  const [draggingLessonNoteId, setDraggingLessonNoteId] = useState('');
  const [lessonWeekStart, setLessonWeekStart] = useState(() => getWeekStartDate(new Date()));
  const [lessonPlannerView, setLessonPlannerView] = useState('week');
  const [lessonTeacherNoteDrafts, setLessonTeacherNoteDrafts] = useState({});
  const [studentPlannerWeekStart, setStudentPlannerWeekStart] = useState(() => getWeekStartDate(new Date()));
  const [eventForm, setEventForm] = useState({ title: '', time: '', description: '' });
  const [noteText, setNoteText] = useState('');
  const [noteAuthor, setNoteAuthor] = useState('Teacher Mode');
  const [selectedNoteAudiences, setSelectedNoteAudiences] = useState(['teachers', 'all-students']);
  const [isNoteAudienceMenuOpen, setIsNoteAudienceMenuOpen] = useState(false);
  const [journalForm, setJournalForm] = useState(createEmptyJournalDraft);
  const [journalEditorMode, setJournalEditorMode] = useState('markdown');
  const [selectedJournalId, setSelectedJournalId] = useState('');
  const [journalSearch, setJournalSearch] = useState('');
  const [isJournalEditorOpen, setIsJournalEditorOpen] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [newClassName, setNewClassName] = useState('');
  const [bulkCsvText, setBulkCsvText] = useState('');
  const [bulkImportLog, setBulkImportLog] = useState('');
  const [bulkImportErrors, setBulkImportErrors] = useState([]);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authPortal, setAuthPortal] = useState('student');
  const [authError, setAuthError] = useState('');
  const [authNotice, setAuthNotice] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authStateChecked, setAuthStateChecked] = useState(false);
  const [studentProvisioningEmail, setStudentProvisioningEmail] = useState('');
  const [classTemporaryPasswords, setClassTemporaryPasswords] = useState({});
  const [classTemporaryPasswordLoading, setClassTemporaryPasswordLoading] = useState('');
  const [studentProfile, setStudentProfile] = useState(null);
  const [exitTicketDrafts, setExitTicketDrafts] = useState({});
  const [exitTicketResponses, setExitTicketResponses] = useState({});
  const [teacherExitTicketSubmissions, setTeacherExitTicketSubmissions] = useState([]);
  const [exitTicketAiGradingLoading, setExitTicketAiGradingLoading] = useState({});
  const [reflectionDashboardByLesson, setReflectionDashboardByLesson] = useState({});
  const [expandedSubmissionImage, setExpandedSubmissionImage] = useState(null);
  const [lessonRecording, setLessonRecording] = useState(null);
  const [lessonRecordingElapsedMs, setLessonRecordingElapsedMs] = useState(0);
  const [lessonRecordingError, setLessonRecordingError] = useState('');
  const [showLessonTranscript, setShowLessonTranscript] = useState(false);
  const lessonRecorderRef = useRef(null);
  const lessonRecordingStreamRef = useRef(null);
  const lessonNoteSaveTimeoutsRef = useRef({});
  const lessonRecordingTimerRef = useRef(null);
  const lessonRecordingTimeoutRef = useRef(null);
  const lessonChunkQueueRef = useRef(Promise.resolve());
  const lessonChunkIndexRef = useRef(0);
  const lessonRecordingStartedAtRef = useRef(0);
  const lessonRecordingShouldContinueRef = useRef(false);
  const [forumRooms, setForumRooms] = useState([]);
  const [selectedForumRoomId, setSelectedForumRoomId] = useState('');
  const [editingForumRoomId, setEditingForumRoomId] = useState('');
  const [forumRoomForm, setForumRoomForm] = useState({
    title: '',
    description: '',
    allowedClassIds: [],
    allowedEmails: ''
  });
  const [forumPostsByRoom, setForumPostsByRoom] = useState({});
  const [forumThreadForm, setForumThreadForm] = useState({ body: '', imageUrl: '', externalUrl: '' });
  const [forumReplyDrafts, setForumReplyDrafts] = useState({});
  const [editingForumPostId, setEditingForumPostId] = useState('');
  const [forumPostEditDrafts, setForumPostEditDrafts] = useState({});
  const [forumLinkModal, setForumLinkModal] = useState({
    isOpen: false,
    url: '',
    text: '',
    editorElement: null,
    onEditorChange: null
  });
  const [forumAiModal, setForumAiModal] = useState({
    isOpen: false,
    mode: 'thread',
    title: '',
    data: null,
    generatedAt: 0
  });
  const [forumAiLoading, setForumAiLoading] = useState(false);
  const [forumStudentAiLoadingRootId, setForumStudentAiLoadingRootId] = useState('');
  const [forumStudentAiUsageByThread, setForumStudentAiUsageByThread] = useState({});
  const [forumAiPromptModal, setForumAiPromptModal] = useState({
    isOpen: false,
    threadRoot: null,
    customPrompt: ''
  });
  const [isEditingRoomDescription, setIsEditingRoomDescription] = useState(false);
  const [roomDescriptionDraft, setRoomDescriptionDraft] = useState('');
  const [expandedForumCards, setExpandedForumCards] = useState({});
  const [forumExpandAll, setForumExpandAll] = useState(false);
  const forumThreadTextareaRef = useRef(null);
  const forumThreadImageInputRef = useRef(null);
  const forumReplyTextareaRefs = useRef({});
  const forumReplyImageInputRefs = useRef({});
  const forumPostEditTextareaRefs = useRef({});
  const forumPostEditImageInputRefs = useRef({});
  const journalEditorRef = useRef(null);
  const journalAutosaveTimeoutRef = useRef(null);
  const journalLastSavedFingerprintRef = useRef('');
  const plannerPublishFingerprintRef = useRef({});
  const forumStandaloneRoomKey = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    return params.get('room') || '';
  }, []);
  const isForumStandalone = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('forum') === '1';
  }, []);
  const forumBaseUrl = useMemo(() => {
    if (typeof window === 'undefined') return '/';
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('forum');
    nextUrl.searchParams.delete('room');
    return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.style.colorScheme = isDarkMode ? 'dark' : 'light';
    window.localStorage.setItem('phyhub-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const renderThemeToggle = () => (
    <button
      type="button"
      onClick={() => setIsDarkMode((current) => !current)}
      className="theme-toggle inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950"
      aria-label={isDarkMode ? 'Use light mode' : 'Use dark mode'}
      title={isDarkMode ? 'Use light mode' : 'Use dark mode'}
    >
      {isDarkMode ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
    </button>
  );

  useEffect(() => {
    setSelectedLessonSectionIndex(0);
    setShowLessonTranscript(false);
  }, [selectedLesson?.id]);

  useEffect(() => {
    if (!selectedLesson?.id) return;
    const currentLesson = lessons.find((lesson) => lesson.id === selectedLesson.id);
    if (currentLesson && currentLesson !== selectedLesson) setSelectedLesson(currentLesson);
  }, [lessons, selectedLesson]);

  // Show demo data only in offline mode (not authenticated, not Firebase enabled)
  useEffect(() => {
    if (!isFirebaseEnabled && !user) {
      setClasses(DEFAULT_CLASSES);
      setEvents(DEFAULT_EVENTS);
      setNotes(DEFAULT_NOTES);
      setJournals([]);
    } else {
      setClasses([]);
      setEvents([]);
      setNotes([]);
      setJournals([]);
    }
  }, [isFirebaseEnabled, user]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3600);
  };

  const handleSignInWithGoogle = async () => {
    if (!firebaseAuth) {
      setAuthError('Authentication is not available.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    setAuthNotice('');

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(firebaseAuth, provider);
      showToast('Signed in with Google.');
    } catch (error) {
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        setAuthError('Google sign-in was cancelled. Please try again.');
      } else if (error?.code === 'auth/popup-blocked') {
        setAuthError('Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.');
      } else if (error?.code === 'auth/unauthorized-domain') {
        setAuthError('This website domain has not been authorized for Google sign-in in Firebase.');
      } else {
        setAuthError(error.message || 'Google sign-in failed.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!firebaseAuth) {
      setAuthError('Authentication is not available.');
      return;
    }

    const email = authEmail.trim();
    const password = authPassword;

    if (!email || !password) {
      setAuthError('Email and password are required.');
      return;
    }
    if (authPortal === 'student' && !isAllowedStudentEmail(email)) {
      setAuthError(`Student emails must end with ${STUDENT_EMAIL_DOMAIN_LABEL}.`);
      return;
    }

    setAuthLoading(true);
    setAuthError('');
    setAuthNotice('');

    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      showToast('Signed in successfully.');
    } catch (signInError) {
      if (authPortal === 'student') {
        try {
          if (!firebaseFunctions) throw signInError;
          const classSignIn = httpsCallable(firebaseFunctions, 'signInWithClassPassword');
          const result = await classSignIn({ email: email.toLowerCase(), password: password.trim() });
          if (!result.data?.token) throw signInError;
          await signInWithCustomToken(firebaseAuth, result.data.token);
          showToast('Signed in with the temporary class password.');
        } catch {
          setAuthError('Incorrect password, or the temporary class password has expired. First-time students can also use Activate account.');
        }
      } else if (signInError?.code === 'auth/invalid-credential' || signInError?.code === 'auth/user-not-found') {
        try {
          await createUserWithEmailAndPassword(firebaseAuth, email, password);
          showToast('Account created and signed in.');
        } catch (createError) {
          setAuthError(createError.message || 'Email sign-in failed.');
        }
      } else {
        setAuthError(signInError.message || 'Email sign-in failed.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSetClassTemporaryPassword = async (cls) => {
    if (!firebaseFunctions || !cls?.id) return;
    const password = String(classTemporaryPasswords[cls.id] || '').trim();
    if (password.length < 8) {
      showToast('Use at least 8 characters for the class password.', 'error');
      return;
    }
    setClassTemporaryPasswordLoading(cls.id);
    try {
      const setTemporaryPassword = httpsCallable(firebaseFunctions, 'setClassTemporaryPassword');
      await setTemporaryPassword({ classId: cls.id, password });
      setClassTemporaryPasswords((prev) => ({ ...prev, [cls.id]: '' }));
      showToast(`Temporary password enabled for ${cls.name} for 1 hour.`);
    } catch (error) {
      showToast(error.message || 'Could not set the class password.', 'error');
    } finally {
      setClassTemporaryPasswordLoading('');
    }
  };

  const handleStudentActivation = async () => {
    if (!firebaseAuth || !firebaseFunctions) {
      setAuthError('Authentication is not available.');
      return;
    }

    const email = authEmail.trim().toLowerCase();
    if (!email) {
      setAuthError('Enter your school email first.');
      return;
    }
    if (!isAllowedStudentEmail(email)) {
      setAuthError(`Student emails must end with ${STUDENT_EMAIL_DOMAIN_LABEL}.`);
      return;
    }

    setAuthLoading(true);
    setAuthError('');
    setAuthNotice('');

    try {
      const provisionStudentAccess = httpsCallable(firebaseFunctions, 'provisionStudentAccess');
      await provisionStudentAccess({ email });
      await sendPasswordResetEmail(firebaseAuth, email);
      setAuthNotice('If your account is ready, an activation email has been sent. Use that link to set your password, then sign in here with email.');
    } catch (error) {
      if (error?.code === 'auth/invalid-email') {
        setAuthError('Enter a valid school email address.');
      } else if (error?.code?.startsWith('functions/')) {
        setAuthError('Student activation is temporarily unavailable. Please try again shortly.');
      } else {
        setAuthNotice('If your account is ready, an activation email has been sent. Use that link to set your password, then sign in here with email.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleProvisionStudentAccess = async (cls, student) => {
    if (!user || !firebaseAuth || !firebaseFunctions) {
      showToast('Student provisioning is not available.', 'error');
      return;
    }

    const email = (student.email || '').trim().toLowerCase();
    if (!email) {
      showToast('This student needs an email address first.', 'error');
      return;
    }

    const teacherId = user.uid;
    const provisionId = encodeURIComponent(email);

    setStudentProvisioningEmail(email);
    try {
      const provisionStudentAccess = httpsCallable(firebaseFunctions, 'provisionStudentAccess');
      await provisionStudentAccess({ email });

      await sendPasswordResetEmail(firebaseAuth, email);

      await setDoc(doc(db, 'teachers', teacherId, 'studentAccess', provisionId), {
        email,
        studentId: student.id,
        studentName: student.name,
        classId: cls.id,
        className: cls.name,
        activationSentAt: Date.now(),
        activationSentBy: teacherId,
        status: 'activation-sent'
      }, { merge: true });

      showToast(`Activation email sent to ${email}.`);
    } catch (error) {
      showToast(error.message || 'Could not provision student access.', 'error');
    } finally {
      setStudentProvisioningEmail('');
    }
  };

  const handleContinueAsGuest = async () => {
    if (!firebaseAuth) {
      setAuthError('Authentication is not available.');
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      await signInAnonymously(firebaseAuth);
      showToast('Continuing as guest.');
    } catch (error) {
      setAuthError(error.message || 'Guest sign-in failed.');
    } finally {
      setAuthLoading(false);
    }
  };

  const renderTextWithLinks = (text, onLinkClick) => {
    if (!text) return null;
      const parts = String(text).split(/(https?:\/\/[^\s)]+|www\.[^\s)]+)/g);
    return parts.map((part, index) => {
      if (/^(https?:\/\/|www\.)/i.test(part)) {
        return (
          <a
            key={`${part}-${index}`}
            href={toExternalUrl(part)}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              if (onLinkClick) onLinkClick();
            }}
            className="text-blue-600 hover:text-blue-800 underline break-all"
          >
            {part}
          </a>
        );
      }
      return <span key={`text-${index}`}>{part}</span>;
    });
  };


  const handleSignOut = async () => {
    try {
      await signOut(firebaseAuth);
      setUser(null);
      setActiveTab('dashboard');
      showToast('Signed out successfully.');
    } catch (error) {
      showToast('Sign out failed.', 'error');
    }
  };

  useEffect(() => {
    if (!isFirebaseEnabled) {
      showToast('Firebase is not configured. Offline demo mode enabled.', 'error');
      setIsSyncing(false);
      setAuthStateChecked(true);
      return;
    }

    const initAuth = async () => {
      if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
        try {
          await signInWithCustomToken(firebaseAuth, __initial_auth_token);
        } catch (tokenError) {
          console.warn('Custom token sign-in failed:', tokenError);
        }
      }
    };

    initAuth();
    if (!firebaseAuth) {
      setAuthStateChecked(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(firebaseAuth, (usr) => {
      setUser(usr);
      setAuthStateChecked(true);
    }, (error) => {
      console.error('Auth state failed:', error);
      setAuthStateChecked(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setClasses([]);
    setLessons([]);
    setExitTickets([]);
    setStudentPlannerEntries([]);
    setStudentPlannerEvents([]);
    setLessonTodos([]);
    setAttendance([]);
    setHomework([]);
    setStudentProfile(null);
    setExitTicketDrafts({});
    setExitTicketResponses({});
    setTeacherExitTicketSubmissions([]);
    setSelectedExitTicketLibraryId('');
    setExitTicketLibraryForm(createEmptyExitTicketTemplateForm());
    setSelectedClassId('');
    setForumRooms([]);
    setSelectedForumRoomId('');
    setEditingForumRoomId('');
    setForumRoomForm({ title: '', description: '', allowedClassIds: [], allowedEmails: '' });
    setForumPostsByRoom({});
    setForumThreadForm({ body: '', imageUrl: '', externalUrl: '' });
    setForumReplyDrafts({});
    setExpandedForumCards({});
    setForumExpandAll(false);
  }, [user?.uid]);

  useEffect(() => {
    if (!user || !isFirebaseEnabled) return;
    setIsSyncing(true);

    const profileRef = doc(db, 'studentProfiles', user.uid);
    const sharedEventsRef = collection(db, 'artifacts', appId, 'public', 'data', 'events');
    const sharedNotesRef = collection(db, 'artifacts', appId, 'public', 'data', 'notes');
    let cleanup = () => {};

    const unsubEvents = onSnapshot(sharedEventsRef, (snapshot) => {
      const nextEvents = [];
      snapshot.forEach((docItem) => nextEvents.push({ id: docItem.id, ...docItem.data() }));
      setEvents(nextEvents);
    }, (error) => console.error('Events sync error:', error));

    const unsubNotes = onSnapshot(sharedNotesRef, (snapshot) => {
      const nextNotes = [];
      snapshot.forEach((docItem) => nextNotes.push({ id: docItem.id, ...docItem.data() }));
      setNotes(nextNotes);
    }, (error) => console.error('Notes sync error:', error));

    const unsubProfile = onSnapshot(profileRef, (profileSnapshot) => {
      const profileData = profileSnapshot.exists() ? { id: profileSnapshot.id, ...profileSnapshot.data() } : null;
      setStudentProfile(profileData);

      cleanup();

      if (profileData) {
        setLessons([]);
        setLessonTodos([]);
        setAttendance([]);

        const memberships = dedupeStudentMemberships(profileData.memberships || []);
        const classUnsubs = [];
        const assignmentUnsubs = [];
        const plannerUnsubs = [];
        const responseUnsubs = [];
        const todoUnsubs = [];
        const eventUnsubs = [];
        const classBuckets = new Map();
        const assignmentBuckets = new Map();
        const plannerBuckets = new Map();
        const responseBuckets = new Map();
        const updateStudentClasses = () => {
          const items = Array.from(classBuckets.values());
          setClasses(items);
          if (items.length > 0) {
            setSelectedClassId((current) => current || items[0].id);
          }
        };
        const updateStudentHomework = () => {
          const items = Array.from(assignmentBuckets.values()).flat().sort(compareAssignmentsAlphabetically);
          setHomework(items);
        };
        const updateStudentPlannerEntries = () => {
          const items = Array.from(plannerBuckets.values())
            .flat()
            .sort((a, b) => {
              const dateDiff = (a.date || '').localeCompare(b.date || '');
              if (dateDiff !== 0) return dateDiff;
              return (a.startTime || '').localeCompare(b.startTime || '');
            });
          setStudentPlannerEntries(items);
        };
        const updateStudentExitTicketResponses = () => {
          const responseMap = {};
          Array.from(responseBuckets.values())
            .flat()
            .forEach((submission) => {
              if (!submission?.entryId) return;
              const current = responseMap[submission.entryId];
              if (!current || Number(submission.submittedAt || 0) >= Number(current.submittedAt || 0)) {
                responseMap[submission.entryId] = submission;
              }
            });
          setExitTicketResponses(responseMap);
        };

        memberships.forEach((membership) => {
          const classRef = doc(db, 'teachers', membership.teacherId, 'classes', membership.classId);
          const unsub = onSnapshot(classRef, (classSnapshot) => {
            if (!classSnapshot.exists()) {
              classBuckets.delete(`${membership.teacherId}:${membership.classId}`);
              updateStudentClasses();
              return;
            }
            classBuckets.set(`${membership.teacherId}:${membership.classId}`, {
              id: classSnapshot.id,
              teacherId: membership.teacherId,
              ...classSnapshot.data()
            });
            updateStudentClasses();
          }, (error) => console.error('Student classes sync error:', error));
          classUnsubs.push(unsub);
        });

        const membershipKey = (membership) => `${membership.teacherId}:${membership.classId}`;
        if (!memberships.length) {
          setClasses([]);
          setHomework([]);
          setStudentPlannerEntries([]);
          setStudentPlannerEvents([]);
          setExitTicketResponses({});
        } else {
          memberships.forEach((membership) => {
            const key = membershipKey(membership);
            const assignmentsQuery = query(
              collection(db, 'teachers', membership.teacherId, 'assignments'),
              where('classId', '==', membership.classId)
            );
            const unsub = onSnapshot(assignmentsQuery, (assignmentSnapshot) => {
              const nextAssignments = [];
              assignmentSnapshot.forEach((assignmentDoc) => {
                const assignmentData = assignmentDoc.data();
                nextAssignments.push({
                  id: assignmentDoc.id,
                  teacherId: membership.teacherId,
                  ...assignmentData
                });
              });
              assignmentBuckets.set(key, nextAssignments);
              updateStudentHomework();
            }, (error) => console.error('Student assignments sync error:', error));
            assignmentUnsubs.push(unsub);

            const plannerEntriesQuery = query(
              collection(db, 'artifacts', appId, 'public', 'data', 'plannerEntries'),
              where('teacherId', '==', membership.teacherId),
              where('classId', '==', membership.classId)
            );
            const unsubPlanner = onSnapshot(plannerEntriesQuery, (plannerSnapshot) => {
              const nextEntries = [];
              plannerSnapshot.forEach((plannerDoc) => {
                const plannerData = plannerDoc.data();
                if (plannerData.teacherId !== membership.teacherId) return;
                nextEntries.push({
                  id: plannerDoc.id,
                  ...plannerData
                });
              });
              plannerBuckets.set(key, nextEntries);
              updateStudentPlannerEntries();
            }, (error) => console.error('Student planner sync error:', error));
            plannerUnsubs.push(unsubPlanner);

            const responseQuery = query(
              collection(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses'),
              where('teacherId', '==', membership.teacherId),
              where('studentUid', '==', user.uid)
            );
            const unsubResponse = onSnapshot(responseQuery, (responseSnapshot) => {
              const nextResponses = [];
              responseSnapshot.forEach((responseDoc) => {
                const responseData = responseDoc.data();
                if (responseData.classId !== membership.classId) return;
                nextResponses.push({
                  id: responseDoc.id,
                  ...responseData
                });
              });
              responseBuckets.set(key, nextResponses);
              updateStudentExitTicketResponses();
            }, (error) => console.error('Student exit-ticket sync error:', error));
            responseUnsubs.push(unsubResponse);
          });
        }

        const studentTodosRef = collection(db, 'studentProfiles', user.uid, 'plannerTodos');
        const unsubStudentTodos = onSnapshot(studentTodosRef, (todoSnapshot) => {
          const todoList = [];
          todoSnapshot.forEach((docItem) => todoList.push({ id: docItem.id, ...docItem.data() }));
          setLessonTodos(todoList);
        }, (error) => console.error('Student planner todos sync error:', error));
        todoUnsubs.push(unsubStudentTodos);

        const studentEventsRef = collection(db, 'studentProfiles', user.uid, 'plannerEvents');
        const unsubStudentEvents = onSnapshot(studentEventsRef, (eventSnapshot) => {
          const eventList = [];
          eventSnapshot.forEach((docItem) => eventList.push({ id: docItem.id, ...docItem.data() }));
          setStudentPlannerEvents(eventList);
        }, (error) => console.error('Student planner events sync error:', error));
        eventUnsubs.push(unsubStudentEvents);

        cleanup = () => {
          classUnsubs.forEach((fn) => fn());
          assignmentUnsubs.forEach((fn) => fn());
          plannerUnsubs.forEach((fn) => fn());
          responseUnsubs.forEach((fn) => fn());
          todoUnsubs.forEach((fn) => fn());
          eventUnsubs.forEach((fn) => fn());
        };
        setIsSyncing(false);
        return;
      }

      const teacherId = user.uid;
      const classesRef = collection(db, 'teachers', teacherId, 'classes');
      const lessonsRef = collection(db, 'teachers', teacherId, 'lessonPlans');
      const exitTicketsRef = collection(db, 'teachers', teacherId, 'exitTickets');
      const journalsRef = collection(db, 'teachers', teacherId, 'journals');
      const lessonTodosRef = collection(db, 'teachers', teacherId, 'lessonTodos');
      const assignmentsRef = collection(db, 'teachers', teacherId, 'assignments');
      const attendanceRef = collection(db, 'teachers', teacherId, 'attendance');
      const submissionsQuery = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses'),
        where('teacherId', '==', teacherId)
      );

      const unsubClasses = onSnapshot(classesRef, (snapshot) => {
        const clsList = [];
        snapshot.forEach((docItem) => clsList.push({ id: docItem.id, ...docItem.data() }));
        setClasses(clsList);
        if (clsList.length > 0) {
          setSelectedClassId((current) => current || clsList[0].id);
        }
      }, (error) => console.error('Classes sync error:', error));

      const unsubLessons = onSnapshot(lessonsRef, (snapshot) => {
        const lesList = [];
        snapshot.forEach((docItem) => lesList.push({ id: docItem.id, ...docItem.data() }));
        setLessons(lesList.sort((a, b) => new Date(a.date) - new Date(b.date)));
      }, (error) => console.error('Lessons sync error:', error));

      const unsubExitTickets = onSnapshot(exitTicketsRef, (snapshot) => {
        const ticketList = [];
        snapshot.forEach((docItem) => ticketList.push({ id: docItem.id, ...docItem.data() }));
        setExitTickets(ticketList.sort((a, b) => {
          const topicDiff = String(a.topic || '').localeCompare(String(b.topic || ''));
          if (topicDiff !== 0) return topicDiff;
          return String(a.title || '').localeCompare(String(b.title || ''));
        }));
      }, (error) => console.error('Exit ticket template sync error:', error));

      const unsubJournals = onSnapshot(journalsRef, (snapshot) => {
        const journalList = [];
        snapshot.forEach((docItem) => journalList.push({ id: docItem.id, ...docItem.data() }));
        setJournals(journalList.sort((a, b) => {
          const dateDiff = new Date(b.date || 0) - new Date(a.date || 0);
          if (dateDiff !== 0) return dateDiff;
          return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
        }));
      }, (error) => console.error('Journal sync error:', error));

      const unsubLessonTodos = onSnapshot(lessonTodosRef, (snapshot) => {
        const todoList = [];
        snapshot.forEach((docItem) => todoList.push({ id: docItem.id, ...docItem.data() }));
        setLessonTodos(todoList);
      }, (error) => console.error('Lesson todos sync error:', error));

      const unsubAssignments = onSnapshot(assignmentsRef, (snapshot) => {
        const asgList = [];
        snapshot.forEach((docItem) => asgList.push({ id: docItem.id, ...docItem.data() }));
        setHomework(asgList.sort(compareAssignmentsAlphabetically));
      }, (error) => console.error('Assignments sync error:', error));

      const unsubAttendance = onSnapshot(attendanceRef, (snapshot) => {
        const attList = [];
        snapshot.forEach((docItem) => attList.push({ id: docItem.id, ...docItem.data() }));
        setAttendance(attList);
      }, (error) => console.error('Attendance sync error:', error));

      const unsubExitTicketSubmissions = onSnapshot(submissionsQuery, (snapshot) => {
        const submissionList = [];
        snapshot.forEach((docItem) => submissionList.push({ id: docItem.id, ...docItem.data() }));
        setTeacherExitTicketSubmissions(submissionList);
      }, (error) => console.error('Exit ticket submissions sync error:', error));

      cleanup = () => {
        unsubClasses();
        unsubLessons();
        unsubExitTickets();
        unsubJournals();
        unsubLessonTodos();
        unsubAssignments();
        unsubAttendance();
        unsubExitTicketSubmissions();
      };
      setIsSyncing(false);
    }, (error) => {
      console.error('Student profile sync error:', error);
      setIsSyncing(false);
    });

    return () => {
      cleanup();
      unsubEvents();
      unsubNotes();
      unsubProfile();
    };
  }, [user]);

  const activeClass = useMemo(() => classes.find((c) => c.id === selectedClassId) || classes[0] || null, [classes, selectedClassId]);
  const selectedHomeworkEditItem = useMemo(() => homework.find((h) => h.id === selectedHomeworkEditId) || null, [homework, selectedHomeworkEditId]);
  const visibleJournals = useMemo(() => {
    const term = journalSearch.trim().toLowerCase();
    return journals.filter((entry) => {
      if (!term) return true;
      const haystack = [entry.title || '', entry.body || '', entry.date || '', ...(entry.tags || [])].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [journals, journalSearch]);
  const journalTags = useMemo(() => Array.from(new Set(journals.flatMap((entry) => entry.tags || []))).sort(), [journals]);

  useEffect(() => {
    const validAudiences = new Set(['teachers', 'all-students', ...classes.map((cls) => `class:${cls.id}`)]);
    setSelectedNoteAudiences((current) => {
      const nextValues = current.filter((entry) => validAudiences.has(entry));
      return nextValues.length > 0 ? nextValues : ['teachers', 'all-students'];
    });
  }, [classes]);

  useEffect(() => {
    const classHomework = homework.filter((h) => h.classId === activeClass?.id);
    if (classHomework.length === 0) {
      setSelectedHomeworkEditId('');
      setHomeworkEditForm({ title: '', dueDate: '', maxPoints: '10', trackingMode: 'score' });
      return;
    }

    const existing = classHomework.find((h) => h.id === selectedHomeworkEditId) || null;
    if (!existing) {
      setSelectedHomeworkEditId('');
      setHomeworkEditForm({ title: '', dueDate: '', maxPoints: '10', trackingMode: 'score' });
      return;
    }

    setHomeworkEditForm({
      title: existing.title || '',
      dueDate: existing.dueDate || '',
      maxPoints: existing.maxPoints || '10',
      trackingMode: getHomeworkTrackingMode(existing)
    });
  }, [activeClass?.id, homework]);

  const metrics = useMemo(() => {
    const totalStudentsCount = classes.reduce((acc, curr) => acc + (curr.students?.length || 0), 0);
    const totalClassesCount = classes.length;
    const presentCount = attendance.reduce((count, record) => count + Object.values(record.statuses || {}).filter((s) => s === 'present').length, 0);
    const totalStatuses = attendance.reduce((count, record) => count + Object.values(record.statuses || {}).length, 0);
    const currentAttendanceRate = totalStatuses > 0 ? Math.round((presentCount / totalStatuses) * 100) : 94;
    return { totalStudentsCount, totalClassesCount, currentAttendanceRate };
  }, [classes, attendance]);

  const workspaceOverview = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);
    const absencesByStudent = new Map();

    attendance.forEach((record) => {
      const recordDate = new Date(`${record.date || ''}T00:00:00`);
      if (Number.isNaN(recordDate.getTime()) || recordDate < sevenDaysAgo || recordDate > today) return;

      const classRecord = classes.find((classItem) => classItem.id === record.classId);
      Object.entries(record.statuses || {}).forEach(([studentId, status]) => {
        if (status !== 'absent') return;
        const student = classRecord?.students?.find((entry) => entry.id === studentId);
        const key = `${record.classId || 'unknown'}-${studentId}`;
        const existing = absencesByStudent.get(key);
        const dateLabel = recordDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        absencesByStudent.set(key, {
          name: student?.name || 'Unknown student',
          className: classRecord?.name || record.className || 'Unknown class',
          absenceCount: (existing?.absenceCount || 0) + 1,
          mostRecent: existing?.mostRecent && existing.mostRecent > recordDate ? existing.mostRecent : recordDate,
          dateLabel: existing?.mostRecent && existing.mostRecent > recordDate ? existing.dateLabel : dateLabel
        });
      });
    });

    return {
      classEnrollment: classes.map((classItem) => ({
        id: classItem.id,
        name: classItem.name,
        count: classItem.students?.length || 0
      })),
      recentAbsentees: Array.from(absencesByStudent.values())
        .sort((a, b) => b.mostRecent - a.mostRecent || b.absenceCount - a.absenceCount)
    };
  }, [attendance, classes]);

  const todayDateKey = getTodayDateValue();
  const noteAudienceOptions = useMemo(() => {
    const baseOptions = [
      { id: 'teachers', label: 'Teachers' },
      { id: 'all-students', label: 'All students' }
    ];
    const classOptions = classes.map((cls) => ({ id: `class:${cls.id}`, label: cls.name }));
    return [...baseOptions, ...classOptions];
  }, [classes]);
  const sortedNotes = useMemo(() => {
    return [...notes]
      .map((note) => ({ ...note, audiences: normalizeNoteAudiences(note.audiences) }))
      .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
  }, [notes]);
  const exitTicketsByTopic = useMemo(() => {
    const grouped = new Map();
    exitTickets.forEach((ticket) => {
      const key = ticket.reflectionOnly
        ? 'Reflection-only tickets'
        : (String(ticket.topic || 'Uncategorized').trim() || 'Uncategorized');
      const list = grouped.get(key) || [];
      list.push(ticket);
      grouped.set(key, list);
    });
    return Array.from(grouped.entries())
      .map(([topic, items]) => ({
        topic,
        items: items.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
      }))
      .sort((a, b) => a.topic.localeCompare(b.topic));
  }, [exitTickets]);
  const todayScheduleItems = useMemo(() => {
    const lessonItems = lessons
      .filter((lesson) => lesson.date === todayDateKey && lesson.source !== 'dashboard-event')
      .map((lesson) => ({
        id: `lesson-${lesson.id}`,
        type: 'lesson',
        lesson,
        time: lesson.startTime || '',
        endTime: lesson.endTime || '',
        title: lesson.topic,
        subtitle: lesson.subject,
        description: lesson.objectives || lesson.materials || 'Lesson plan scheduled for today.'
      }));

    const eventItems = events.map((eventItem) => {
      const linkedLesson = lessons.find((lesson) => (
        lesson.id === eventItem.linkedLessonId
        || lesson.dashboardEventId === eventItem.id
      ));
      return {
        id: `event-${eventItem.id}`,
        type: 'event',
        sourceId: eventItem.id,
        lesson: linkedLesson || {
          id: '',
          topic: eventItem.title,
          subject: 'Unassigned',
          className: '',
          date: todayDateKey,
          startTime: normalizeDashboardEventTime(eventItem.time),
          endTime: '',
          objectives: eventItem.description || 'No description provided',
          isDashboardEventPreview: true
        },
        time: eventItem.time || '',
        title: eventItem.title,
        subtitle: 'Event',
        description: eventItem.description || 'No description provided'
      };
    });

    return [...lessonItems, ...eventItems].sort((a, b) => {
      const timeDiff = getTimeSortValue(a.time) - getTimeSortValue(b.time);
      if (timeDiff !== 0) return timeDiff;
      return a.title.localeCompare(b.title);
    });
  }, [events, lessons, todayDateKey]);
  const todayLessonTodos = useMemo(() => (
    lessonTodos
      .filter((todo) => todo.date === todayDateKey)
      .slice()
      .sort((a, b) => {
        const doneDiff = Number(Boolean(a.done)) - Number(Boolean(b.done));
        if (doneDiff !== 0) return doneDiff;
        return (Number(a.order) || 0) - (Number(b.order) || 0);
      })
  ), [lessonTodos, todayDateKey]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!user || !eventForm.title || !eventForm.time) return;
    try {
      const eventRef = doc(collection(db, 'artifacts', appId, 'public', 'data', 'events'));
      const lessonId = `dashboard-event-${eventRef.id}`;
      const description = eventForm.description || 'No description provided';
      await Promise.all([
        setDoc(eventRef, {
          title: eventForm.title,
          time: eventForm.time,
          description,
          linkedLessonId: lessonId,
          createdAt: Date.now()
        }),
        setDoc(doc(db, 'teachers', user.uid, 'lessonPlans', lessonId), {
          subject: 'Unassigned',
          topic: eventForm.title,
          date: todayDateKey,
          startTime: normalizeDashboardEventTime(eventForm.time),
          endTime: '',
          objectives: description,
          materials: '',
          notesForStudents: '',
          notesForTeachers: '',
          classId: '',
          className: '',
          source: 'dashboard-event',
          dashboardEventId: eventRef.id,
          createdAt: Date.now(),
          updatedAt: Date.now()
        })
      ]);
      setEventForm({ title: '', time: '', description: '' });
      showToast('Event published and added to the lesson planner.');
    } catch (error) {
      showToast('Error adding event.', 'error');
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      const eventItem = events.find((item) => item.id === id);
      const linkedLessonId = eventItem?.linkedLessonId || `dashboard-event-${id}`;
      await Promise.all([
        deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'events', id)),
        user ? deleteDoc(doc(db, 'teachers', user.uid, 'lessonPlans', linkedLessonId)) : Promise.resolve()
      ]);
      showToast('Event removed.');
    } catch (error) {
      showToast('Failed to remove event.', 'error');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    if (selectedNoteAudiences.length === 0) {
      showToast('Select at least one notice audience.', 'error');
      return;
    }
    try {
      const docRef = doc(collection(db, 'artifacts', appId, 'public', 'data', 'notes'));
      await setDoc(docRef, {
        text: noteText.trim(),
        author: noteAuthor.trim() || 'Anonymous',
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        audiences: selectedNoteAudiences,
        createdAt: Date.now()
      });
      setNoteText('');
      setSelectedNoteAudiences(['teachers', 'all-students']);
      setIsNoteAudienceMenuOpen(false);
      showToast('Note posted.');
    } catch (error) {
      showToast('Could not post note.', 'error');
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'notes', id));
      showToast('Note removed.');
    } catch (error) {
      showToast('Could not remove note.', 'error');
    }
  };

  const handleNewJournalDraft = () => {
    if (journalAutosaveTimeoutRef.current) {
      clearTimeout(journalAutosaveTimeoutRef.current);
      journalAutosaveTimeoutRef.current = null;
    }
    setSelectedJournalId('');
    journalLastSavedFingerprintRef.current = '';
    setJournalForm(createEmptyJournalDraft());
    setJournalEditorMode('markdown');
    setIsJournalEditorOpen(true);
  };

  const handleSelectJournal = (entry) => {
    if (journalAutosaveTimeoutRef.current) {
      clearTimeout(journalAutosaveTimeoutRef.current);
      journalAutosaveTimeoutRef.current = null;
    }
    const normalizedBodyHtml = String(entry.bodyHtml || '').trim();
    const normalizedBody = normalizedBodyHtml
      ? (getPlainTextFromRichHtml(normalizedBodyHtml) || String(entry.body || '').trim())
      : String(entry.body || '').trim();
    const normalizedDate = entry.date || getTodayDateValue();
    journalLastSavedFingerprintRef.current = JSON.stringify({
      title: String(entry.title || '').trim(),
      date: normalizedDate,
      tags: (entry.tags || []).join(', '),
      body: normalizedBody,
      bodyHtml: String(normalizedBodyHtml || '').trim(),
      transcriptChunks: Array.isArray(entry.transcriptChunks) ? entry.transcriptChunks : [],
      journalAiSummary: String(entry.journalAiSummary || '')
    });
    setSelectedJournalId(entry.id);
    setJournalEditorMode(normalizedBodyHtml ? 'richtext' : 'markdown');
    setJournalForm({
      title: entry.title || '',
      date: normalizedDate,
      tags: (entry.tags || []).join(', '),
      body: normalizedBody,
      bodyHtml: normalizedBodyHtml,
      transcriptChunks: Array.isArray(entry.transcriptChunks) ? entry.transcriptChunks : [],
      journalAiSummary: String(entry.journalAiSummary || '')
    });
    setIsJournalEditorOpen(true);
  };

  const buildJournalDraftSnapshot = (draft = journalForm, { useLiveEditor = false } = {}) => {
    const draftBodyHtml = String(draft.bodyHtml || '').trim();
    const liveEditorHtml = useLiveEditor ? String(journalEditorRef.current?.innerHTML || '').trim() : '';
    const fallbackBody = String(draft.body || '').trim();
    const bodyHtml = draftBodyHtml || liveEditorHtml;
    const body = (bodyHtml ? getPlainTextFromRichHtml(bodyHtml) : fallbackBody).trim();

    return {
      title: String(draft.title || '').trim(),
      date: String(draft.date || '').trim() || getTodayDateValue(),
      tags: String(draft.tags || ''),
      body,
      bodyHtml,
      transcriptChunks: Array.isArray(draft.transcriptChunks) ? draft.transcriptChunks : [],
      journalAiSummary: String(draft.journalAiSummary || '')
    };
  };

  const persistJournalDraft = async ({
    draft = journalForm,
    useLiveEditor = false,
    closeOnSuccess = false,
    showSuccessToast = false,
    requireBody = false,
    allowCreateWhenEmpty = false,
    showErrorToast = false
  } = {}) => {
    if (!user) {
      return { saved: false, reason: 'no-user' };
    }

    const snapshot = buildJournalDraftSnapshot(draft, { useLiveEditor });
    const tagList = normalizeJournalTags(snapshot.tags);
    const hasMeaningfulContent = Boolean(snapshot.body || snapshot.title || tagList.length > 0);

    if (!snapshot.date) {
      return { saved: false, reason: 'missing-date' };
    }

    if (requireBody && !snapshot.body.trim()) {
      return { saved: false, reason: 'missing-body' };
    }

    if (!selectedJournalId && !allowCreateWhenEmpty && !hasMeaningfulContent) {
      return { saved: false, reason: 'empty-new-entry' };
    }

    const fingerprint = JSON.stringify({
      title: snapshot.title,
      date: snapshot.date,
      tags: snapshot.tags,
      body: snapshot.body,
      bodyHtml: snapshot.bodyHtml,
      transcriptChunks: snapshot.transcriptChunks,
      journalAiSummary: snapshot.journalAiSummary
    });

    if (fingerprint === journalLastSavedFingerprintRef.current) {
      return { saved: false, reason: 'unchanged' };
    }

    const teacherId = user.uid;
    const now = new Date().toISOString();
    const targetRef = selectedJournalId
      ? doc(db, 'teachers', teacherId, 'journals', selectedJournalId)
      : doc(collection(db, 'teachers', teacherId, 'journals'));

    try {
      await setDoc(targetRef, {
        title: snapshot.title || `Teaching Journal ${snapshot.date}`,
        date: snapshot.date,
        body: snapshot.body,
        bodyHtml: snapshot.bodyHtml,
        tags: tagList,
        transcriptChunks: snapshot.transcriptChunks,
        journalAiSummary: snapshot.journalAiSummary,
        updatedAt: now
      }, { merge: true });

      journalLastSavedFingerprintRef.current = fingerprint;
      if (!selectedJournalId) {
        setSelectedJournalId(targetRef.id);
      }
      // Saving acknowledges this snapshot; it must not replace the live draft.
      // The user may have typed more while the write was pending, and the saved
      // text is trimmed, so echoing it back also removes trailing whitespace
      // and resets the Markdown textarea's caret.

      if (closeOnSuccess) {
        setIsJournalEditorOpen(false);
      }
      if (showSuccessToast) {
        showToast(selectedJournalId ? 'Journal entry updated.' : 'Journal entry saved.');
      }
      return { saved: true, id: targetRef.id };
    } catch (error) {
      console.error('Journal save failed:', error);
      if (showErrorToast) {
        showToast(error?.message ? `Could not save journal entry: ${error.message}` : 'Could not save journal entry.', 'error');
      }
      return { saved: false, reason: 'write-failed', error };
    }
  };

  const handleSaveJournal = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const result = await persistJournalDraft({
      draft: journalForm,
      useLiveEditor: journalEditorMode === 'richtext',
      closeOnSuccess: true,
      showSuccessToast: true,
      requireBody: true,
      allowCreateWhenEmpty: false,
      showErrorToast: true
    });

    if (result.saved || result.reason === 'write-failed') {
      return;
    }

    if (result.reason === 'missing-date' || result.reason === 'missing-body' || result.reason === 'empty-new-entry' || result.reason === 'no-user') {
      showToast('Journal date and content are required.', 'error');
    }
  };

  useEffect(() => {
    if (!isJournalEditorOpen || !user) return;

    if (journalAutosaveTimeoutRef.current) {
      clearTimeout(journalAutosaveTimeoutRef.current);
    }

    journalAutosaveTimeoutRef.current = setTimeout(() => {
      persistJournalDraft({
        draft: journalForm,
        useLiveEditor: journalEditorMode === 'richtext',
        closeOnSuccess: false,
        showSuccessToast: false,
        requireBody: false,
        allowCreateWhenEmpty: false,
        showErrorToast: false
      });
    }, 1200);

    return () => {
      if (journalAutosaveTimeoutRef.current) {
        clearTimeout(journalAutosaveTimeoutRef.current);
        journalAutosaveTimeoutRef.current = null;
      }
    };
  }, [isJournalEditorOpen, user, selectedJournalId, journalForm, persistJournalDraft]);

  useEffect(() => () => {
    if (journalAutosaveTimeoutRef.current) {
      clearTimeout(journalAutosaveTimeoutRef.current);
      journalAutosaveTimeoutRef.current = null;
    }
  }, []);

  const handleDeleteJournal = async (journalId) => {
    if (!user) return;
    if (!window.confirm('Delete this journal entry?')) return;

    try {
      await deleteDoc(doc(db, 'teachers', user.uid, 'journals', journalId));
      if (selectedJournalId === journalId) {
        handleNewJournalDraft();
      }
      showToast('Journal entry deleted.');
    } catch (error) {
      showToast('Could not delete journal entry.', 'error');
    }
  };

  const openNewLessonModal = (defaults = {}) => {
    setEditingLessonId('');
    setDuplicatingLessonDate('');
    setLessonForm({
      ...createEmptyLessonForm(),
      ...defaults
    });
    setLessonUploadFiles([]);
    setRemovedAttachmentUrls([]);
    setShowLessonModal(true);
  };

  const stopLessonRecording = () => {
    lessonRecordingShouldContinueRef.current = false;
    const recorder = lessonRecorderRef.current;
    if (recorder?.state === 'recording') recorder.stop();
  };

  const startLessonRecording = async (lesson) => {
    const recordingTeacherId = user?.uid;
    if (lessonRecording || !recordingTeacherId || !firebaseFunctions) return;
    const durationMs = getLessonDurationMs(lesson);
    if (!durationMs) {
      showToast('Add a valid lesson start and end time before recording.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      showToast('Audio recording is not supported in this browser.');
      return;
    }

    setLessonRecordingError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
        .find((type) => MediaRecorder.isTypeSupported(type));
      const transcribeLessonChunk = httpsCallable(
        firebaseFunctions,
        'transcribeLessonAudioChunk',
        { timeout: 300000 }
      );
      const summarizeLesson = httpsCallable(firebaseFunctions, 'summarizeLessonRecording');
      const transcriptChunks = [];
      await updateDoc(doc(db, 'teachers', recordingTeacherId, 'lessonPlans', lesson.id), {
        transcriptChunks: [],
        aiSummary: null,
        transcriptionModel: 'Google Gemini'
      });

      lessonRecordingStreamRef.current = stream;
      lessonChunkIndexRef.current = 0;
      lessonChunkQueueRef.current = Promise.resolve();
      lessonRecordingStartedAtRef.current = Date.now();
      lessonRecordingShouldContinueRef.current = true;
      setLessonRecording({ lessonId: lesson.id, durationMs, status: 'recording' });
      setLessonRecordingElapsedMs(0);

      const finalizeRecording = async () => {
        window.clearInterval(lessonRecordingTimerRef.current);
        window.clearTimeout(lessonRecordingTimeoutRef.current);
        lessonRecordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        setLessonRecording((current) => current ? { ...current, status: 'processing' } : current);
        try {
          await lessonChunkQueueRef.current;
          await summarizeLesson({ lessonId: lesson.id });
          showToast('Google transcript and AI summary are ready.');
        } catch (error) {
          console.error('Lesson recording processing error:', error);
          setLessonRecordingError(error?.message || 'The recording could not be processed.');
          showToast('Recording stopped, but processing failed.');
        } finally {
          lessonRecorderRef.current = null;
          lessonRecordingStreamRef.current = null;
          lessonRecordingShouldContinueRef.current = false;
          setLessonRecording(null);
          setLessonRecordingElapsedMs(0);
        }
      };

      const startRecordingInterval = () => {
        const intervalStartedAt = Date.now();
        const audioParts = [];
        const recorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
        lessonRecorderRef.current = recorder;

        recorder.addEventListener('dataavailable', (event) => {
          if (event.data?.size) audioParts.push(event.data);
        });

        recorder.addEventListener('stop', () => {
          window.clearTimeout(lessonRecordingTimeoutRef.current);
          const audioBlob = new Blob(audioParts, { type: recorder.mimeType || preferredType || 'audio/webm' });
          if (audioBlob.size) {
            const chunkIndex = lessonChunkIndexRef.current++;
            const startMs = Math.max(0, intervalStartedAt - lessonRecordingStartedAtRef.current);
            const endMs = Math.min(Date.now() - lessonRecordingStartedAtRef.current, durationMs);
            lessonChunkQueueRef.current = lessonChunkQueueRef.current.then(async () => {
              const result = await transcribeLessonChunk({
                lessonId: lesson.id,
                chunkIndex,
                startMs,
                endMs,
                mimeType: audioBlob.type || 'audio/webm',
                audioBase64: await blobToBase64(audioBlob)
              });
              const text = String(result?.data?.text || '').trim() || '[No clear speech detected]';
              transcriptChunks.push({ index: chunkIndex, startMs, endMs, text });
              transcriptChunks.sort((a, b) => a.index - b.index);
            });
          }

          const elapsedMs = Date.now() - lessonRecordingStartedAtRef.current;
          if (lessonRecordingShouldContinueRef.current && elapsedMs < durationMs) {
            startRecordingInterval();
          } else {
            finalizeRecording();
          }
        });

        recorder.start();
        const remainingMs = Math.max(1, durationMs - (Date.now() - lessonRecordingStartedAtRef.current));
        lessonRecordingTimeoutRef.current = window.setTimeout(() => {
          if (recorder.state === 'recording') recorder.stop();
        }, Math.min(60 * 1000, remainingMs));
      };

      startRecordingInterval();
      lessonRecordingTimerRef.current = window.setInterval(() => {
        const elapsedMs = Math.min(Date.now() - lessonRecordingStartedAtRef.current, durationMs);
        setLessonRecordingElapsedMs(elapsedMs);
        if (elapsedMs >= durationMs) stopLessonRecording();
      }, 1000);
    } catch (error) {
      console.error('Microphone access error:', error);
      lessonRecordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      setLessonRecording(null);
      setLessonRecordingError('Microphone access was denied or unavailable.');
      showToast('Allow microphone access to record this lesson.');
    }
  };

  const renderLessonRecordingButton = (lesson, compact = false) => {
    const isThisLesson = lessonRecording?.lessonId === lesson.id;
    const isRecording = isThisLesson && lessonRecording.status === 'recording';
    const isProcessing = isThisLesson && lessonRecording.status === 'processing';
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (isRecording) stopLessonRecording();
          else startLessonRecording(lesson);
        }}
        disabled={Boolean(lessonRecording) && !isThisLesson || isProcessing}
        className={`${compact ? 'mt-2 w-full' : ''} flex items-center justify-center gap-1.5 rounded border px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${
          isRecording
            ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
            : isProcessing
              ? 'border-amber-200 bg-amber-50 text-amber-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900 disabled:opacity-40'
        }`}
      >
        {isRecording ? <StopCircle className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
        {isRecording
          ? `Stop ${formatRecordingTime(lessonRecordingElapsedMs)}`
          : isProcessing
            ? 'Creating recap…'
            : lesson.transcriptChunks?.length
              ? 'Record again'
              : 'Record lesson'}
      </button>
    );
  };

  const openDuplicateLessonModal = (lesson) => {
    if (!lesson) return;
    const nextDate = lesson.date ? addDaysToDateValue(lesson.date, 7) : '';
    setEditingLessonId('');
    setDuplicatingLessonDate(lesson.date || '');
    setLessonForm({
      ...createEmptyLessonForm(),
      subject: lesson.subject || '',
      topic: DUPLICATED_LESSON_TOPIC_PLACEHOLDER,
      date: nextDate,
      startTime: lesson.startTime || '',
      endTime: lesson.endTime || '',
      objectives: lesson.objectives || '',
      materials: lesson.materials || '',
      notesForStudents: '',
      notesForTeachers: '',
      exitTicketId: lesson.exitTicketId || '',
      recurrenceFrequency: 'weekly',
      recurrenceEndDate: nextDate
    });
    setLessonUploadFiles([]);
    setRemovedAttachmentUrls([]);
    setSelectedLesson(null);
    setShowLessonModal(true);
  };

  const handleStartNewExitTicketTemplate = () => {
    setSelectedExitTicketLibraryId('');
    setExitTicketLibraryForm(createEmptyExitTicketTemplateForm());
  };

  const handleEditExitTicketTemplate = (ticket) => {
    if (!ticket) return;
    setSelectedExitTicketLibraryId(ticket.id);
    setExitTicketLibraryForm({
      topic: String(ticket.topic || ''),
      title: String(ticket.title || ''),
      instructions: String(ticket.instructions || ''),
      aiSystemPrompt: String(ticket.aiSystemPrompt || ''),
      reflectionEnabled: ticket.reflectionEnabled !== false,
      reflectionOnly: Boolean(ticket.reflectionOnly),
      reflectionPrompts: normalizeReflectionPrompts(ticket.reflectionPrompts),
      questions: normalizeExitTicketConfig({
        enabled: true,
        title: ticket.title || 'Exit Ticket',
        instructions: ticket.instructions || '',
        aiSystemPrompt: ticket.aiSystemPrompt || '',
        reflectionEnabled: ticket.reflectionEnabled !== false,
        reflectionOnly: Boolean(ticket.reflectionOnly),
        reflectionPrompts: ticket.reflectionPrompts,
        questions: ticket.questions || []
      }).questions
    });
    window.requestAnimationFrame(() => {
      exitTicketEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      exitTicketTopicInputRef.current?.focus({ preventScroll: true });
    });
  };

  const handleSaveExitTicketTemplate = async (e) => {
    e.preventDefault();
    if (!user) return;

    if ((!exitTicketLibraryForm.reflectionOnly && !exitTicketLibraryForm.topic.trim()) || !exitTicketLibraryForm.title.trim()) {
      showToast(exitTicketLibraryForm.reflectionOnly ? 'A title is required.' : 'Topic and title are required for exit tickets.', 'error');
      return;
    }

    const prepared = sanitizeExitTicketConfig({
      enabled: true,
      title: exitTicketLibraryForm.title,
      instructions: exitTicketLibraryForm.instructions,
      aiSystemPrompt: exitTicketLibraryForm.aiSystemPrompt,
      reflectionEnabled: exitTicketLibraryForm.reflectionEnabled,
      reflectionOnly: exitTicketLibraryForm.reflectionOnly,
      reflectionPrompts: exitTicketLibraryForm.reflectionPrompts,
      questions: exitTicketLibraryForm.questions
    });

    if (!prepared.reflectionOnly && prepared.questions.length === 0) {
      showToast('Add at least one valid question.', 'error');
      return;
    }

    const teacherId = user.uid;
    const targetRef = selectedExitTicketLibraryId
      ? doc(db, 'teachers', teacherId, 'exitTickets', selectedExitTicketLibraryId)
      : doc(collection(db, 'teachers', teacherId, 'exitTickets'));

    try {
      await setDoc(targetRef, {
        topic: prepared.reflectionOnly ? '' : exitTicketLibraryForm.topic.trim(),
        title: prepared.title,
        instructions: prepared.instructions,
        aiSystemPrompt: prepared.aiSystemPrompt,
        reflectionEnabled: prepared.reflectionEnabled,
        reflectionOnly: prepared.reflectionOnly,
        reflectionPrompts: prepared.reflectionPrompts,
        questions: prepared.questions,
        updatedAt: Date.now(),
        ...(selectedExitTicketLibraryId ? {} : { createdAt: Date.now() })
      }, { merge: true });
      showToast(selectedExitTicketLibraryId ? 'Exit ticket updated.' : 'Exit ticket created.');
      handleStartNewExitTicketTemplate();
    } catch (error) {
      showToast('Could not save exit ticket template.', 'error');
    }
  };

  const handleDeleteExitTicketTemplate = async (ticketId) => {
    if (!user || !ticketId) return;
    if (!window.confirm('Delete this exit ticket template?')) return;
    try {
      await deleteDoc(doc(db, 'teachers', user.uid, 'exitTickets', ticketId));
      if (selectedExitTicketLibraryId === ticketId) {
        handleStartNewExitTicketTemplate();
      }
      showToast('Exit ticket deleted.');
    } catch (error) {
      showToast('Could not delete exit ticket.', 'error');
    }
  };

  const uploadExitTicketImageFile = async (file) => {
    if (!file || !storage || !user) {
      throw new Error('Image paste is unavailable.');
    }

    const safeName = String(file.name || 'clipboard-image.png').replace(/[^a-zA-Z0-9._-]/g, '-');
    const imagePath = `teachers/${user.uid}/exitTickets/images/${Date.now()}-${safeName}`;
    const imageRef = storageRef(storage, imagePath);
    await uploadBytes(imageRef, file);
    return getDownloadURL(imageRef);
  };

  const handleExitTicketImagePaste = async (e, applyInsert) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type && item.type.startsWith('image/'));
    if (!imageItem) return;

    const file = imageItem.getAsFile();
    if (!file) return;

    e.preventDefault();

    const target = e.target;
    const selectionStart = Number.isInteger(target?.selectionStart) ? target.selectionStart : null;
    const selectionEnd = Number.isInteger(target?.selectionEnd) ? target.selectionEnd : selectionStart;

    try {
      const imageUrl = await uploadExitTicketImageFile(file);
      const markdown = `\n![Pasted image](${imageUrl})\n`;
      applyInsert({ markdown, selectionStart, selectionEnd });

      if (target && selectionStart !== null) {
        const cursorPosition = selectionStart + markdown.length;
        requestAnimationFrame(() => {
          target.focus();
          target.setSelectionRange(cursorPosition, cursorPosition);
        });
      }

      showToast('Image pasted into exit ticket.');
    } catch (error) {
      showToast(error?.message || 'Could not upload pasted image.', 'error');
    }
  };

  const handleImportGoogleCalendarLessons = async ({ refresh = false } = {}) => {
    const calendarUrl = googleCalendarUrl.trim();
    if (!user || !firebaseFunctions) {
      showToast('Calendar import is not available.', 'error');
      return;
    }
    if (!refresh && !calendarUrl) {
      showToast('Paste your Google Calendar secret iCal URL first.', 'error');
      return;
    }
    try {
      const url = new URL(refresh ? 'https://calendar.google.com/basic.ics' : calendarUrl);
      if (url.protocol !== 'https:' || !['calendar.google.com', 'calendar.googleusercontent.com'].includes(url.hostname) || url.username || url.password || !url.pathname.endsWith('.ics')) {
        throw new Error('invalid-calendar-url');
      }
    } catch {
      showToast('Enter an HTTPS Google Calendar iCal address ending in .ics.', 'error');
      return;
    }

    if (googleCalendarSyncRef.current) return;
    googleCalendarSyncRef.current = true;
    setIsImportingGoogleCalendar(true);
    setGoogleCalendarSyncError('');
    const syncingUid = user.uid;
    try {
      const importGoogleCalendarLessons = httpsCallable(firebaseFunctions, 'importGoogleCalendarLessons');
      if (refresh) {
        const status = await importGoogleCalendarLessons({ action: 'status' });
        if (firebaseAuth.currentUser?.uid !== syncingUid) return;
        setGoogleCalendarConnected(Boolean(status.data?.connected));
        setGoogleCalendarLastSynced(status.data?.lastSyncedAt || null);
        if (!status.data?.connected) return;
      }
      const today = new Date();
      const windowStartDate = new Date(today);
      windowStartDate.setDate(today.getDate() - 14);
      const windowEndDate = new Date(today);
      windowEndDate.setDate(today.getDate() + 120);
      const result = await importGoogleCalendarLessons({
        ...(refresh ? {} : { calendarUrl }),
        appId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        windowStart: windowStartDate.toISOString().slice(0, 10),
        windowEnd: windowEndDate.toISOString().slice(0, 10)
      });
      if (firebaseAuth.currentUser?.uid !== syncingUid) return;
      setGoogleCalendarConnected(Boolean(result.data?.connected));
      setGoogleCalendarLastSynced(result.data?.lastSyncedAt || null);
      if (!refresh) {
        setGoogleCalendarUrl('');
        showToast('Google Calendar connected. Events will refresh automatically.');
      }
    } catch (error) {
      if (firebaseAuth.currentUser?.uid === syncingUid) {
        setGoogleCalendarSyncError('Calendar could not refresh. Your last synced events are still shown.');
        if (!refresh) showToast('Could not connect Google Calendar. Check the secret iCal address and try again.', 'error');
      }
    } finally {
      googleCalendarSyncRef.current = false;
      setIsImportingGoogleCalendar(false);
    }
  };

  useEffect(() => {
    setGoogleCalendarUrl('');
    setGoogleCalendarConnected(false);
    setGoogleCalendarLastSynced(null);
    setGoogleCalendarSyncError('');
  }, [user?.uid]);

  const handleDisconnectGoogleCalendar = async () => {
    if (googleCalendarSyncRef.current) return;
    googleCalendarSyncRef.current = true;
    setIsImportingGoogleCalendar(true);
    try {
      await httpsCallable(firebaseFunctions, 'importGoogleCalendarLessons')({ action: 'disconnect' });
      setGoogleCalendarConnected(false);
      setGoogleCalendarLastSynced(null);
      setGoogleCalendarUrl('');
      setGoogleCalendarSyncError('');
      showToast('Calendar disconnected. Existing lesson cards are kept.');
    } catch {
      showToast('Could not disconnect Google Calendar.', 'error');
    } finally {
      googleCalendarSyncRef.current = false;
      setIsImportingGoogleCalendar(false);
    }
  };

  const handleRemoveGoogleCalendarImportedLessons = async () => {
    if (!user || !firebaseFunctions) {
      showToast('Calendar cleanup is not available.', 'error');
      return;
    }
    if (!window.confirm('Remove all lesson cards imported from Google Calendar? Hand-created lesson cards will stay.')) {
      return;
    }

    setIsRemovingGoogleCalendarLessons(true);
    try {
      const removeGoogleCalendarImportedLessons = httpsCallable(firebaseFunctions, 'removeGoogleCalendarImportedLessons');
      const result = await removeGoogleCalendarImportedLessons({ appId });
      const deletedCount = Number(result.data?.deletedCount || 0);
      showToast(deletedCount > 0 ? `Removed ${deletedCount} imported calendar lesson${deletedCount === 1 ? '' : 's'}.` : 'No imported calendar lessons found.');
    } catch (error) {
      showToast(error?.message || 'Could not remove imported calendar lessons.', 'error');
    } finally {
      setIsRemovingGoogleCalendarLessons(false);
    }
  };

  const handleAssignCalendarLessonClass = async (lesson, classId) => {
    if (!user || !lesson?.id) return;
    const linkedClass = classes.find((item) => item.id === classId);
    const title = String(lesson.topic || lesson.subject || '').trim().toLocaleLowerCase();
    const matchingLessons = lessons.filter((item) => (
      item.calendarSource === 'google-calendar'
      && String(item.topic || item.subject || '').trim().toLocaleLowerCase() === title
    ));
    try {
      await Promise.all(matchingLessons.map((matchingLesson) => updateDoc(
        doc(db, 'teachers', user.uid, 'lessonPlans', matchingLesson.id),
        {
          classId: linkedClass?.id || '',
          className: linkedClass?.name || '',
          classAssignedByTeacher: Boolean(linkedClass),
          updatedAt: Date.now()
        }
      )));
      showToast(linkedClass
        ? `Assigned ${matchingLessons.length} lesson${matchingLessons.length === 1 ? '' : 's'} titled “${lesson.topic || lesson.subject}” to ${linkedClass.name}.`
        : `Class assignment removed from ${matchingLessons.length} matching lesson${matchingLessons.length === 1 ? '' : 's'}.`);
    } catch (error) {
      showToast('Could not update the lesson class.', 'error');
    }
  };

  const handleAddLessonNoteCard = async (dateKey) => {
    if (!user) return;

    const teacherId = user.uid;
    try {
      const noteDocRef = doc(collection(db, 'teachers', teacherId, 'lessonPlans'));
      const payload = {
        id: noteDocRef.id,
        cardType: 'note',
        date: dateKey || '',
        subject: '',
        topic: '',
        startTime: '',
        endTime: '',
        objectives: '',
        materials: '',
        notesForStudents: '',
        notesForTeachers: '',
        noteText: '',
        exitTicketId: '',
        exitTicketTopic: '',
        exitTicketTitle: '',
        exitTicket: createEmptyExitTicket(),
        attachments: [],
        recurrenceFrequency: 'none',
        recurrenceSeriesId: '',
        recurrenceStartDate: '',
        recurrenceEndDate: '',
        classId: '',
        className: '',
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await setDoc(noteDocRef, payload);
      showToast('Note card created.');
    } catch (error) {
      showToast('Could not create note card.', 'error');
    }
  };

  const handleUpdateLessonNoteCard = (lesson, nextText) => {
    const normalizedText = normalizeCardListShortcuts(nextText);
    setLessonNoteDrafts((current) => ({ ...current, [lesson.id]: normalizedText }));
    if (lessonNoteSaveTimeoutsRef.current[lesson.id]) {
      clearTimeout(lessonNoteSaveTimeoutsRef.current[lesson.id]);
    }
    lessonNoteSaveTimeoutsRef.current[lesson.id] = setTimeout(() => {
      saveLessonNoteCard(lesson, normalizedText);
    }, 400);
  };

  const saveLessonNoteCard = async (lesson, nextText) => {
    if (!user || !lesson?.id) return;
    const normalized = String(nextText || '');
    const currentDraft = lessonNoteDrafts[lesson.id];
    if (currentDraft === undefined && normalized === (lesson.noteText || '')) return;

    try {
      await updateDoc(doc(db, 'teachers', user.uid, 'lessonPlans', lesson.id), {
        noteText: normalized,
        updatedAt: Date.now()
      });
      setLessonNoteDrafts((current) => {
        const next = { ...current };
        delete next[lesson.id];
        return next;
      });
    } catch (error) {
      showToast('Could not save note card.', 'error');
    }
  };

  const getLessonPlannerPosition = (lesson) => {
    const savedPosition = Number(lesson?.plannerPosition);
    if (Number.isFinite(savedPosition)) return savedPosition;
    const [hours, minutes] = String(lesson?.startTime || '').split(':').map(Number);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) return (hours * 60) + minutes;
    return 24 * 60;
  };

  const sortLessonPlannerCards = (cards) => [...cards].sort((a, b) => {
    const positionDiff = getLessonPlannerPosition(a) - getLessonPlannerPosition(b);
    if (positionDiff !== 0) return positionDiff;
    return (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0);
  });

  const handleDropLessonNote = async (targetDate, targetIndex) => {
    if (!user || !draggingLessonNoteId) return;
    const draggedNote = lessons.find((lesson) => lesson.id === draggingLessonNoteId && lesson.cardType === 'note');
    if (!draggedNote) return;

    const targetCards = sortLessonPlannerCards(
      lessons.filter((lesson) => lesson.date === targetDate && lesson.id !== draggedNote.id)
    );
    const insertIndex = Math.max(0, Math.min(Number(targetIndex) || 0, targetCards.length));
    const previousPosition = insertIndex > 0 ? getLessonPlannerPosition(targetCards[insertIndex - 1]) : null;
    const nextPosition = insertIndex < targetCards.length ? getLessonPlannerPosition(targetCards[insertIndex]) : null;
    let plannerPosition;
    if (previousPosition === null && nextPosition === null) plannerPosition = 12 * 60;
    else if (previousPosition === null) plannerPosition = nextPosition - 1;
    else if (nextPosition === null) plannerPosition = previousPosition + 1;
    else plannerPosition = previousPosition + ((nextPosition - previousPosition) / 2);

    setDraggingLessonNoteId('');
    try {
      await updateDoc(doc(db, 'teachers', user.uid, 'lessonPlans', draggedNote.id), {
        date: targetDate,
        plannerPosition,
        updatedAt: Date.now()
      });
    } catch (error) {
      showToast('Could not move note card.', 'error');
    }
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    if (!user || !lessonForm.subject || !lessonForm.topic || !lessonForm.date || !lessonForm.startTime || !lessonForm.endTime) {
      showToast('Fill in required fields.', 'error');
      return;
    }
    if (lessonForm.endTime <= lessonForm.startTime) {
      showToast('End time must be after start time.', 'error');
      return;
    }
    if (!editingLessonId && lessonForm.recurrenceFrequency !== 'none') {
      if (!lessonForm.recurrenceEndDate) {
        showToast('Choose an end date for recurring lessons.', 'error');
        return;
      }
      if (lessonForm.recurrenceEndDate < lessonForm.date) {
        showToast('Recurrence end date must be after the start date.', 'error');
        return;
      }
    }
    const teacherId = user.uid;
    try {
      const selectedExitTicket = exitTickets.find((ticket) => ticket.id === lessonForm.exitTicketId);
      const preparedExitTicket = selectedExitTicket
        ? sanitizeExitTicketConfig({
          enabled: true,
          title: selectedExitTicket.title || 'Exit Ticket',
          instructions: selectedExitTicket.instructions || '',
          aiSystemPrompt: selectedExitTicket.aiSystemPrompt || '',
          reflectionEnabled: selectedExitTicket.reflectionEnabled !== false,
          reflectionOnly: Boolean(selectedExitTicket.reflectionOnly),
          reflectionPrompts: selectedExitTicket.reflectionPrompts,
          questions: selectedExitTicket.questions || []
        })
        : createEmptyExitTicket();
      const previousLesson = editingLessonId
        ? lessons.find((item) => item.id === editingLessonId)
        : null;
      const linkedClass = classes.find((cls) => cls.name === lessonForm.subject || cls.id === lessonForm.subject)
        || classes.find((cls) => cls.id === previousLesson?.classId);
      const recurrenceFrequency = editingLessonId ? 'none' : (lessonForm.recurrenceFrequency || 'none');
      const recurrenceDates = getRecurringLessonDates({
        startDate: lessonForm.date,
        endDate: lessonForm.recurrenceEndDate,
        frequency: recurrenceFrequency
      });
      if (recurrenceDates.length === 0) {
        showToast('Choose valid recurrence dates.', 'error');
        return;
      }
      const isRecurringCreate = !editingLessonId && recurrenceDates.length > 1;
      const recurrenceSeriesId = isRecurringCreate ? `series-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : previousLesson?.recurrenceSeriesId || '';
      const targetRefs = editingLessonId
        ? [doc(db, 'teachers', teacherId, 'lessonPlans', editingLessonId)]
        : recurrenceDates.map(() => doc(collection(db, 'teachers', teacherId, 'lessonPlans')));

      let uploadedAttachments = [];
      if (lessonUploadFiles.length > 0) {
        if (!storage) {
          showToast('Storage is not configured for uploads.', 'error');
          return;
        }
        uploadedAttachments = await Promise.all(
          lessonUploadFiles.map(async (file) => {
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const attachmentOwnerId = isRecurringCreate ? recurrenceSeriesId : targetRefs[0].id;
            const path = `teachers/${teacherId}/lessonPlans/${attachmentOwnerId}/${Date.now()}-${safeName}`;
            const ref = storageRef(storage, path);
            await uploadBytes(ref, file);
            const url = await getDownloadURL(ref);
            return {
              name: file.name,
              url,
              storagePath: path,
              type: file.type || 'application/octet-stream',
              size: file.size || 0
            };
          })
        );
      }

      const existingAttachments = (previousLesson?.attachments || []).filter(
        (attachment) => !removedAttachmentUrls.includes(attachment.url)
      );

      const {
        recurrenceFrequency: _unusedRecurrenceFrequency,
        recurrenceEndDate: _unusedRecurrenceEndDate,
        ...lessonFields
      } = lessonForm;
      const allAttachments = [...existingAttachments, ...uploadedAttachments];
      await Promise.all(targetRefs.map((targetRef, index) => {
        const date = editingLessonId ? lessonForm.date : recurrenceDates[index];
        const lessonPayload = {
          ...lessonFields,
          date,
          classId: linkedClass?.id || previousLesson?.classId || '',
          className: linkedClass?.name || previousLesson?.className || lessonForm.subject,
          attachments: allAttachments,
          exitTicketId: selectedExitTicket?.id || '',
          exitTicketTopic: selectedExitTicket?.topic || '',
          exitTicketTitle: selectedExitTicket?.title || '',
          exitTicket: preparedExitTicket,
          recurrenceFrequency: isRecurringCreate ? recurrenceFrequency : previousLesson?.recurrenceFrequency || 'none',
          recurrenceSeriesId,
          recurrenceStartDate: isRecurringCreate ? lessonForm.date : previousLesson?.recurrenceStartDate || '',
          recurrenceEndDate: isRecurringCreate ? lessonForm.recurrenceEndDate : previousLesson?.recurrenceEndDate || ''
        };

        return Promise.all([
          setDoc(targetRef, lessonPayload),
          setDoc(
            doc(db, 'artifacts', appId, 'public', 'data', 'plannerEntries', getStudentPlannerEntryId(teacherId, targetRef.id)),
            {
              teacherId,
              lessonId: targetRef.id,
              classId: linkedClass?.id || previousLesson?.classId || '',
              className: linkedClass?.name || previousLesson?.className || lessonForm.subject,
              topic: lessonForm.topic,
              date,
              startTime: lessonForm.startTime,
              endTime: lessonForm.endTime,
              notesForStudents: lessonForm.notesForStudents || '',
              attachments: allAttachments,
              exitTicketId: selectedExitTicket?.id || '',
              exitTicketTopic: selectedExitTicket?.topic || '',
              exitTicketTitle: selectedExitTicket?.title || '',
              exitTicket: preparedExitTicket,
              updatedAt: Date.now()
            },
            { merge: true }
          )
        ]);
      }));

      // Clean up storage objects for attachments explicitly removed in edit mode.
      if (editingLessonId && storage && previousLesson?.attachments?.length && !previousLesson?.recurrenceSeriesId) {
        const removedWithPath = previousLesson.attachments.filter(
          (attachment) => removedAttachmentUrls.includes(attachment.url) && attachment.storagePath
        );
        await Promise.all(
          removedWithPath.map(async (attachment) => {
            try {
              await deleteObject(storageRef(storage, attachment.storagePath));
            } catch (cleanupError) {
              console.warn('Attachment cleanup failed:', cleanupError);
            }
          })
        );
      }

      setLessonForm(createEmptyLessonForm());
      setShowLessonModal(false);
      setEditingLessonId('');
      setDuplicatingLessonDate('');
      setLessonUploadFiles([]);
      setRemovedAttachmentUrls([]);
      showToast(editingLessonId ? 'Lesson plan updated.' : recurrenceDates.length > 1 ? `${recurrenceDates.length} lesson plans saved.` : 'Lesson plan saved.');
    } catch (error) {
      showToast('Error saving lesson.', 'error');
    }
  };

  const handleRemoveAttachmentFromLesson = (url) => {
    if (!editingLessonId) return;
    setRemovedAttachmentUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
  };

  const saveInlineTeacherNotes = async (lesson) => {
    const draft = lessonTeacherNoteDrafts[lesson.id];
    if (draft === undefined || draft === (lesson.notesForTeachers || '')) return;

    try {
      await updateDoc(doc(db, 'teachers', user.uid, 'lessonPlans', lesson.id), {
        notesForTeachers: draft
      });
      setLessonTeacherNoteDrafts((current) => {
        const next = { ...current };
        delete next[lesson.id];
        return next;
      });
    } catch (error) {
      showToast('Could not save teacher notes.', 'error');
    }
  };

  const handleDeleteLesson = async (id) => {
    if (!user) return;
    const teacherId = user.uid;
    try {
      await deleteDoc(doc(db, 'teachers', teacherId, 'lessonPlans', id));
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'plannerEntries', getStudentPlannerEntryId(teacherId, id)));
      showToast('Lesson deleted.');
    } catch (error) {
      showToast('Could not delete lesson plan.', 'error');
    }
  };

  const handleAddLessonTodo = async (dateKey) => {
    if (!user) return;
    const text = (lessonTodoInputByDate[dateKey] || '').trim();
    if (!text) return;

    const existingForDay = lessonTodos.filter((todo) => todo.date === dateKey);
    const nextOrder = existingForDay.length > 0
      ? Math.max(...existingForDay.map((todo) => Number(todo.order) || 0)) + 1
      : 1;

    try {
      const docRef = isStudentUser
        ? doc(collection(db, 'studentProfiles', user.uid, 'plannerTodos'))
        : doc(collection(db, 'teachers', user.uid, 'lessonTodos'));
      await setDoc(docRef, {
        date: dateKey,
        text,
        done: false,
        order: nextOrder,
        createdAt: Date.now()
      });
      setLessonTodoInputByDate((prev) => ({ ...prev, [dateKey]: '' }));
    } catch (error) {
      showToast('Could not add todo item.', 'error');
    }
  };

  const handleSaveLessonTodoText = async (todoId) => {
    if (!user) return;
    const todo = lessonTodos.find((item) => item.id === todoId);
    if (!todo) return;

    const draft = lessonTodoDrafts[todoId];
    const text = (draft ?? todo.text ?? '').trim();
    if (!text) return;
    if (text === (todo.text || '')) return;

    try {
      await updateDoc(
        isStudentUser
          ? doc(db, 'studentProfiles', user.uid, 'plannerTodos', todoId)
          : doc(db, 'teachers', user.uid, 'lessonTodos', todoId),
        { text }
      );
    } catch (error) {
      showToast('Could not update todo item.', 'error');
    }
  };

  const handleDeleteLessonTodo = async (todoId) => {
    if (!user) return;
    try {
      await deleteDoc(
        isStudentUser
          ? doc(db, 'studentProfiles', user.uid, 'plannerTodos', todoId)
          : doc(db, 'teachers', user.uid, 'lessonTodos', todoId)
      );
    } catch (error) {
      showToast('Could not remove todo item.', 'error');
    }
  };

  const handleToggleLessonTodoDone = async (todoId, done) => {
    if (!user) return;
    try {
      await updateDoc(
        isStudentUser
          ? doc(db, 'studentProfiles', user.uid, 'plannerTodos', todoId)
          : doc(db, 'teachers', user.uid, 'lessonTodos', todoId),
        {
        done: !done
        }
      );
    } catch (error) {
      showToast('Could not update todo state.', 'error');
    }
  };

  const handleDropLessonTodo = async (targetDate, targetIndex = null) => {
    if (!user) return;
    if (!draggingTodoId) return;
    const sourceTodo = lessonTodos.find((todo) => todo.id === draggingTodoId);
    if (!sourceTodo) return;

    const sourceDate = sourceTodo.date;
    const sourceList = lessonTodos
      .filter((todo) => todo.date === sourceDate && todo.id !== sourceTodo.id)
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
    const targetList = lessonTodos
      .filter((todo) => todo.date === targetDate && todo.id !== sourceTodo.id)
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

    const insertIndex = targetIndex === null ? targetList.length : Math.max(0, Math.min(targetIndex, targetList.length));
    const movedTodo = { ...sourceTodo, date: targetDate };

    if (sourceDate === targetDate) {
      targetList.splice(insertIndex, 0, movedTodo);
      try {
        await Promise.all(targetList.map((todo, index) => updateDoc(
          isStudentUser
            ? doc(db, 'studentProfiles', user.uid, 'plannerTodos', todo.id)
            : doc(db, 'teachers', user.uid, 'lessonTodos', todo.id),
          { date: targetDate, order: index + 1 }
        )));
      } catch (error) {
        showToast('Could not reorder todo items.', 'error');
      }
    } else {
      targetList.splice(insertIndex, 0, movedTodo);
      try {
        await Promise.all([
          ...sourceList.map((todo, index) => updateDoc(
            isStudentUser
              ? doc(db, 'studentProfiles', user.uid, 'plannerTodos', todo.id)
              : doc(db, 'teachers', user.uid, 'lessonTodos', todo.id),
            { order: index + 1 }
          )),
          ...targetList.map((todo, index) => updateDoc(
            isStudentUser
              ? doc(db, 'studentProfiles', user.uid, 'plannerTodos', todo.id)
              : doc(db, 'teachers', user.uid, 'lessonTodos', todo.id),
            { date: targetDate, order: index + 1 }
          ))
        ]);
      } catch (error) {
        showToast('Could not move todo item.', 'error');
      }
    }

    setDraggingTodoId('');
  };

  const handleExitTicketAnswerChange = (entryId, questionId, answerValue) => {
    setExitTicketDrafts((prev) => {
      const existingDraft = prev[entryId] || { answers: {}, followUps: {}, drawings: {} };
      return {
        ...prev,
        [entryId]: {
          ...existingDraft,
          answers: {
            ...existingDraft.answers,
            [questionId]: answerValue
          }
        }
      };
    });
  };

  const handleExitTicketDrawingChange = (entryId, questionId, drawingValue) => {
    setExitTicketDrafts((prev) => {
      const existingDraft = prev[entryId] || { answers: {}, followUps: {}, drawings: {} };
      return {
        ...prev,
        [entryId]: {
          ...existingDraft,
          drawings: {
            ...existingDraft.drawings,
            [questionId]: drawingValue
          }
        }
      };
    });
  };

  const handleExitTicketFollowUpChange = (entryId, questionId, followUpValue) => {
    setExitTicketDrafts((prev) => {
      const existingDraft = prev[entryId] || { answers: {}, followUps: {}, drawings: {} };
      return {
        ...prev,
        [entryId]: {
          ...existingDraft,
          followUps: {
            ...existingDraft.followUps,
            [questionId]: followUpValue
          }
        }
      };
    });
  };

  const handleExitTicketReflectionChange = (entryId, field, value) => {
    setExitTicketDrafts((prev) => {
      const existingDraft = prev[entryId] || { answers: {}, followUps: {}, drawings: {}, reflections: {} };
      return {
        ...prev,
        [entryId]: {
          ...existingDraft,
          reflections: {
            ...existingDraft.reflections,
            [field]: value
          }
        }
      };
    });
  };

  const generateStudentExitTicketAiFeedback = async ({ lesson, responseDocId, normalizedTicket, submittedAnswers }) => {
    if (!firebaseFunctions || !user || !lesson?.teacherId || !lesson?.classId || !responseDocId) return;

    const shortAnswers = (submittedAnswers || []).filter((answerItem) => answerItem.type === 'short');
    if (shortAnswers.length === 0) return;

    try {
      const gradeShortAnswerResponse = httpsCallable(firebaseFunctions, 'gradeShortAnswerResponse');
      const results = await Promise.all(shortAnswers.map(async (answerItem) => {
        const questionConfig = normalizedTicket.questions.find((question) => question.id === answerItem.questionId) || {};
        try {
          const result = await gradeShortAnswerResponse({
            teacherId: lesson.teacherId || '',
            classId: lesson.classId || '',
            studentUid: user.uid,
            prompt: questionConfig.prompt || answerItem.prompt || '',
            answerKey: questionConfig.answerKey || '',
            systemPrompt: normalizedTicket.aiSystemPrompt || '',
            studentAnswer: answerItem.answer || '',
            studentDrawingDataUrl: answerItem.drawingDataUrl || '',
            followUpQuestion: answerItem.followUpQuestion || questionConfig.followUpQuestion || '',
            followUpAnswer: answerItem.followUpAnswer || ''
          });
          return [answerItem.questionId, result?.data || null];
        } catch (error) {
          return [answerItem.questionId, { error: true }];
        }
      }));

      const resultByQuestionId = new Map(results);
      const nextAnswers = (submittedAnswers || []).map((answerItem) => {
        if (answerItem.type !== 'short') return answerItem;

        const result = resultByQuestionId.get(answerItem.questionId);
        const suggestion = result?.analysis;
        if (!suggestion || typeof suggestion !== 'object') {
          return {
            ...answerItem,
            aiFeedbackStatus: 'failed'
          };
        }

        const aiIsCorrect = Boolean(suggestion.isCorrect);
        return {
          ...answerItem,
          aiSuggestion: {
            isCorrect: aiIsCorrect,
            confidence: Number(suggestion.confidence || 0),
            rationale: String(suggestion.rationale || ''),
            feedback: String(suggestion.suggestedFeedback || ''),
            followUpQuestion: String(suggestion.suggestedFollowUpQuestion || ''),
            model: String(result?.model || '')
          },
          manualIsCorrect: aiIsCorrect,
          finalIsCorrect: aiIsCorrect,
          teacherFeedback: String(suggestion.suggestedFeedback || answerItem.teacherFeedback || ''),
          followUpQuestion: String(suggestion.suggestedFollowUpQuestion || answerItem.followUpQuestion || ''),
          aiFeedbackStatus: 'completed',
          manualReviewStatus: 'completed'
        };
      });
      const summary = summarizeExitTicketAnswers(nextAnswers);

      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses', responseDocId),
        {
          answers: nextAnswers,
          correctCount: summary.correctCount,
          totalQuestions: summary.totalQuestions,
          scorePercent: summary.scorePercent,
          pendingManualCount: summary.pendingManualCount,
          aiFeedbackUpdatedAt: Date.now()
        }
      );
      showToast('Personalized AI feedback is ready.');
    } catch (error) {
      showToast('Exit ticket submitted, but AI feedback could not be generated.', 'error');
    }
  };

  const generateStudentReflectionAiFeedback = async ({ lesson, responseDocId, reflections, submittedAnswers, reflectionHistory }) => {
    if (!firebaseFunctions || !user || !responseDocId) return;
    try {
      const analyzeReflections = httpsCallable(firebaseFunctions, 'analyzeExitTicketReflections');
      const result = await analyzeReflections({
        mode: 'student_feedback',
        teacherId: lesson.teacherId || '',
        classId: lesson.classId || '',
        studentUid: user.uid,
        lessonTopic: lesson.topic || '',
        reflections,
        reflectionHistory,
        answers: (submittedAnswers || []).map((item) => ({
          prompt: item.prompt,
          answer: item.answer,
          isCorrect: item.finalIsCorrect,
          feedback: item.feedback
        }))
      });
      const analysis = result?.data?.analysis;
      if (!analysis || typeof analysis !== 'object') {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses', responseDocId), {
          reflectionAiFeedbackStatus: 'unavailable'
        });
        return;
      }
      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses', responseDocId),
        {
          reflectionAiFeedback: {
            feedback: String(analysis.feedback || ''),
            nextSteps: Array.isArray(analysis.nextSteps) ? analysis.nextSteps.map((item) => String(item)).slice(0, 3) : [],
            encouragement: String(analysis.encouragement || ''),
            followUpQuestion: String(analysis.followUpQuestion || ''),
            model: String(result?.data?.model || '')
          },
          reflectionAiFeedbackStatus: 'completed',
          reflectionAiFeedbackUpdatedAt: Date.now()
        }
      );
    } catch (error) {
      // The exit ticket remains submitted when optional AI support is unavailable.
      try {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses', responseDocId), {
          reflectionAiFeedbackStatus: 'unavailable'
        });
      } catch {
        // No further action is needed when status reporting also fails.
      }
    }
  };

  const handleGenerateReflectionDashboard = async (lesson, submissions) => {
    if (!firebaseFunctions || !lesson?.id || !user) {
      showToast('AI reflection analysis is not available.', 'error');
      return;
    }
    const reflections = (submissions || [])
      .filter((submission) => submission.reflections && Object.values(submission.reflections).some((value) => String(value || '').trim()))
      .map((submission) => ({
        learned: String(submission.reflections?.learned || ''),
        wantToKnow: String(submission.reflections?.wantToKnow || ''),
        revisit: String(submission.reflections?.revisit || '')
      }));
    if (reflections.length === 0) {
      showToast('No student reflections are available to summarize yet.', 'error');
      return;
    }
    setReflectionDashboardByLesson((prev) => ({ ...prev, [lesson.id]: { loading: true, analysis: prev[lesson.id]?.analysis || null } }));
    try {
      const analyzeReflections = httpsCallable(firebaseFunctions, 'analyzeExitTicketReflections');
      const result = await analyzeReflections({
        mode: 'dashboard',
        teacherId: user.uid,
        classId: lesson.classId || '',
        lessonTopic: lesson.topic || '',
        reflections
      });
      const analysis = result?.data?.analysis;
      if (!analysis || typeof analysis !== 'object') throw new Error('No AI summary returned.');
      setReflectionDashboardByLesson((prev) => ({ ...prev, [lesson.id]: { loading: false, analysis, responseCount: reflections.length } }));
    } catch (error) {
      setReflectionDashboardByLesson((prev) => ({ ...prev, [lesson.id]: { loading: false, analysis: prev[lesson.id]?.analysis || null } }));
      showToast('Could not generate the reflection summary.', 'error');
    }
  };

  const handleSubmitExitTicket = async (lesson) => {
    if (!user || !lesson?.id || !lesson?.teacherId || !lesson?.classId) return;

    const normalizedTicket = sanitizeExitTicketConfig(lesson.exitTicket);
    if (!normalizedTicket.enabled) {
      showToast('This lesson has no active exit ticket.', 'error');
      return;
    }

    const draft = exitTicketDrafts[lesson.id] || { answers: {}, followUps: {}, drawings: {}, reflections: {} };
    const answers = draft.answers || {};
    const followUps = draft.followUps || {};
    const drawings = draft.drawings || {};

    const missingAnswers = normalizedTicket.questions.filter((question) => {
      const typedAnswer = String(answers[question.id] || '').trim();
      const drawnAnswer = String(drawings[question.id] || '').trim();
      return !typedAnswer && !(question.type === 'short' && question.allowDrawing && drawnAnswer);
    });
    if (missingAnswers.length > 0) {
      showToast('Please answer every exit-ticket question before submitting.', 'error');
      return;
    }

    const membership = studentMemberships.find(
      (item) => item.teacherId === lesson.teacherId && item.classId === lesson.classId
    );
    const linkedClass = classes.find(
      (item) => (item.teacherId || '') === (lesson.teacherId || '') && item.id === lesson.classId
    );
    const fallbackName = membership?.studentName || user.email || 'Student';
    const resolvedStudent = (linkedClass?.students || []).find((student) => {
      const studentEmail = String(student?.email || '').trim().toLowerCase();
      if (!studentEmail) return false;
      return studentEmail === normalizedUserEmail;
    });

    const studentUid = user.uid;
    const studentName = resolvedStudent?.name || fallbackName;

    const perQuestion = normalizedTicket.questions.map((question) => {
      const answer = String(answers[question.id] || '').trim();
      const drawingDataUrl = question.type === 'short' && question.allowDrawing ? String(drawings[question.id] || '').trim() : '';
      const correct = isExitTicketQuestionCorrect(question, answer);
      const followUpAnswer = String(followUps[question.id] || '').trim();
      return {
        questionId: question.id,
        prompt: question.prompt,
        type: question.type,
        answer,
        drawingDataUrl,
        autoIsCorrect: correct,
        isCorrect: correct,
        finalIsCorrect: correct,
        manualIsCorrect: null,
        needsManualReview: question.type === 'short',
        manualReviewStatus: question.type === 'short' ? 'pending' : 'not-required',
        teacherFeedback: '',
        aiSuggestion: null,
        aiFeedbackStatus: question.type === 'short' && firebaseFunctions ? 'pending' : 'not-required',
        feedback: correct ? question.feedbackCorrect : question.feedbackIncorrect,
        followUpQuestion: !correct ? question.followUpQuestion : '',
        followUpAnswer: !correct ? followUpAnswer : ''
      };
    });

    const summary = summarizeExitTicketAnswers(perQuestion);
    const cleanedReflections = {
      learned: String(draft.reflections?.learned || '').trim(),
      wantToKnow: String(draft.reflections?.wantToKnow || '').trim(),
      revisit: String(draft.reflections?.revisit || '').trim()
    };
    const reflectionHistory = Object.values(exitTicketResponses)
      .filter((submission) => (
        submission
        && submission.entryId !== lesson.id
        && submission.teacherId === lesson.teacherId
        && submission.classId === lesson.classId
        && submission.reflections
        && Object.values(submission.reflections).some((value) => String(value || '').trim())
      ))
      .sort((a, b) => Number(b.submittedAt || 0) - Number(a.submittedAt || 0))
      .slice(0, 5)
      .map((submission) => ({
        date: submission.date || '',
        topic: submission.topic || '',
        learned: String(submission.reflections?.learned || ''),
        wantToKnow: String(submission.reflections?.wantToKnow || ''),
        revisit: String(submission.reflections?.revisit || ''),
        previousAiFollowUp: String(submission.reflectionAiFeedback?.followUpQuestion || '')
      }));
    const submissionPayload = {
      appId,
      entryId: lesson.id,
      lessonId: lesson.lessonId || '',
      teacherId: lesson.teacherId,
      classId: lesson.classId,
      className: lesson.className || '',
      topic: lesson.topic || '',
      date: lesson.date || '',
      studentUid,
      studentName,
      quizTitle: normalizedTicket.title || 'Exit Ticket',
      aiSystemPrompt: normalizedTicket.aiSystemPrompt || '',
      totalQuestions: summary.totalQuestions,
      correctCount: summary.correctCount,
      scorePercent: summary.scorePercent,
      pendingManualCount: summary.pendingManualCount,
      answers: perQuestion,
      reflections: cleanedReflections,
      reflectionPrompts: normalizedTicket.reflectionPrompts,
      reflectionEnabled: normalizedTicket.reflectionEnabled,
      reflectionAiFeedback: null,
      reflectionAiFeedbackStatus: normalizedTicket.reflectionEnabled && firebaseFunctions ? 'pending' : 'not-requested',
      submittedAt: Date.now()
    };

    try {
      const responseDocId = `${lesson.id}__${studentUid}`;
      await setDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses', responseDocId),
        submissionPayload,
        { merge: true }
      );
      showToast(firebaseFunctions ? 'Exit ticket submitted. Generating AI feedback...' : 'Exit ticket submitted.');
      generateStudentExitTicketAiFeedback({
        lesson,
        responseDocId,
        normalizedTicket,
        submittedAnswers: perQuestion
      });
      if (normalizedTicket.reflectionEnabled) {
        generateStudentReflectionAiFeedback({
          lesson,
          responseDocId,
          reflections: cleanedReflections,
          submittedAnswers: perQuestion,
          reflectionHistory
        });
      }
    } catch (error) {
      showToast('Could not submit exit ticket.', 'error');
    }
  };

  const handleSubmitExitTicketFollowUp = async (lesson, submission, answerItem, followUpValue) => {
    if (!user || !lesson?.id || !submission?.id || !answerItem?.questionId) return;
    if (submission.studentUid && submission.studentUid !== user.uid) return;

    const cleanedFollowUp = String(followUpValue || '').trim();
    if (!cleanedFollowUp) {
      showToast('Please write your follow-up response first.', 'error');
      return;
    }

    const nextAnswers = (submission.answers || []).map((item) => {
      if (item.questionId !== answerItem.questionId) return item;
      return {
        ...item,
        followUpAnswer: cleanedFollowUp,
        followUpSubmittedAt: Date.now(),
        manualReviewStatus: item.type === 'short' ? 'pending' : item.manualReviewStatus,
        aiFeedbackStatus: item.type === 'short' && firebaseFunctions ? 'pending' : item.aiFeedbackStatus,
        teacherFeedback: item.teacherFeedback || '',
        aiSuggestion: item.aiSuggestion || null
      };
    });
    const summary = summarizeExitTicketAnswers(nextAnswers);

    try {
      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses', submission.id),
        {
          answers: nextAnswers,
          correctCount: summary.correctCount,
          totalQuestions: summary.totalQuestions,
          scorePercent: summary.scorePercent,
          pendingManualCount: summary.pendingManualCount,
          followUpUpdatedAt: Date.now()
        }
      );
      showToast(firebaseFunctions ? 'Follow-up response submitted. Refreshing AI feedback...' : 'Follow-up response submitted.');
      generateStudentExitTicketAiFeedback({
        lesson,
        responseDocId: submission.id,
        normalizedTicket: sanitizeExitTicketConfig(lesson.exitTicket),
        submittedAnswers: nextAnswers
      });
    } catch (error) {
      showToast('Could not submit follow-up response.', 'error');
    }
  };

  const handleTeacherUpdateExitTicketAnswer = async (submission, questionId, updater) => {
    if (!submission?.id || !user) return;
    const nextAnswers = (submission.answers || []).map((answerItem) => {
      if (answerItem.questionId !== questionId) return answerItem;
      const updated = typeof updater === 'function' ? updater(answerItem) : { ...answerItem, ...updater };
      const finalIsCorrect = getExitTicketAnswerFinalCorrect(updated);
      return {
        ...updated,
        finalIsCorrect
      };
    });

    const summary = summarizeExitTicketAnswers(nextAnswers);

    try {
      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'exitTicketResponses', submission.id),
        {
          answers: nextAnswers,
          correctCount: summary.correctCount,
          totalQuestions: summary.totalQuestions,
          scorePercent: summary.scorePercent,
          pendingManualCount: summary.pendingManualCount,
          gradedAt: Date.now(),
          gradedBy: user.uid
        }
      );
    } catch (error) {
      showToast('Could not update grading.', 'error');
    }
  };

  const handleTeacherAiGradeShortAnswer = async ({ submission, answerItem, questionConfig }) => {
    if (!firebaseFunctions || !submission?.id || !answerItem?.questionId || !questionConfig) {
      showToast('AI grading is not available.', 'error');
      return;
    }

    const loadingKey = `${submission.id}:${answerItem.questionId}`;
    setExitTicketAiGradingLoading((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      const gradeShortAnswerResponse = httpsCallable(firebaseFunctions, 'gradeShortAnswerResponse');
      const result = await gradeShortAnswerResponse({
        teacherId: user?.uid || '',
        classId: submission.classId || '',
        prompt: questionConfig.prompt || answerItem.prompt || '',
        answerKey: questionConfig.answerKey || '',
        systemPrompt: submission.aiSystemPrompt || '',
        studentAnswer: answerItem.answer || '',
        studentDrawingDataUrl: answerItem.drawingDataUrl || '',
        followUpQuestion: answerItem.followUpQuestion || questionConfig.followUpQuestion || '',
        followUpAnswer: answerItem.followUpAnswer || ''
      });

      const suggestion = result?.data?.analysis;
      if (!suggestion || typeof suggestion !== 'object') {
        showToast('AI grading returned no suggestion.', 'error');
        return;
      }

      await handleTeacherUpdateExitTicketAnswer(submission, answerItem.questionId, (current) => ({
        ...current,
        aiSuggestion: {
          isCorrect: Boolean(suggestion.isCorrect),
          confidence: Number(suggestion.confidence || 0),
          rationale: String(suggestion.rationale || ''),
          feedback: String(suggestion.suggestedFeedback || ''),
          followUpQuestion: String(suggestion.suggestedFollowUpQuestion || ''),
          model: String(result?.data?.model || '')
        },
        manualIsCorrect: Boolean(suggestion.isCorrect),
        teacherFeedback: String(suggestion.suggestedFeedback || current.teacherFeedback || ''),
        followUpQuestion: String(suggestion.suggestedFollowUpQuestion || current.followUpQuestion || ''),
        aiFeedbackStatus: 'completed',
        manualReviewStatus: 'completed'
      }));
      showToast('AI feedback applied.');
    } catch (error) {
      showToast('AI grading failed for this response.', 'error');
    } finally {
      setExitTicketAiGradingLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleMarkAttendance = async (studentId, status) => {
    if (!user || !activeClass) return;
    const teacherId = user.uid;
    const recordId = `${activeClass.id}-${attendanceDate}`;
    const existingRecord = attendance.find((a) => a.id === recordId);
    const updatedStatuses = existingRecord ? { ...existingRecord.statuses, [studentId]: status } : { [studentId]: status };
    try {
      await setDoc(doc(db, 'teachers', teacherId, 'attendance', recordId), {
        classId: activeClass.id,
        className: activeClass.name,
        date: attendanceDate,
        statuses: updatedStatuses,
        comments: existingRecord?.comments || {}
      });
    } catch (error) {
      showToast('Error updating attendance.', 'error');
    }
  };

  const handleMarkAllPresent = async () => {
    if (!user || !activeClass) return;
    const teacherId = user.uid;
    const recordId = `${activeClass.id}-${attendanceDate}`;
    const existingRecord = attendance.find((a) => a.id === recordId);
    const updatedStatuses = existingRecord ? { ...existingRecord.statuses } : {};
    const pendingStudentIds = activeClass.students
      .filter((student) => !updatedStatuses[student.id] || updatedStatuses[student.id] === 'pending')
      .map((student) => student.id);

    if (pendingStudentIds.length === 0) {
      showToast('All students are already marked.');
      return;
    }

    pendingStudentIds.forEach((studentId) => {
      updatedStatuses[studentId] = 'present';
    });

    try {
      await setDoc(doc(db, 'teachers', teacherId, 'attendance', recordId), {
        classId: activeClass.id,
        className: activeClass.name,
        date: attendanceDate,
        statuses: updatedStatuses,
        comments: existingRecord?.comments || {}
      });
      showToast(`Marked ${pendingStudentIds.length} student${pendingStudentIds.length === 1 ? '' : 's'} present.`);
    } catch (error) {
      showToast('Error marking all present.', 'error');
    }
  };

  const handleUnmarkAll = async () => {
    if (!user || !activeClass) return;
    const teacherId = user.uid;
    const recordId = `${activeClass.id}-${attendanceDate}`;
    const existingRecord = attendance.find((a) => a.id === recordId);
    if (!existingRecord || !existingRecord.statuses || Object.keys(existingRecord.statuses).length === 0) {
      showToast('No attendance marks to clear.');
      return;
    }

    try {
      await setDoc(doc(db, 'teachers', teacherId, 'attendance', recordId), {
        classId: activeClass.id,
        className: activeClass.name,
        date: attendanceDate,
        statuses: {},
        comments: existingRecord.comments || {}
      });
      showToast('All attendance marks cleared.');
    } catch (error) {
      showToast('Error clearing attendance marks.', 'error');
    }
  };

  const getAttendanceStatus = (studentId) => {
    if (!activeClass) return 'pending';
    const recordId = `${activeClass.id}-${attendanceDate}`;
    const record = attendance.find((a) => a.id === recordId);
    return record?.statuses?.[studentId] || 'pending';
  };

  const getAttendanceComment = (studentId) => {
    if (!activeClass) return '';
    const recordId = `${activeClass.id}-${attendanceDate}`;
    const record = attendance.find((a) => a.id === recordId);
    return record?.comments?.[studentId] || '';
  };

  const handleAttendanceCommentChange = async (studentId, comment) => {
    if (!user || !activeClass) return;
    const teacherId = user.uid;
    const recordId = `${activeClass.id}-${attendanceDate}`;
    const existingRecord = attendance.find((a) => a.id === recordId);
    const updatedComments = existingRecord ? { ...(existingRecord.comments || {}) } : {};
    const trimmedComment = comment.trim();

    if (trimmedComment) {
      updatedComments[studentId] = comment;
    } else {
      delete updatedComments[studentId];
    }

    try {
      await setDoc(doc(db, 'teachers', teacherId, 'attendance', recordId), {
        classId: activeClass.id,
        className: activeClass.name,
        date: attendanceDate,
        statuses: existingRecord?.statuses || {},
        comments: updatedComments
      });
    } catch (error) {
      showToast('Error saving attendance comment.', 'error');
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!user || !activeClass) return;
    if (!homeworkForm.title) {
      showToast('Title is required.', 'error');
      return;
    }
    const teacherId = user.uid;
    try {
      const docRef = doc(collection(db, 'teachers', teacherId, 'assignments'));
      const statusMap = {};
      activeClass.students.forEach((s) => {
        statusMap[s.id] = { status: 'unsubmitted', score: '' };
      });
      await setDoc(docRef, {
        classId: activeClass.id,
        className: activeClass.name,
        title: homeworkForm.title,
        dueDate: homeworkForm.dueDate,
        maxPoints: homeworkForm.maxPoints,
        trackingMode: 'score',
        submissions: statusMap
      });
      setHomeworkForm({ title: '', dueDate: '', maxPoints: '10' });
      showToast('Homework assigned.');
    } catch (error) {
      showToast('Could not assign homework.', 'error');
    }
  };

  const getHomeworkTrackingMode = (homeworkItem) => {
    if (homeworkItem?.trackingMode === 'score' || homeworkItem?.trackingMode === 'status') {
      return homeworkItem.trackingMode;
    }

    const values = Object.values(homeworkItem?.submissions || {});
    const hasScore = values.some((value) => {
      if (typeof value === 'number') return true;
      if (typeof value === 'object' && value !== null) {
        return value.mode === 'score' || (value.score !== undefined && value.score !== null && String(value.score).trim() !== '');
      }
      return false;
    });

    return hasScore ? 'score' : 'status';
  };

  const getHomeworkSubmission = (homeworkItem, studentId) => {
    const raw = homeworkItem?.submissions?.[studentId];
    if (!raw) return { status: 'unsubmitted', score: '' };

    if (typeof raw === 'number') {
      return { status: 'unsubmitted', score: String(raw) };
    }

    if (typeof raw === 'object') {
      if (raw.mode === 'score') {
        return {
          status: 'unsubmitted',
          score: raw.score === '' || raw.score === null || raw.score === undefined ? '' : String(raw.score)
        };
      }

      if (raw.mode === 'status') {
        return {
          status: raw.status || 'unsubmitted',
          score: ''
        };
      }

      return {
        status: raw.status || 'unsubmitted',
        score: raw.score === '' || raw.score === null || raw.score === undefined ? '' : String(raw.score)
      };
    }

    if (raw === 'completed') return { status: 'submitted', score: '' };
    if (raw === 'missing') return { status: 'unsubmitted', score: '' };
    if (raw === 'pending') return { status: 'unsubmitted', score: '' };
    if (raw === 'late' || raw === 'submitted' || raw === 'unsubmitted') {
      return { status: raw, score: '' };
    }

    return { status: 'unsubmitted', score: '' };
  };

  const handleDownloadHomeworkCsv = () => {
    if (!activeClass) {
      showToast('Select a class first.', 'error');
      return;
    }

    const classAssignments = homework.filter((item) => item.classId === activeClass.id);
    if (classAssignments.length === 0) {
      showToast('This class has no assignments to export.', 'error');
      return;
    }

    const csvCell = (value) => {
      let text = String(value ?? '');
      if (/^[=+\-@]/.test(text)) text = `'${text}`;
      return `"${text.replace(/"/g, '""')}"`;
    };
    const rows = [[
      'Class',
      'Student Name',
      'Student Email',
      'Assignment',
      'Due Date',
      'Submission Status',
      'Score',
      'Max Score'
    ]];

    (activeClass.students || []).forEach((student) => {
      classAssignments.forEach((assignment) => {
        const submission = getHomeworkSubmission(assignment, student.id);
        const score = submission.score ?? '';
        const hasScore = String(score).trim() !== '' && score !== 'N.A.';
        const status = hasScore && (!submission.status || submission.status === 'unsubmitted')
          ? 'submitted'
          : (submission.status || 'unsubmitted');
        rows.push([
          activeClass.name,
          student.name,
          student.email || '',
          assignment.title,
          assignment.dueDate || '',
          status,
          score,
          assignment.maxPoints ?? ''
        ]);
      });
    });

    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeClassName = String(activeClass.name || 'class')
      .trim()
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'class';
    link.href = url;
    link.download = `${safeClassName}-homework.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('Homework CSV downloaded.');
  };

  const handleUpdateSubmission = async (homeworkId, studentId, submission) => {
    if (!user) return;
    const teacherId = user.uid;
    const hwItem = homework.find((h) => h.id === homeworkId);
    if (!hwItem) return;
    const updatedSubmissions = { ...hwItem.submissions, [studentId]: submission };
    try {
      await updateDoc(doc(db, 'teachers', teacherId, 'assignments', homeworkId), {
        submissions: updatedSubmissions
      });
    } catch (error) {
      showToast('Error updating submission.', 'error');
    }
  };

  const handleSetHomeworkTrackingMode = async (homeworkId, mode) => {
    if (!user) return;
    const teacherId = user.uid;
    try {
      await updateDoc(doc(db, 'teachers', teacherId, 'assignments', homeworkId), {
        trackingMode: mode
      });
    } catch (error) {
      showToast('Error updating assignment mode.', 'error');
    }
  };

  const handleBatchUpdateSubmissionStatus = async (homeworkItem, status) => {
    if (!user) return;
    const teacherId = user.uid;
    const targetClass = classes.find((cls) => cls.id === homeworkItem.classId);
    if (!targetClass) return;

    const updatedSubmissions = { ...homeworkItem.submissions };
    targetClass.students.forEach((student) => {
      const current = getHomeworkSubmission(homeworkItem, student.id);
      updatedSubmissions[student.id] = { status, score: current.score || '' };
    });

    try {
      await updateDoc(doc(db, 'teachers', teacherId, 'assignments', homeworkItem.id), {
        trackingMode: 'status',
        submissions: updatedSubmissions
      });
      showToast('Batch submission status updated.');
    } catch (error) {
      showToast('Error applying batch status update.', 'error');
    }
  };

  const parseBatchScores = (text, students) => {
    const rows = text
      .replace(/\r/g, '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (rows.length === 0) {
      return { updates: {}, errors: ['Paste score rows first.'] };
    }

    const updates = {};
    const errors = [];
    const byName = new Map(students.map((student) => [student.name.toLowerCase(), student.id]));

    rows.forEach((row, index) => {
      const parts = row
        .split(/[\t,]/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      let studentId = '';
      let scoreToken = '';

      if (parts.length === 1) {
        studentId = students[index]?.id || '';
        scoreToken = parts[0];
      } else {
        const possibleName = parts.slice(0, -1).join(' ').toLowerCase();
        studentId = byName.get(possibleName) || students[index]?.id || '';
        scoreToken = parts[parts.length - 1];
      }

      const numericScore = Number(scoreToken);
      if (!studentId) {
        errors.push(`Row ${index + 1}: could not match a student.`);
        return;
      }

      if (Number.isNaN(numericScore)) {
        errors.push(`Row ${index + 1}: invalid score value.`);
        return;
      }

      updates[studentId] = String(numericScore);
    });

    return { updates, errors };
  };

  const handleBatchPasteScores = async (homeworkItem) => {
    if (!user) return;
    const teacherId = user.uid;
    const targetClass = classes.find((cls) => cls.id === homeworkItem.classId);
    if (!targetClass) return;

    const scoreText = homeworkScorePaste[homeworkItem.id] || '';
    const { updates, errors } = parseBatchScores(scoreText, targetClass.students || []);
    if (errors.length > 0) {
      showToast(errors[0], 'error');
      return;
    }

    const updatedSubmissions = { ...homeworkItem.submissions };
    Object.entries(updates).forEach(([studentId, score]) => {
      const current = getHomeworkSubmission(homeworkItem, studentId);
      updatedSubmissions[studentId] = {
        status: current.status || 'unsubmitted',
        score
      };
    });

    try {
      await updateDoc(doc(db, 'teachers', teacherId, 'assignments', homeworkItem.id), {
        trackingMode: 'score',
        submissions: updatedSubmissions
      });
      setHomeworkScorePaste((prev) => ({ ...prev, [homeworkItem.id]: '' }));
      showToast('Scores pasted for class.');
    } catch (error) {
      showToast('Error applying pasted scores.', 'error');
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!user) return;
    const teacherId = user.uid;
    try {
      await deleteDoc(doc(db, 'teachers', teacherId, 'assignments', id));
      showToast('Assignment deleted.');
    } catch (error) {
      showToast('Could not delete homework.', 'error');
    }
  };

  const handleSaveHomeworkEdit = async (e) => {
    e.preventDefault();
    if (!user || !selectedHomeworkEditItem) return;
    if (!homeworkEditForm.title) {
      showToast('Title is required.', 'error');
      return;
    }
    const teacherId = user.uid;
    try {
      await updateDoc(doc(db, 'teachers', teacherId, 'assignments', selectedHomeworkEditItem.id), {
        title: homeworkEditForm.title,
        dueDate: homeworkEditForm.dueDate,
        maxPoints: homeworkEditForm.maxPoints || '10',
        trackingMode: homeworkEditForm.trackingMode || 'score'
      });
      showToast('Assignment updated.');
    } catch (error) {
      showToast('Could not update assignment.', 'error');
    }
  };

  const parseCsvRow = (row) => {
    const values = row
      .split(/,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/)
      .map((value) => value.trim().replace(/^['\"]|['\"]$/g, ''));

    return values;
  };

  const parseSpreadsheetRows = (text) => {
    const normalizedText = String(text || '').replace(/\r/g, '').replace(/^\uFEFF/, '');
    return normalizedText
      .split('\n')
      .filter((line) => line.trim().length > 0)
      .map((line) => line.includes('\t') ? line.split('\t').map((value) => value.trim()) : parseCsvRow(line));
  };

  const getHomeworkSheetCsvUrl = (url) => {
    const match = String(url || '').match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (!match) return '';
    const gid = String(url || '').match(/[?&#]gid=(\d+)/)?.[1] || '0';
    return `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv&gid=${gid}`;
  };

  const handleLoadHomeworkSheet = async () => {
    const csvUrl = getHomeworkSheetCsvUrl(homeworkSheetUrl);
    if (!csvUrl) {
      setHomeworkSheetImportErrors(['Enter a valid Google Sheets URL.']);
      return;
    }

    setIsImportingHomeworkSheet(true);
    setHomeworkSheetImportErrors([]);
    setHomeworkSheetImportLog('');
    try {
      const response = await fetch(csvUrl);
      if (!response.ok) throw new Error(`Google Sheets returned ${response.status}.`);
      const text = await response.text();
      if (!text.trim() || text.trim().startsWith('<!DOCTYPE')) {
        throw new Error('The sheet did not return CSV data.');
      }
      setHomeworkSheetCsv(text);
      setHomeworkSheetImportLog('Sheet loaded. Review and import when ready.');
    } catch (error) {
      setHomeworkSheetImportErrors([
        `${error.message || 'Could not load the sheet'} If the sheet is private, download the Grades tab as CSV and upload it below.`
      ]);
    } finally {
      setIsImportingHomeworkSheet(false);
    }
  };

  const handleHomeworkSheetFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setHomeworkSheetCsv(await file.text());
    setHomeworkSheetImportErrors([]);
    setHomeworkSheetImportLog(`${file.name} loaded. Review and import when ready.`);
  };

  const handleImportHomeworkSheet = async () => {
    if (!user || !homeworkSheetCsv.trim()) {
      setHomeworkSheetImportErrors(['Load the Google Sheet or select a CSV file first.']);
      return;
    }

    const rows = parseSpreadsheetRows(homeworkSheetCsv);
    const headers = rows[0] || [];
    const normalizedHeaders = headers.map((value) => String(value || '').trim().toLowerCase());
    const emailIndex = normalizedHeaders.indexOf('email');
    const classIndex = normalizedHeaders.indexOf('class');
    const nameIndex = normalizedHeaders.indexOf('name');
    if (emailIndex < 0 || classIndex < 0 || nameIndex < 0 || headers.length <= 4) {
      setHomeworkSheetImportErrors(['Expected headers Email, Class, Name, followed by homework columns.']);
      return;
    }

    const dataRows = rows.slice(1).filter((row) => String(row[emailIndex] || '').trim());
    const classByLabel = new Map();
    classes.forEach((cls) => {
      classByLabel.set(String(cls.name || '').trim().toLowerCase(), cls);
      classByLabel.set(String(cls.id || '').trim().toLowerCase(), cls);
    });
    const combinedAcClass = classByLabel.get('s3-ac');
    if (combinedAcClass) {
      classByLabel.set('s3-a', combinedAcClass);
      classByLabel.set('s3-c', combinedAcClass);
    }

    const unmatchedClasses = new Set();
    const unmatchedStudents = [];
    const matchedRows = dataRows.map((row) => {
      const classLabel = String(row[classIndex] || '').trim();
      const targetClass = classByLabel.get(classLabel.toLowerCase());
      if (!targetClass) {
        unmatchedClasses.add(classLabel || '(blank)');
        return null;
      }
      const email = String(row[emailIndex] || '').trim().toLowerCase();
      const student = (targetClass.students || []).find(
        (entry) => String(entry.email || '').trim().toLowerCase() === email
      );
      if (!student) {
        unmatchedStudents.push(`${row[nameIndex] || email} (${classLabel})`);
        return null;
      }
      return { row, targetClass, student };
    }).filter(Boolean);

    if (unmatchedClasses.size > 0 || unmatchedStudents.length > 0) {
      const errors = [];
      if (unmatchedClasses.size > 0) errors.push(`Classes not found: ${Array.from(unmatchedClasses).join(', ')}`);
      if (unmatchedStudents.length > 0) errors.push(`Students not matched by email: ${unmatchedStudents.slice(0, 8).join(', ')}${unmatchedStudents.length > 8 ? ` and ${unmatchedStudents.length - 8} more` : ''}`);
      setHomeworkSheetImportErrors(errors);
      return;
    }

    const teacherId = user.uid;
    const spreadsheetMatch = homeworkSheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    const sourceSpreadsheetId = spreadsheetMatch?.[1] || 'csv-upload';
    const assignments = [];

    for (let columnIndex = 4; columnIndex < headers.length; columnIndex += 1) {
      const title = String(headers[columnIndex] || '').trim();
      if (!title) continue;
      const populatedValues = matchedRows
        .map(({ row }) => String(row[columnIndex] ?? '').trim())
        .filter(Boolean);
      const numericValues = populatedValues
        .map((value) => Number(value.replace(',', '.')))
        .filter((value) => Number.isFinite(value));
      const trackingMode = numericValues.length > populatedValues.length / 2 ? 'score' : 'status';
      const headerMax = title.match(/\((?:max:\s*)?(\d+(?:\.\d+)?)\)\s*$/i)?.[1] || '';
      const maxPoints = headerMax || (numericValues.length > 0 ? String(Math.max(...numericValues)) : '');

      classes.forEach((targetClass) => {
        const classRows = matchedRows.filter(({ targetClass: matchedClass }) => matchedClass.id === targetClass.id);
        if (classRows.length === 0) return;
        const submissions = {};
        classRows.forEach(({ row, student }) => {
          const rawValue = String(row[columnIndex] ?? '').trim();
          const numericValue = Number(rawValue.replace(',', '.'));
          if (trackingMode === 'score') {
            submissions[student.id] = {
              status: 'unsubmitted',
              score: rawValue && Number.isFinite(numericValue) ? String(numericValue) : 'N.A.'
            };
          } else {
            const normalizedValue = rawValue.toLowerCase();
            const status = ['submitted', 'submitted via email', 'done', 'complete', 'completed'].includes(normalizedValue)
              ? 'submitted'
              : normalizedValue === 'late' ? 'late' : 'unsubmitted';
            submissions[student.id] = { status, score: '' };
          }
        });

        assignments.push({
          id: `sheet-${columnIndex}-${String(targetClass.id).replace(/[^a-zA-Z0-9_-]/g, '-')}`,
          data: {
            classId: targetClass.id,
            className: targetClass.name,
            title,
            dueDate: '',
            maxPoints,
            trackingMode,
            submissions,
            sourceSpreadsheetId,
            sourceSheetName: 'Grades',
            sourceColumnIndex: columnIndex
          }
        });
      });
    }

    setIsImportingHomeworkSheet(true);
    setHomeworkSheetImportErrors([]);
    try {
      for (const assignment of assignments) {
        await setDoc(doc(db, 'teachers', teacherId, 'assignments', assignment.id), assignment.data, { merge: true });
      }
      setHomeworkSheetImportLog(`Imported ${assignments.length} class assignments for ${matchedRows.length} students. Re-importing updates the same records.`);
      showToast('Homework sheet imported.');
    } catch (error) {
      setHomeworkSheetImportErrors([error.message || 'Homework import failed.']);
      showToast('Homework import failed.', 'error');
    } finally {
      setIsImportingHomeworkSheet(false);
    }
  };

  const parseBulkStudentCsv = (text) => {
    const normalizedText = text.replace(/\r/g, '').replace(/^\uFEFF/, '');
    const rows = normalizedText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (rows.length === 0) {
      return { entries: [], errors: ['No rows found in CSV text.'] };
    }

    const headerRow = parseCsvRow(rows[0]);
    const headerLower = headerRow.map((cell) => cell.toLowerCase().replace(/^\uFEFF/, '').trim());
    const hasHeader = headerLower.includes('email') && headerLower.includes('name') && headerLower.includes('class');
    const dataRows = hasHeader ? rows.slice(1) : rows;
    const errors = [];
    const entries = [];

    dataRows.forEach((row, index) => {
      const values = parseCsvRow(row);
      const entry = { email: '', name: '', classLabel: '' };

      if (hasHeader) {
        headerLower.forEach((field, i) => {
          if (field.includes('email')) entry.email = values[i] || '';
          if (field.includes('name')) entry.name = values[i] || '';
          if (field.includes('class')) entry.classLabel = values[i] || '';
        });
      } else {
        entry.email = values[0] || '';
        entry.name = values[1] || '';
        entry.classLabel = values[2] || '';
      }

      if (!entry.name && !entry.email) {
        return;
      }

      if (!entry.email || !entry.name || !entry.classLabel) {
        errors.push(`Row ${index + 1}: missing required field (email, name, or class).`);
        return;
      }

      if (!isAllowedStudentEmail(entry.email)) {
        errors.push(`Row ${index + 1}: student email must end with ${STUDENT_EMAIL_DOMAIN_LABEL}.`);
        return;
      }

      entries.push({
        email: entry.email.trim(),
        name: entry.name.trim(),
        classLabel: entry.classLabel.trim()
      });
    });

    return { entries, errors };
  };

  const handleBulkCsvFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const rawText = await file.text();
    const normalizedText = rawText.replace(/\r/g, '').replace(/^\uFEFF/, '');
    setBulkCsvText(normalizedText);
    setBulkImportErrors([]);
    setBulkImportLog('');
  };

  const downloadCsvTemplate = () => {
    const csv = 'email,name,class\nstudent@students.edu.sg,Student Name,Grade 10 Math\ntest.student@myhub.test,Test Student,Grade 11 Physics';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBulkImportStudents = async () => {
    if (!bulkCsvText.trim()) {
      setBulkImportErrors(['Paste CSV text or select a file first.']);
      return;
    }

    const { entries, errors } = parseBulkStudentCsv(bulkCsvText);
    if (errors.length > 0) {
      setBulkImportErrors(errors);
      setBulkImportLog('');
      return;
    }

    const classMap = new Map();
    classes.forEach((cls) => classMap.set(cls.name.toLowerCase(), cls));
    classes.forEach((cls) => classMap.set(cls.id.toLowerCase(), cls));

    const updates = new Map();
    const createClasses = new Map();
    const skipped = [];

    entries.forEach((entry, index) => {
      const classKey = entry.classLabel.toLowerCase();
      let targetClass = classMap.get(classKey);
      if (!targetClass) {
        const classId = `class-${Date.now()}-${index}`;
        targetClass = { id: classId, name: entry.classLabel, students: [] };
        classMap.set(classKey, targetClass);
        classMap.set(classId.toLowerCase(), targetClass);
        createClasses.set(classId, targetClass);
      }

      const existingStudents = targetClass.students || [];
      const existingEmailSet = new Set(existingStudents.map((student) => (student.email || '').toLowerCase()));
      if (existingEmailSet.has(entry.email.toLowerCase())) {
        skipped.push(`${entry.name} (${entry.email}) in ${targetClass.name}`);
        return;
      }

      const studentId = `stu-${Date.now()}-${index}`;
      existingStudents.push({ id: studentId, name: entry.name, email: entry.email });
      updates.set(targetClass.id, existingStudents);
    });

    if (!user) return;
    const teacherId = user.uid;
    try {
      for (const [classId, students] of updates.entries()) {
        const targetClass = classMap.get(classId.toLowerCase()) || Array.from(classMap.values()).find((cls) => cls.id === classId);
        await setDoc(doc(db, 'teachers', teacherId, 'classes', classId), {
          ...targetClass,
          students
        });
      }

      for (const cls of createClasses.values()) {
        if (!updates.has(cls.id)) {
          await setDoc(doc(db, 'teachers', teacherId, 'classes', cls.id), cls);
        }
      }

      setBulkImportErrors([]);
      setBulkImportLog(`Imported ${updates.size} class updates. ${skipped.length > 0 ? `${skipped.length} skipped duplicates.` : ''}`);
      setBulkCsvText('');
      showToast('Bulk import complete.');
    } catch (error) {
      showToast('Bulk import failed.', 'error');
      setBulkImportErrors([error.message || 'Unknown error']);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!user || !studentName.trim() || !studentEmail.trim()) return;
    const teacherId = user.uid;
    const classId = selectedClassId || activeClass?.id || '';
    if (!classId) {
      showToast('Select a class first.', 'error');
      return;
    }

    if (!isAllowedStudentEmail(studentEmail)) {
      showToast(`Student emails must end with ${STUDENT_EMAIL_DOMAIN_LABEL}.`, 'error');
      return;
    }

    try {
      const targetClass = classes.find((cls) => cls.id === classId);
      if (!targetClass) {
        showToast('Selected class not found.', 'error');
        return;
      }

      const normalizedEmail = studentEmail.trim().toLowerCase();
      const duplicateExists = (targetClass.students || []).some(
        (student) => String(student?.email || '').trim().toLowerCase() === normalizedEmail
      );
      if (duplicateExists) {
        showToast('Student email already exists in this class.', 'error');
        return;
      }

      const nextStudents = [
        ...(targetClass.students || []),
        {
          id: `stu-${Date.now()}`,
          name: studentName.trim(),
          email: normalizedEmail
        }
      ];

      await setDoc(doc(db, 'teachers', teacherId, 'classes', classId), {
        ...targetClass,
        students: nextStudents
      });

      setSelectedClassId(classId);
      setStudentName('');
      setStudentEmail('');
      showToast('Student added to class.');
    } catch (error) {
      showToast('Could not add student.', 'error');
    }
  };

  const handleRemoveStudent = async (classId, studentId, studentEmail = '') => {
    if (!user) return;
    const teacherId = user.uid;

    try {
      const cls = classes.find((item) => item.id === classId);
      if (!cls) {
        showToast('Class not found.', 'error');
        return;
      }

      const targetEmail = String(studentEmail || '').trim().toLowerCase();
      const nextStudents = (cls.students || []).filter((student) => {
        const sameId = student?.id && student.id === studentId;
        const sameEmail = targetEmail && String(student?.email || '').trim().toLowerCase() === targetEmail;
        return !(sameId || sameEmail);
      });

      await setDoc(doc(db, 'teachers', teacherId, 'classes', classId), {
        ...cls,
        students: nextStudents
      });

      showToast('Student removed.');
    } catch (error) {
      showToast('Could not remove student.', 'error');
    }
  };

  const handleAddClass = async (e) => {
    e.preventDefault();
    if (!user || !newClassName.trim()) return;
    const teacherId = user.uid;
    const classId = `class-${Date.now()}`;
    try {
      await setDoc(doc(db, 'teachers', teacherId, 'classes', classId), {
        id: classId,
        name: newClassName.trim(),
        students: []
      });
      setNewClassName('');
      setSelectedClassId(classId);
      showToast('Class created.');
    } catch (error) {
      showToast('Could not create class.', 'error');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!user || !window.confirm('Are you sure you want to delete this class?')) return;
    const teacherId = user.uid;
    try {
      await deleteDoc(doc(db, 'teachers', teacherId, 'classes', classId));
      if (selectedClassId === classId) setSelectedClassId('');
      showToast('Class deleted.');
    } catch (error) {
      showToast('Could not delete class.', 'error');
    }
  };

  const normalizedUserEmail = (user?.email || '').trim().toLowerCase();
  const studentMemberships = useMemo(() => {
    if (studentProfile?.memberships?.length) {
      return dedupeStudentMemberships(studentProfile.memberships);
    }
    if (!normalizedUserEmail) return [];
    const matches = [];
    classes.forEach((cls) => {
      (cls.students || []).forEach((student) => {
        if ((student.email || '').trim().toLowerCase() === normalizedUserEmail) {
          matches.push({
            classId: cls.id,
            className: cls.name,
            studentId: student.id,
            studentName: student.name,
            email: student.email
          });
        }
      });
    });
    return dedupeStudentMemberships(matches);
  }, [classes, normalizedUserEmail]);
  const isStudentUser = Boolean(studentProfile) || (Boolean(normalizedUserEmail) && studentMemberships.length > 0);

  useEffect(() => {
    if (!user || isStudentUser || activeTab !== 'lessons') return;
    const refresh = () => {
      if (document.visibilityState === 'visible') handleImportGoogleCalendarLessons({ refresh: true });
    };
    refresh();
    const interval = window.setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [user?.uid, activeTab, isStudentUser]);


  const canManageForumRooms = Boolean(user) && !user.isAnonymous && !isStudentUser;
  const studentHomeworkItems = useMemo(() => {
    if (!isStudentUser) return [];
    const classByKey = new Map(classes.map((cls) => [`${cls.teacherId || ''}:${cls.id}`, cls]));
    const membershipByKey = new Map(studentMemberships.map((membership) => [`${membership.teacherId || ''}:${membership.classId}`, membership]));
    const uniqueHomework = new Map();
    homework
      .filter((hw) => membershipByKey.has(`${hw.teacherId || ''}:${hw.classId}`))
      .map((hw) => {
        const membership = membershipByKey.get(`${hw.teacherId || ''}:${hw.classId}`);
        const linkedClass = classByKey.get(`${hw.teacherId || ''}:${hw.classId}`);
        const resolvedStudent = (linkedClass?.students || []).find((student) => {
          const studentEmail = String(student?.email || '').trim().toLowerCase();
          if (!studentEmail) return false;
          if (studentEmail === normalizedUserEmail) return true;
          return studentEmail === String(membership.email || '').trim().toLowerCase();
        });
        const resolvedStudentId = resolvedStudent?.id || membership.studentId;
        const submission = getHomeworkSubmission(hw, resolvedStudentId);
        const item = {
          ...hw,
          className: linkedClass?.name || membership.className,
          studentName: resolvedStudent?.name || membership.studentName,
          submission,
          trackingMode: getHomeworkTrackingMode(hw)
        };
        const normalizedTitle = String(item.title || '')
          .trim()
          .replace(/\s+/g, ' ')
          .toLocaleLowerCase();
        const uniqueKey = [
          item.teacherId || '',
          item.classId || '',
          normalizedTitle,
          item.dueDate || ''
        ].join(':');
        const existing = uniqueHomework.get(uniqueKey);
        if (!existing) {
          uniqueHomework.set(uniqueKey, { ...item, studentRowKey: uniqueKey });
        } else {
          const existingSubmission = existing.submission || {};
          const nextSubmission = item.submission || {};
          const existingHasResult = Boolean(existingSubmission.score)
            || !['', 'unsubmitted'].includes(String(existingSubmission.status || '').toLowerCase());
          const nextHasResult = Boolean(nextSubmission.score)
            || !['', 'unsubmitted'].includes(String(nextSubmission.status || '').toLowerCase());
          uniqueHomework.set(uniqueKey, {
            ...existing,
            ...item,
            id: existing.id,
            studentRowKey: uniqueKey,
            submission: nextHasResult || !existingHasResult ? nextSubmission : existingSubmission
          });
        }
        return item;
      });
    return Array.from(uniqueHomework.values()).sort(compareAssignmentsAlphabetically);
  }, [isStudentUser, classes, homework, normalizedUserEmail, studentMemberships]);
  const studentAudienceKeys = useMemo(() => new Set(studentMemberships.map((membership) => `class:${membership.classId}`)), [studentMemberships]);
  const studentVisibleNotes = useMemo(() => {
    if (!isStudentUser) return [];
    return sortedNotes.filter((note) => note.audiences.includes('all-students') || note.audiences.some((audience) => studentAudienceKeys.has(audience)));
  }, [isStudentUser, sortedNotes, studentAudienceKeys]);
  const studentPlannerLessons = useMemo(() => {
    if (!isStudentUser) return [];
    return [...studentPlannerEntries].sort((a, b) => {
      const dateDiff = (a.date || '').localeCompare(b.date || '');
      if (dateDiff !== 0) return dateDiff;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [isStudentUser, studentPlannerEntries]);
  const studentPlannerEventsByDate = useMemo(() => {
    if (!isStudentUser) return new Map();
    const grouped = new Map();
    studentPlannerEvents
      .slice()
      .sort((a, b) => {
        const timeDiff = getTimeSortValue(a.time || '') - getTimeSortValue(b.time || '');
        if (timeDiff !== 0) return timeDiff;
        return (a.title || '').localeCompare(b.title || '');
      })
      .forEach((eventItem) => {
        const items = grouped.get(eventItem.date) || [];
        items.push(eventItem);
        grouped.set(eventItem.date, items);
      });
    return grouped;
  }, [isStudentUser, studentPlannerEvents]);
  const studentPlannerTodosByDate = useMemo(() => {
    if (!isStudentUser) return new Map();
    const grouped = new Map();
    lessonTodos
      .slice()
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      .forEach((todo) => {
        const items = grouped.get(todo.date) || [];
        items.push(todo);
        grouped.set(todo.date, items);
      });
    return grouped;
  }, [isStudentUser, lessonTodos]);

  const forumClassOptions = useMemo(() => {
    if (!isStudentUser) {
      return classes.map((cls) => ({
        id: cls.id,
        teacherId: user?.uid || '',
        name: cls.name
      }));
    }

    return classes.map((cls) => ({
      id: cls.id,
      teacherId: cls.teacherId || '',
      name: cls.name
    }));
  }, [classes, isStudentUser, user?.uid]);

  const accessibleForumRooms = useMemo(() => {
    if (!isStudentUser) {
      return [...forumRooms].sort((a, b) => Number(b.updatedAt || b.createdAt || 0) - Number(a.updatedAt || a.createdAt || 0));
    }

    const membershipMap = new Map();
    studentMemberships.forEach((membership) => {
      if (!membership?.teacherId || !membership?.classId) return;
      const key = membershipMap.get(membership.teacherId) || new Set();
      key.add(membership.classId);
      membershipMap.set(membership.teacherId, key);
    });

    return forumRooms
      .filter((room) => {
        const allowedClassIds = Array.isArray(room.allowedClassIds) ? room.allowedClassIds : [];
        const allowedEmails = Array.isArray(room.allowedEmails) ? room.allowedEmails : [];
        if (allowedClassIds.length === 0 && allowedEmails.length === 0) return true;
        if (allowedEmails.includes(normalizedUserEmail)) return true;
        const classIdsForTeacher = membershipMap.get(room.teacherId) || new Set();
        return allowedClassIds.some((classId) => classIdsForTeacher.has(classId));
      })
      .sort((a, b) => Number(b.updatedAt || b.createdAt || 0) - Number(a.updatedAt || a.createdAt || 0));
  }, [forumRooms, isStudentUser, normalizedUserEmail, studentMemberships]);

  const selectedForumRoom = useMemo(
    () => accessibleForumRooms.find((room) => room.roomKey === selectedForumRoomId) || null,
    [accessibleForumRooms, selectedForumRoomId]
  );

  const selectedForumPosts = useMemo(
    () => forumPostsByRoom[selectedForumRoom?.roomKey || ''] || [],
    [forumPostsByRoom, selectedForumRoom?.roomKey]
  );

  const forumRepliesByParent = useMemo(() => {
    const grouped = new Map();
    selectedForumPosts.forEach((post) => {
      if (!post.parentId) return;
      const items = grouped.get(post.parentId) || [];
      items.push(post);
      grouped.set(post.parentId, items);
    });
    grouped.forEach((items) => items.sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0)));
    return grouped;
  }, [selectedForumPosts]);

  const forumThreadRoots = useMemo(
    () => selectedForumPosts
      .filter((post) => !post.parentId)
      .sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0)),
    [selectedForumPosts]
  );

  const forumPostsByRootId = useMemo(() => {
    const grouped = new Map();
    selectedForumPosts.forEach((post) => {
      const rootKey = post.rootId || post.id;
      const items = grouped.get(rootKey) || [];
      items.push(post);
      grouped.set(rootKey, items);
    });
    grouped.forEach((items) => items.sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0)));
    return grouped;
  }, [selectedForumPosts]);

  const forumRootPostById = useMemo(() => {
    const byId = new Map();
    forumThreadRoots.forEach((post) => {
      byId.set(post.id, post);
    });
    return byId;
  }, [forumThreadRoots]);

  const forumClassNameById = useMemo(
    () => new Map(forumClassOptions.map((entry) => [`${entry.teacherId || ''}:${entry.id}`, entry.name])),
    [forumClassOptions]
  );

  useEffect(() => {
    if (accessibleForumRooms.length === 0) {
      setSelectedForumRoomId('');
      return;
    }

    const currentExists = accessibleForumRooms.some((room) => room.roomKey === selectedForumRoomId);
    if (!currentExists) {
      setSelectedForumRoomId(accessibleForumRooms[0].roomKey);
    }
  }, [accessibleForumRooms, selectedForumRoomId]);

  useEffect(() => {
    if (!isForumStandalone) return;
    setActiveTab('forum');
    if (forumStandaloneRoomKey) {
      setSelectedForumRoomId(forumStandaloneRoomKey);
    }
  }, [isForumStandalone, forumStandaloneRoomKey]);

  useEffect(() => {
    if (!isFirebaseEnabled || !user) return;

    if (canManageForumRooms) {
      const roomsRef = collection(db, 'teachers', user.uid, 'forumRooms');
      const unsub = onSnapshot(roomsRef, (snapshot) => {
        const nextRooms = [];
        snapshot.forEach((docItem) => {
          const roomData = docItem.data();
          nextRooms.push({
            roomKey: `${user.uid}__${docItem.id}`,
            roomId: docItem.id,
            teacherId: user.uid,
            allowedClassIds: Array.isArray(roomData.allowedClassIds) ? roomData.allowedClassIds : [],
            allowedEmails: Array.isArray(roomData.allowedEmails) ? roomData.allowedEmails.map((email) => String(email || '').trim().toLowerCase()).filter(Boolean) : [],
            ...roomData
          });
        });
        setForumRooms(nextRooms);
      }, (error) => console.error('Forum room sync error:', error));
      return () => unsub();
    }

    const teacherIds = Array.from(new Set(
      studentMemberships
        .map((membership) => membership.teacherId)
        .filter(Boolean)
    ));

    if (teacherIds.length === 0) {
      setForumRooms([]);
      return;
    }

    const roomsByKey = new Map();
    const unsubscribers = teacherIds.map((teacherId) => onSnapshot(
      collection(db, 'teachers', teacherId, 'forumRooms'),
      (snapshot) => {
        const prefix = `${teacherId}__`;
        Array.from(roomsByKey.keys()).forEach((key) => {
          if (key.startsWith(prefix)) roomsByKey.delete(key);
        });

        snapshot.forEach((docItem) => {
          const roomData = docItem.data();
          const roomKey = `${teacherId}__${docItem.id}`;
          roomsByKey.set(roomKey, {
            roomKey,
            roomId: docItem.id,
            teacherId,
            allowedClassIds: Array.isArray(roomData.allowedClassIds) ? roomData.allowedClassIds : [],
            allowedEmails: Array.isArray(roomData.allowedEmails) ? roomData.allowedEmails.map((email) => String(email || '').trim().toLowerCase()).filter(Boolean) : [],
            ...roomData
          });
        });

        setForumRooms(Array.from(roomsByKey.values()));
      },
      (error) => console.error('Student forum room sync error:', error)
    ));

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [canManageForumRooms, isFirebaseEnabled, user, studentMemberships]);

  useEffect(() => {
    if (!isFirebaseEnabled || !selectedForumRoom) return;

    const postsRef = collection(db, 'teachers', selectedForumRoom.teacherId, 'forumRooms', selectedForumRoom.roomId, 'posts');
    const unsub = onSnapshot(postsRef, (snapshot) => {
      const nextPosts = [];
      snapshot.forEach((docItem) => {
        const postData = docItem.data();
        nextPosts.push({
          id: docItem.id,
          parentId: postData.parentId || null,
          rootId: postData.rootId || docItem.id,
          ...postData
        });
      });

      nextPosts.sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0));
      setForumPostsByRoom((current) => ({ ...current, [selectedForumRoom.roomKey]: nextPosts }));
    }, (error) => console.error('Forum post sync error:', error));

    return () => unsub();
  }, [isFirebaseEnabled, selectedForumRoom]);

  useEffect(() => {
    setExpandedForumCards({});
    setForumExpandAll(false);
    setEditingForumPostId('');
    setForumPostEditDrafts({});
    setIsEditingRoomDescription(false);
    setRoomDescriptionDraft(selectedForumRoom?.description || '');
  }, [selectedForumRoomId]);

  useEffect(() => {
    setRoomDescriptionDraft(selectedForumRoom?.description || '');
  }, [selectedForumRoom?.roomKey, selectedForumRoom?.description]);

  useEffect(() => {
    if (!isFirebaseEnabled || !user || !isStudentUser) {
      setForumStudentAiUsageByThread({});
      return;
    }

    const usageRef = collection(db, 'studentProfiles', user.uid, 'forumAiUsage');
    const unsub = onSnapshot(usageRef, (snapshot) => {
      const nextUsage = {};
      snapshot.forEach((docItem) => {
        const usageData = docItem.data() || {};
        if (!usageData.threadId) return;
        const key = `${usageData.teacherId || ''}:${usageData.roomId || ''}:${usageData.threadId}`;
        nextUsage[key] = {
          id: docItem.id,
          ...usageData
        };
      });
      setForumStudentAiUsageByThread(nextUsage);
    }, (error) => console.error('Forum AI usage sync error:', error));

    return () => unsub();
  }, [isFirebaseEnabled, isStudentUser, user?.uid]);

  const handleResetForumRoomForm = () => {
    setEditingForumRoomId('');
    setForumRoomForm({ title: '', description: '', allowedClassIds: [], allowedEmails: '' });
  };

  const handleSaveForumRoom = async (e) => {
    e.preventDefault();
    if (!canManageForumRooms) {
      showToast('Only teacher accounts can create forum rooms.', 'error');
      return;
    }

    const title = forumRoomForm.title.trim();
    if (!title) {
      showToast('Room title is required.', 'error');
      return;
    }

    try {
      const teacherId = user.uid;
      const payload = {
        title,
        description: forumRoomForm.description.trim(),
        allowedClassIds: forumRoomForm.allowedClassIds,
        allowedEmails: normalizeForumEmails(forumRoomForm.allowedEmails),
        updatedAt: Date.now()
      };

      if (editingForumRoomId) {
        const targetRoom = forumRooms.find((room) => room.roomKey === editingForumRoomId);
        if (!targetRoom) return;
        await updateDoc(doc(db, 'teachers', teacherId, 'forumRooms', targetRoom.roomId), payload);
        showToast('Discussion room updated.');
      } else {
        const roomRef = doc(collection(db, 'teachers', teacherId, 'forumRooms'));
        await setDoc(roomRef, {
          ...payload,
          createdAt: Date.now(),
          createdBy: teacherId
        });
        setSelectedForumRoomId(`${teacherId}__${roomRef.id}`);
        showToast('Discussion room created.');
      }

      handleResetForumRoomForm();
    } catch (error) {
      showToast('Could not save discussion room.', 'error');
    }
  };

  const handleEditForumRoom = (room) => {
    if (!room || room.teacherId !== user?.uid) return;
    setEditingForumRoomId(room.roomKey);
    setForumRoomForm({
      title: room.title || '',
      description: room.description || '',
      allowedClassIds: Array.isArray(room.allowedClassIds) ? room.allowedClassIds : [],
      allowedEmails: (room.allowedEmails || []).join(', ')
    });
  };

  const handleDeleteForumRoom = async (room) => {
    if (!room || room.teacherId !== user?.uid) return;
    if (!window.confirm(`Delete room "${room.title}"?`)) return;

    try {
      await deleteDoc(doc(db, 'teachers', room.teacherId, 'forumRooms', room.roomId));
      setForumPostsByRoom((current) => {
        const next = { ...current };
        delete next[room.roomKey];
        return next;
      });
      if (selectedForumRoomId === room.roomKey) {
        setSelectedForumRoomId('');
      }
      showToast('Discussion room deleted.');
    } catch (error) {
      showToast('Could not delete discussion room.', 'error');
    }
  };

  const handleSaveSelectedRoomDescription = async () => {
    if (!selectedForumRoom || selectedForumRoom.teacherId !== user?.uid) return;

    try {
      await updateDoc(
        doc(db, 'teachers', selectedForumRoom.teacherId, 'forumRooms', selectedForumRoom.roomId),
        {
          description: roomDescriptionDraft.trim(),
          updatedAt: Date.now()
        }
      );
      setIsEditingRoomDescription(false);
      showToast('Room description updated.');
    } catch (error) {
      showToast('Could not update room description.', 'error');
    }
  };

  const handleCreateForumPost = async ({ parentId = null, rootId = null, body, imageUrl, externalUrl }) => {
    if (!selectedForumRoom || !user) return;

    const richBody = String(body || '').trim();
    const cleanedBody = getPlainTextFromRichHtml(richBody);
    if (!cleanedBody) {
      showToast('Post content is required.', 'error');
      return;
    }

    try {
      const postsCollectionRef = collection(db, 'teachers', selectedForumRoom.teacherId, 'forumRooms', selectedForumRoom.roomId, 'posts');
      const postRef = doc(postsCollectionRef);
      const now = Date.now();
      await setDoc(postRef, {
        body: cleanedBody,
        bodyHtml: richBody,
        imageUrl: String(imageUrl || '').trim(),
        externalUrl: String(externalUrl || '').trim(),
        parentId,
        rootId: rootId || postRef.id,
        authorId: user.uid,
        authorName: studentMemberships[0]?.studentName || user.displayName || user.email || 'User',
        authorEmail: normalizedUserEmail,
        authorRole: isStudentUser ? 'student' : 'teacher',
        createdAt: now,
        updatedAt: now
      });

      if (parentId) {
        setForumReplyDrafts((current) => {
          const next = { ...current };
          delete next[parentId];
          return next;
        });
      } else {
        setForumThreadForm({ body: '', imageUrl: '', externalUrl: '' });
      }
      showToast(parentId ? 'Reply posted.' : 'Thread posted.');
    } catch (error) {
      showToast('Could not publish post.', 'error');
    }
  };

  const handleToggleForumExpandAll = () => {
    setForumExpandAll((current) => {
      const nextValue = !current;
      if (!nextValue) {
        setExpandedForumCards({});
      } else {
        const nextExpanded = {};
        selectedForumPosts.forEach((post) => {
          nextExpanded[post.id] = true;
        });
        setExpandedForumCards(nextExpanded);
      }
      return nextValue;
    });
  };

  const openForumRoomInNewTab = (roomKey) => {
    if (!roomKey || typeof window === 'undefined') return;
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('forum', '1');
    nextUrl.searchParams.set('room', roomKey);
    window.open(nextUrl.toString(), '_blank', 'noopener,noreferrer');
  };

  const buildForumAiThreadFeedback = (threadRoot) => {
    const threadPosts = forumPostsByRootId.get(threadRoot.id) || [threadRoot];
    const combinedText = threadPosts
      .map((post) => post.bodyHtml ? getPlainTextFromRichHtml(post.bodyHtml) : String(post.body || ''))
      .join(' ');

    const positiveHits = countForumAiTermHits(combinedText, FORUM_AI_POSITIVE_TERMS);
    const negativeHits = countForumAiTermHits(combinedText, FORUM_AI_NEGATIVE_TERMS);
    const constructiveHits = countForumAiTermHits(combinedText, FORUM_AI_CONSTRUCTIVE_TERMS);
    const sentimentScore = clampForumAiScore(50 + ((positiveHits - negativeHits) * 8), 0, 100);

    const studentPosts = threadPosts.filter((post) => post.authorRole === 'student');
    const strengths = [];
    const concerns = [];
    const recommendations = [];

    if (constructiveHits > 0) {
      strengths.push('Students are giving reasons or examples, which improves discussion quality.');
    }
    if (threadPosts.length > 3) {
      strengths.push('The thread has active participation with multiple follow-up responses.');
    }
    if (positiveHits > negativeHits) {
      strengths.push('Tone is mostly constructive and supportive.');
    }

    if (negativeHits > positiveHits) {
      concerns.push('Tone may be drifting negative in parts of the discussion.');
      recommendations.push('Prompt students to reframe critiques with evidence and respectful language.');
    }
    if (studentPosts.length > 0 && constructiveHits === 0) {
      concerns.push('Many responses are short but lack reasoning.');
      recommendations.push('Ask students to add one sentence of evidence or explanation to each reply.');
    }
    if (threadPosts.length <= 2) {
      concerns.push('The thread is still light on interaction.');
      recommendations.push('Invite 2-3 students to respond directly to the original question.');
    }

    if (strengths.length === 0) strengths.push('This thread is clear and readable for participants.');
    if (concerns.length === 0) concerns.push('No major issues detected in this thread.');
    if (recommendations.length === 0) recommendations.push('Keep this discussion moving with one follow-up question from the teacher.');

    return {
      totalPosts: threadPosts.length,
      studentPosts: studentPosts.length,
      positiveHits,
      negativeHits,
      constructiveHits,
      sentimentScore,
      strengths,
      concerns,
      recommendations
    };
  };

  const buildForumAiRoomDashboard = () => {
    const threadInsights = forumThreadRoots.map((threadRoot) => {
      const feedback = buildForumAiThreadFeedback(threadRoot);
      return {
        id: threadRoot.id,
        title: getForumPreviewText(threadRoot.bodyHtml ? getPlainTextFromRichHtml(threadRoot.bodyHtml) : threadRoot.body, 45),
        sentimentScore: feedback.sentimentScore,
        totalPosts: feedback.totalPosts
      };
    });

    const sortedPositive = [...threadInsights]
      .sort((a, b) => b.sentimentScore - a.sentimentScore)
      .slice(0, 3);
    const sortedNegative = [...threadInsights]
      .sort((a, b) => a.sentimentScore - b.sentimentScore)
      .slice(0, 3);

    const studentBuckets = new Map();
    selectedForumPosts
      .filter((post) => post.authorRole === 'student')
      .forEach((post) => {
        const key = String(post.authorEmail || post.authorId || post.id);
        const entry = studentBuckets.get(key) || {
          name: post.authorName || 'Student',
          email: post.authorEmail || '',
          posts: 0,
          replies: 0,
          words: 0,
          constructiveHits: 0,
          negativeHits: 0
        };
        const rawText = post.bodyHtml ? getPlainTextFromRichHtml(post.bodyHtml) : String(post.body || '');
        entry.posts += 1;
        if (post.parentId) entry.replies += 1;
        entry.words += getForumAiWordCount(rawText);
        entry.constructiveHits += countForumAiTermHits(rawText, FORUM_AI_CONSTRUCTIVE_TERMS);
        entry.negativeHits += countForumAiTermHits(rawText, FORUM_AI_NEGATIVE_TERMS);
        studentBuckets.set(key, entry);
      });

    const studentQuality = Array.from(studentBuckets.values())
      .map((entry) => {
        const avgWords = entry.posts > 0 ? entry.words / entry.posts : 0;
        const constructiveRate = entry.posts > 0 ? entry.constructiveHits / entry.posts : 0;
        const replyRate = entry.posts > 0 ? entry.replies / entry.posts : 0;
        const negativityRate = entry.posts > 0 ? entry.negativeHits / entry.posts : 0;

        const qualityScore = clampForumAiScore(
          35 + (Math.min(avgWords, 70) * 0.45) + (constructiveRate * 20) + (replyRate * 20) - (negativityRate * 25)
        );

        return {
          ...entry,
          qualityScore,
          qualityBand: getForumAiQualityBand(qualityScore),
          avgWords: Math.round(avgWords)
        };
      })
      .sort((a, b) => b.qualityScore - a.qualityScore);

    return {
      totalThreads: forumThreadRoots.length,
      totalPosts: selectedForumPosts.length,
      positiveThreads: sortedPositive,
      negativeThreads: sortedNegative,
      studentQuality
    };
  };

  const requestRemoteForumAiInsights = async ({ mode, threadRoot = null, customPrompt = '' }) => {
    if (!firebaseFunctions || !selectedForumRoom) return null;

    const analyzeForumInsights = httpsCallable(firebaseFunctions, 'analyzeForumInsights');
    const threadPosts = threadRoot ? (forumPostsByRootId.get(threadRoot.id) || [threadRoot]) : [];
    const sourcePosts = mode === 'dashboard' ? selectedForumPosts : threadPosts;
    const postsPayload = sourcePosts.slice(0, 120).map((post) => ({
      id: post.id,
      rootId: post.rootId || post.id,
      parentId: post.parentId || null,
      authorName: post.authorName || 'User',
      authorRole: post.authorRole || 'participant',
      body: post.bodyHtml ? getPlainTextFromRichHtml(post.bodyHtml) : (post.body || ''),
      createdAt: Number(post.createdAt || 0)
    }));

    const result = await analyzeForumInsights({
      mode,
      roomTitle: selectedForumRoom.title || 'Forum Room',
      threadPreview: threadRoot ? getForumPreviewText(threadRoot.bodyHtml ? getPlainTextFromRichHtml(threadRoot.bodyHtml) : threadRoot.body, 120) : '',
      posts: postsPayload,
      customPrompt: String(customPrompt || '').slice(0, 600)
    });

    return result?.data || null;
  };

  const persistThreadAiAnalysis = async ({ threadRoot, analysis, customPrompt = '', model = '' }) => {
    if (!threadRoot || !selectedForumRoom) return;
    try {
      await updateDoc(
        doc(db, 'teachers', selectedForumRoom.teacherId, 'forumRooms', selectedForumRoom.roomId, 'posts', threadRoot.id),
        {
          aiThreadAnalysis: {
            threadSummary: analysis.threadSummary || '',
            sentimentScore: Number.isFinite(Number(analysis.sentimentScore)) ? Number(analysis.sentimentScore) : 50,
            strengths: Array.isArray(analysis.strengths) ? analysis.strengths.slice(0, 6) : [],
            concerns: Array.isArray(analysis.concerns) ? analysis.concerns.slice(0, 6) : [],
            recommendations: Array.isArray(analysis.recommendations) ? analysis.recommendations.slice(0, 6) : [],
            customPrompt: String(customPrompt || '').trim(),
            model: String(model || ''),
            generatedAt: Date.now()
          },
          updatedAt: Date.now()
        }
      );
    } catch {
      // Cache persistence is best-effort and should not block the user flow.
    }
  };

  const openForumAiThreadFeedback = async (threadRoot, customPrompt = '', forceRefresh = false) => {
    if (!threadRoot) return;

    const normalizedPrompt = String(customPrompt || '').trim();
    const liveThreadRoot = forumRootPostById.get(threadRoot.id) || threadRoot;
    const cachedAnalysis = liveThreadRoot?.aiThreadAnalysis && typeof liveThreadRoot.aiThreadAnalysis === 'object'
      ? liveThreadRoot.aiThreadAnalysis
      : null;

    const localData = buildForumAiThreadFeedback(threadRoot);
    const cachedData = cachedAnalysis
      ? {
          ...localData,
          threadSummary: cachedAnalysis.threadSummary || localData.threadSummary || '',
          sentimentScore: Number.isFinite(Number(cachedAnalysis.sentimentScore)) ? Number(cachedAnalysis.sentimentScore) : localData.sentimentScore,
          strengths: Array.isArray(cachedAnalysis.strengths) && cachedAnalysis.strengths.length > 0 ? cachedAnalysis.strengths : localData.strengths,
          concerns: Array.isArray(cachedAnalysis.concerns) && cachedAnalysis.concerns.length > 0 ? cachedAnalysis.concerns : localData.concerns,
          recommendations: Array.isArray(cachedAnalysis.recommendations) && cachedAnalysis.recommendations.length > 0 ? cachedAnalysis.recommendations : localData.recommendations,
          customPrompt: String(cachedAnalysis.customPrompt || ''),
          insightSource: cachedAnalysis.model ? `AI model (${cachedAnalysis.model})` : 'AI model'
        }
      : null;

    setForumAiModal({
      isOpen: true,
      mode: 'thread',
      title: 'AI Thread Feedback',
      data: {
        ...(cachedData || localData),
        threadPreview: getForumPreviewText(threadRoot.bodyHtml ? getPlainTextFromRichHtml(threadRoot.bodyHtml) : threadRoot.body, 85),
        customPrompt: normalizedPrompt || cachedData?.customPrompt || '',
        insightSource: cachedData?.insightSource || 'Local analysis'
      },
      generatedAt: Number(cachedAnalysis?.generatedAt || Date.now())
    });

    if (cachedData && !forceRefresh && !normalizedPrompt) {
      return;
    }

    setForumAiLoading(true);
    try {
      const remote = await requestRemoteForumAiInsights({ mode: 'thread', threadRoot, customPrompt: normalizedPrompt });
      if (remote?.source === 'gemini' && remote.analysis) {
        const analysis = remote.analysis;
        const mergedData = {
          ...localData,
          threadSummary: analysis.threadSummary || localData.threadSummary || '',
          sentimentScore: Number.isFinite(Number(analysis.sentimentScore)) ? Number(analysis.sentimentScore) : localData.sentimentScore,
          strengths: Array.isArray(analysis.strengths) && analysis.strengths.length > 0 ? analysis.strengths : localData.strengths,
          concerns: Array.isArray(analysis.concerns) && analysis.concerns.length > 0 ? analysis.concerns : localData.concerns,
          recommendations: Array.isArray(analysis.recommendations) && analysis.recommendations.length > 0 ? analysis.recommendations : localData.recommendations,
          customPrompt: normalizedPrompt,
          insightSource: remote.model ? `AI model (${remote.model})` : 'AI model',
          threadPreview: getForumPreviewText(threadRoot.bodyHtml ? getPlainTextFromRichHtml(threadRoot.bodyHtml) : threadRoot.body, 85)
        };

        setForumAiModal((current) => ({
          ...current,
          data: mergedData,
          generatedAt: Date.now()
        }));

        await persistThreadAiAnalysis({
          threadRoot,
          analysis: mergedData,
          customPrompt: normalizedPrompt,
          model: String(remote.model || '')
        });
      } else if (remote?.source === 'fallback' && remote?.message) {
        setForumAiModal((current) => ({
          ...current,
          data: {
            ...current.data,
            insightSource: `Local analysis (${remote.message})`
          }
        }));
      }
    } catch (error) {
      setForumAiModal((current) => ({
        ...current,
        data: {
          ...current.data,
          insightSource: 'Local analysis (AI call failed)'
        }
      }));
    } finally {
      setForumAiLoading(false);
    }
  };

  const openForumAiThreadPrompt = (threadRoot) => {
    if (!threadRoot) return;
    setForumAiPromptModal({
      isOpen: true,
      threadRoot,
      customPrompt: ''
    });
  };

  const closeForumAiThreadPrompt = () => {
    setForumAiPromptModal({
      isOpen: false,
      threadRoot: null,
      customPrompt: ''
    });
  };

  const submitForumAiThreadPrompt = async () => {
    const threadRoot = forumAiPromptModal.threadRoot;
    const customPrompt = String(forumAiPromptModal.customPrompt || '').trim();
    closeForumAiThreadPrompt();
    await openForumAiThreadFeedback(threadRoot, customPrompt, true);
  };

  const hasStudentUsedThreadAiFeedback = (threadRoot) => {
    if (!threadRoot || !user?.uid) return false;
    const usageKey = `${selectedForumRoom?.teacherId || ''}:${selectedForumRoom?.roomId || ''}:${threadRoot.id}`;
    return Boolean(forumStudentAiUsageByThread[usageKey]);
  };

  const handleStudentAiFeedbackPost = async (threadRoot) => {
    if (!isStudentUser || !selectedForumRoom || !user?.uid || !threadRoot) return;

    if (hasStudentUsedThreadAiFeedback(threadRoot)) {
      showToast('You have already used AI feedback in this thread.', 'error');
      return;
    }

    setForumStudentAiLoadingRootId(threadRoot.id);
    try {
      const remote = await requestRemoteForumAiInsights({ mode: 'student_feedback', threadRoot });
      const feedbackPost = String(remote?.analysis?.feedbackPost || '').trim();
      if (!feedbackPost) {
        showToast('AI feedback is currently unavailable for this thread.', 'error');
        return;
      }

      const rootPostRef = doc(
        db,
        'teachers',
        selectedForumRoom.teacherId,
        'forumRooms',
        selectedForumRoom.roomId,
        'posts',
        threadRoot.id
      );
      const usageKey = `${selectedForumRoom.teacherId}:${selectedForumRoom.roomId}:${threadRoot.id}`;
      const usageRef = doc(db, 'studentProfiles', user.uid, 'forumAiUsage', usageKey);
      const postsCollectionRef = collection(db, 'teachers', selectedForumRoom.teacherId, 'forumRooms', selectedForumRoom.roomId, 'posts');

      await runTransaction(db, async (transaction) => {
        const rootSnapshot = await transaction.get(rootPostRef);
        if (!rootSnapshot.exists()) {
          throw new Error('Thread not found.');
        }

        const usageSnapshot = await transaction.get(usageRef);
        if (usageSnapshot.exists()) {
          throw new Error('AI feedback already used in this thread.');
        }

        const aiPostRef = doc(postsCollectionRef);
        const now = Date.now();

        transaction.set(usageRef, {
          teacherId: selectedForumRoom.teacherId,
          roomId: selectedForumRoom.roomId,
          threadId: threadRoot.id,
          threadTitle: threadRoot.bodyHtml ? getPlainTextFromRichHtml(threadRoot.bodyHtml).slice(0, 120) : String(threadRoot.body || '').slice(0, 120),
          usedAt: now,
          updatedAt: now
        });

        transaction.set(aiPostRef, {
          body: feedbackPost,
          bodyHtml: '',
          imageUrl: '',
          externalUrl: '',
          parentId: threadRoot.id,
          rootId: threadRoot.id,
          authorId: 'ai-feedback-assistant',
          authorName: 'AI Feedback Assistant',
          authorEmail: '',
          authorRole: 'ai',
          isAiGenerated: true,
          aiPurpose: 'student-thread-feedback',
          triggeredByUserId: user.uid,
          createdAt: now,
          updatedAt: now
        });
      });

      showToast('AI feedback posted to this thread.');
    } catch (error) {
      const message = String(error?.message || 'Could not post AI feedback.');
      if (message.toLowerCase().includes('already used')) {
        showToast('You can use AI feedback only once per thread.', 'error');
      } else {
        showToast('Could not post AI feedback.', 'error');
      }
    } finally {
      setForumStudentAiLoadingRootId('');
    }
  };

  const openForumAiDashboard = async () => {
    if (!selectedForumRoom) {
      showToast('Choose a room first to open AI dashboard.', 'error');
      return;
    }

    const data = buildForumAiRoomDashboard();
    setForumAiModal({
      isOpen: true,
      mode: 'dashboard',
      title: `AI Room Dashboard · ${selectedForumRoom.title}`,
      data: {
        ...data,
        insightSource: 'Local analysis'
      },
      generatedAt: Date.now()
    });

    setForumAiLoading(true);
    try {
      const remote = await requestRemoteForumAiInsights({ mode: 'dashboard' });
      if (remote?.source === 'gemini' && remote.analysis) {
        const analysis = remote.analysis;
        const remoteStudents = Array.isArray(analysis.studentQuality) ? analysis.studentQuality : [];
        setForumAiModal((current) => ({
          ...current,
          data: {
            ...current.data,
            roomSummary: analysis.roomSummary || current.data.roomSummary || '',
            positiveThreads: Array.isArray(analysis.positiveThreads)
              ? analysis.positiveThreads.map((entry, index) => ({
                id: `ai-pos-${index}`,
                title: String(entry?.title || 'Discussion'),
                totalPosts: 0,
                sentimentScore: 0,
                reason: String(entry?.reason || '')
              }))
              : current.data.positiveThreads,
            negativeThreads: Array.isArray(analysis.negativeThreads)
              ? analysis.negativeThreads.map((entry, index) => ({
                id: `ai-neg-${index}`,
                title: String(entry?.title || 'Discussion'),
                totalPosts: 0,
                sentimentScore: 0,
                reason: String(entry?.reason || '')
              }))
              : current.data.negativeThreads,
            studentQuality: remoteStudents.length > 0
              ? remoteStudents.map((entry) => ({
                name: String(entry?.name || 'Student'),
                email: '',
                posts: 0,
                replies: 0,
                avgWords: 0,
                qualityScore: Number.isFinite(Number(entry?.score)) ? Number(entry.score) : 0,
                qualityBand: String(entry?.qualityBand || 'Developing'),
                aiNote: String(entry?.note || '')
              }))
              : current.data.studentQuality,
            insightSource: 'AI model'
          },
          generatedAt: Date.now()
        }));
      } else if (remote?.source === 'fallback' && remote?.message) {
        setForumAiModal((current) => ({
          ...current,
          data: {
            ...current.data,
            insightSource: `Local analysis (${remote.message})`
          }
        }));
      }
    } catch {
      setForumAiModal((current) => ({
        ...current,
        data: {
          ...current.data,
          insightSource: 'Local analysis (AI call failed)'
        }
      }));
    } finally {
      setForumAiLoading(false);
    }
  };

  const closeForumAiModal = () => {
    setForumAiModal({ isOpen: false, mode: 'thread', title: '', data: null, generatedAt: 0 });
  };

  const focusForumEditor = (editor) => {
    if (!editor) return;
    editor.focus();
  };

  const runForumEditorCommand = (editor, command, value = null) => {
    if (!editor || typeof document === 'undefined') return;
    focusForumEditor(editor);
    document.execCommand(command, false, value);
  };

  const openForumLinkModal = (editor, onEditorChange) => {
    if (!editor) return;
    const selectionText = typeof window !== 'undefined' ? (window.getSelection()?.toString() || '').trim() : '';
    setForumLinkModal({
      isOpen: true,
      url: '',
      text: selectionText,
      editorElement: editor,
      onEditorChange
    });
  };

  const closeForumLinkModal = () => {
    setForumLinkModal({ isOpen: false, url: '', text: '', editorElement: null, onEditorChange: null });
  };

  const handleInsertForumLinkFromModal = () => {
    const editor = forumLinkModal.editorElement;
    const normalizedUrl = String(forumLinkModal.url || '').trim();
    const linkText = String(forumLinkModal.text || '').trim() || normalizedUrl;
    if (!editor) {
      closeForumLinkModal();
      return;
    }
    if (!/^https?:\/\//i.test(normalizedUrl)) {
      showToast('Use a valid URL starting with http:// or https://', 'error');
      return;
    }

    runForumEditorCommand(editor, 'insertHTML', `<a href="${normalizedUrl}" target="_blank" rel="noreferrer">${linkText}</a>`);
    if (typeof forumLinkModal.onEditorChange === 'function') {
      forumLinkModal.onEditorChange(editor.innerHTML);
    }
    closeForumLinkModal();
  };

  const renderForumLinkModal = () => {
    if (!forumLinkModal.isOpen) return null;

    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) closeForumLinkModal(); }}>
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-2xl">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-800">Insert Link</p>
            <button type="button" onClick={closeForumLinkModal} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close link modal">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 space-y-3">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">URL</label>
              <input
                type="url"
                value={forumLinkModal.url}
                onChange={(e) => setForumLinkModal((current) => ({ ...current, url: e.target.value }))}
                placeholder="https://example.com"
                className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Display text (optional)</label>
              <input
                type="text"
                value={forumLinkModal.text}
                onChange={(e) => setForumLinkModal((current) => ({ ...current, text: e.target.value }))}
                placeholder="Link text"
                className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeForumLinkModal} className="rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={handleInsertForumLinkFromModal} className="rounded bg-slate-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800">Insert</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderForumAiModal = () => {
    if (!forumAiModal.isOpen || !forumAiModal.data) return null;

    return (
      <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) closeForumAiModal(); }}>
        <div className="w-full max-w-3xl max-h-[84vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-2xl">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">{forumAiModal.title}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">Generated {new Date(forumAiModal.generatedAt).toLocaleString()}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                {forumAiLoading ? 'Refreshing with AI model...' : (forumAiModal.data.insightSource || 'Local analysis')}
              </p>
            </div>
            <button type="button" onClick={closeForumAiModal} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close AI modal">
              <X className="w-4 h-4" />
            </button>
          </div>

          {forumAiModal.mode === 'thread' ? (
            <div className="mt-4 space-y-4">
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Thread snapshot</p>
                <p className="mt-1 text-xs text-slate-700">{forumAiModal.data.threadPreview}</p>
                {forumAiModal.data.customPrompt ? (
                  <p className="mt-2 text-[11px] text-slate-600">
                    Teacher prompt: <span className="font-medium">{forumAiModal.data.customPrompt}</span>
                  </p>
                ) : null}
                {forumAiModal.data.threadSummary ? (
                  <p className="mt-2 text-xs text-slate-600">{forumAiModal.data.threadSummary}</p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-wider">
                  <span className="rounded bg-white px-2 py-1 border border-slate-200 text-slate-600">Posts: {forumAiModal.data.totalPosts}</span>
                  <span className="rounded bg-white px-2 py-1 border border-slate-200 text-slate-600">Student replies: {forumAiModal.data.studentPosts}</span>
                  <span className="rounded bg-white px-2 py-1 border border-slate-200 text-slate-600">Thread score: {forumAiModal.data.sentimentScore}/100</span>
                </div>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">What is going well</p>
                <ul className="mt-1 list-disc pl-5 space-y-1 text-xs text-slate-700">
                  {forumAiModal.data.strengths.map((item, index) => <li key={`strength-${index}`}>{item}</li>)}
                </ul>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Watch outs</p>
                <ul className="mt-1 list-disc pl-5 space-y-1 text-xs text-slate-700">
                  {forumAiModal.data.concerns.map((item, index) => <li key={`concern-${index}`}>{item}</li>)}
                </ul>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Suggested teacher prompts</p>
                <ul className="mt-1 list-disc pl-5 space-y-1 text-xs text-slate-700">
                  {forumAiModal.data.recommendations.map((item, index) => <li key={`recommend-${index}`}>{item}</li>)}
                </ul>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              {forumAiModal.data.roomSummary ? (
                <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                  {forumAiModal.data.roomSummary}
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Threads</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{forumAiModal.data.totalThreads}</p>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Posts</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{forumAiModal.data.totalPosts}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Positive discussions</p>
                  <div className="mt-2 space-y-2">
                    {forumAiModal.data.positiveThreads.length === 0 ? (
                      <p className="text-xs text-emerald-700">No threads yet.</p>
                    ) : forumAiModal.data.positiveThreads.map((thread) => (
                      <div key={`pos-${thread.id}`} className="rounded border border-emerald-200 bg-white px-2 py-1.5 text-xs text-slate-700">
                        <p className="font-semibold">{thread.title}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-emerald-700">Score {thread.sentimentScore}/100 · {thread.totalPosts} posts</p>
                        {thread.reason ? <p className="mt-1 text-[11px] text-slate-600 normal-case tracking-normal">{thread.reason}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded border border-rose-200 bg-rose-50 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Negative discussions</p>
                  <div className="mt-2 space-y-2">
                    {forumAiModal.data.negativeThreads.length === 0 ? (
                      <p className="text-xs text-rose-700">No threads yet.</p>
                    ) : forumAiModal.data.negativeThreads.map((thread) => (
                      <div key={`neg-${thread.id}`} className="rounded border border-rose-200 bg-white px-2 py-1.5 text-xs text-slate-700">
                        <p className="font-semibold">{thread.title}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-rose-700">Score {thread.sentimentScore}/100 · {thread.totalPosts} posts</p>
                        {thread.reason ? <p className="mt-1 text-[11px] text-slate-600 normal-case tracking-normal">{thread.reason}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded border border-slate-200 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Student response quality</p>
                {forumAiModal.data.studentQuality.length === 0 ? (
                  <p className="mt-2 text-xs text-slate-500">No student responses yet.</p>
                ) : (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <th className="py-1.5 pr-2">Student</th>
                          <th className="py-1.5 pr-2">Quality</th>
                          <th className="py-1.5 pr-2">Posts</th>
                          <th className="py-1.5 pr-2">Avg words</th>
                          <th className="py-1.5">Replies</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forumAiModal.data.studentQuality.map((row) => (
                          <tr key={`${row.email}-${row.name}`} className="border-b border-slate-100 text-slate-700">
                            <td className="py-2 pr-2">
                              <p className="font-semibold">{row.name}</p>
                              <p className="text-[10px] text-slate-500">{row.email || 'No email'}</p>
                            </td>
                            <td className="py-2 pr-2">
                              <p className="font-semibold">{Math.round(row.qualityScore)}/100</p>
                              <p className="text-[10px] text-slate-500">{row.qualityBand}</p>
                              {row.aiNote ? <p className="text-[10px] text-slate-500 mt-0.5">{row.aiNote}</p> : null}
                            </td>
                            <td className="py-2 pr-2">{row.posts}</td>
                            <td className="py-2 pr-2">{row.avgWords}</td>
                            <td className="py-2">{row.replies}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {selectedLesson ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm" onClick={(event) => { if (event.target === event.currentTarget) setSelectedLesson(null); }}>
                  <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-950" />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Lesson Details</h4>
                      </div>
                      <button type="button" onClick={() => setSelectedLesson(null)} className="text-slate-400 hover:text-slate-900"><X className="h-4 w-4" /></button>
                    </div>
                    <div className="space-y-4 overflow-y-auto p-6">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-800">{selectedLesson.className || selectedLesson.subject || 'Unassigned'}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">{formatDateLabel(selectedLesson.date)}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">{selectedLesson.startTime || '--:--'} - {selectedLesson.endTime || '--:--'}</span>
                      </div>
                      <div>
                        <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Topic</p>
                        <p className="text-sm font-bold text-slate-900">{getLessonTopicLabel(selectedLesson.topic)}</p>
                      </div>
                      {selectedLesson.objectives ? (
                        <div>
                          <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Objectives</p>
                          <div className="text-xs text-slate-700">{renderMarkdown(selectedLesson.objectives)}</div>
                        </div>
                      ) : null}
                      {!selectedLesson.isDashboardEventPreview ? (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Lesson recording</p>
                              <p className="mt-0.5 text-[10px] text-slate-500">Record, transcribe, and create an AI recap.</p>
                            </div>
                            <div className="flex shrink-0 flex-col gap-2">
                              {renderLessonRecordingButton(selectedLesson)}
                              {Array.isArray(selectedLesson.transcriptChunks) && selectedLesson.transcriptChunks.length ? (
                                <button type="button" onClick={() => setShowLessonTranscript((current) => !current)} className="rounded border border-slate-200 bg-white px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-600 hover:border-slate-400 hover:text-slate-900">
                                  {showLessonTranscript ? 'Hide transcript' : 'Show transcript'}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {selectedLesson.aiSummary?.overview ? (
                        <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-800">AI lesson recap</p>
                          <p className="mt-2 text-xs leading-relaxed text-slate-700">{selectedLesson.aiSummary.overview}</p>
                        </div>
                      ) : null}
                      {showLessonTranscript && Array.isArray(selectedLesson.transcriptChunks) ? (
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Transcript · {selectedLesson.transcriptChunks.length} intervals</p>
                          <div className="mt-3 max-h-64 space-y-3 overflow-y-auto">
                            {selectedLesson.transcriptChunks.map((chunk) => (
                              <div key={chunk.index} className="border-l-2 border-slate-200 pl-3">
                                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{formatRecordingTime(chunk.startMs)}–{formatRecordingTime(chunk.endMs)}</p>
                                <p className="mt-1 whitespace-pre-wrap text-xs text-slate-700">{chunk.text}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <button type="button" onClick={() => setActiveTab('lessons')} className="w-full rounded border border-slate-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50">
                        Open full lesson planner
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderForumAiThreadPromptModal = () => {
    if (!forumAiPromptModal.isOpen || !forumAiPromptModal.threadRoot) return null;

    return (
      <div className="fixed inset-0 z-[74] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) closeForumAiThreadPrompt(); }}>
        <div className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-4 shadow-2xl">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-900">AI Thread Prompt</p>
            <button type="button" onClick={closeForumAiThreadPrompt} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close AI thread prompt modal">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 space-y-3">
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              {getForumPreviewText(
                forumAiPromptModal.threadRoot.bodyHtml
                  ? getPlainTextFromRichHtml(forumAiPromptModal.threadRoot.bodyHtml)
                  : forumAiPromptModal.threadRoot.body,
                140
              )}
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Custom prompt (optional)</label>
              <textarea
                rows="4"
                value={forumAiPromptModal.customPrompt}
                onChange={(e) => setForumAiPromptModal((current) => ({ ...current, customPrompt: e.target.value.slice(0, 600) }))}
                placeholder="Example: Focus on misconceptions in Newton's laws and suggest two next teacher replies."
                className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none"
              />
              <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">{forumAiPromptModal.customPrompt.length}/600</p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeForumAiThreadPrompt} className="rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={submitForumAiThreadPrompt} className="rounded bg-slate-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800">Generate</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderJournalContent = (bodyHtml, body) => {
    if (String(bodyHtml || '').trim()) {
      return <div className="forum-rich-content whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: injectInlineLatexIntoRichHtml(bodyHtml) }} />;
    }

    if (String(body || '').trim()) {
      return <JournalMarkdown>{body}</JournalMarkdown>;
    }

    return <p className="text-sm text-slate-400">Start writing to preview your journal entry.</p>;
  };

  const handlePrintOpenJournal = () => {
    const content = document.createElement('div');
    content.className = 'space-y-3';
    const bodyHtml = journalEditorMode === 'richtext'
      ? journalEditorRef.current?.innerHTML ?? journalForm.bodyHtml
      : '';
    content.innerHTML = renderToStaticMarkup(<>
      {journalForm.transcriptChunks?.length > 0 && <section>
        <h2>Google transcript</h2>
        {journalForm.transcriptChunks.map((chunk, index) => <div key={index}>
          <p>{formatRecordingTime(chunk.startMs)}–{formatRecordingTime(chunk.endMs)}</p>
          <p className="whitespace-pre-wrap">{chunk.text}</p>
        </div>)}
      </section>}
      {journalForm.journalAiSummary && <section>
        <h2>AI summary</h2>
        <p className="whitespace-pre-wrap">{journalForm.journalAiSummary}</p>
      </section>}
      {renderJournalContent(bodyHtml, journalForm.body)}
    </>);
    printJournal({ content, title: journalForm.title, date: journalForm.date, tags: normalizeJournalTags(journalForm.tags) })
      .catch((error) => showToast(error.message || 'Could not print journal entry.', 'error'));
  };

  const renderJournalEditorModal = () => {
    if (!isJournalEditorOpen) return null;

    return (
      <div className="fixed inset-0 z-[76] bg-slate-950/70 backdrop-blur-sm">
        <div className="flex h-full flex-col bg-white">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">{selectedJournalId ? 'Edit Journal Entry' : 'New Journal Entry'}</p>
              <p className="mt-1 text-[11px] text-slate-400">Full-page WYSIWYG editor for teacher reflections.</p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={handlePrintOpenJournal}
                className="rounded border border-slate-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
              >
                Print / Save PDF
              </button>
              <button
                type="button"
                onClick={() => setIsJournalEditorOpen(false)}
                className="rounded border border-slate-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleSaveJournal}
                className="rounded bg-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800"
              >
                Save Journal
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col px-6 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-slate-500">Title</label>
                  <input
                    type="text"
                    value={journalForm.title}
                    onChange={(e) => setJournalForm((current) => ({ ...current, title: e.target.value }))}
                    placeholder="Lesson reflections, observations, follow-ups"
                    className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-slate-400"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-slate-500">Date</label>
                  <input
                    type="date"
                    value={journalForm.date}
                    onChange={(e) => setJournalForm((current) => ({ ...current, date: e.target.value }))}
                    className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-slate-400"
                    required
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-slate-500">Tags</label>
                <input
                  type="text"
                  value={journalForm.tags}
                  onChange={(e) => setJournalForm((current) => ({ ...current, tags: e.target.value }))}
                  placeholder="behavior, lab-day, parent-followup"
                  className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-slate-400"
                />
              </div>

              {Array.isArray(journalForm.transcriptChunks) && journalForm.transcriptChunks.length ? (
                <details open className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
                  <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-wider text-slate-600">
                    Google transcript · {journalForm.transcriptChunks.length} intervals
                  </summary>
                  <div className="mt-3 max-h-48 space-y-3 overflow-y-auto pr-1">
                    {journalForm.transcriptChunks.map((chunk) => (
                      <div key={`journal-transcript-${chunk.index}`} className="border-l-2 border-slate-300 pl-3">
                        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                          {formatRecordingTime(chunk.startMs)}–{formatRecordingTime(chunk.endMs)}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">{chunk.text}</p>
                      </div>
                    ))}
                  </div>
                </details>
              ) : null}

              {journalForm.journalAiSummary ? (
                <div className="mt-3 rounded border border-indigo-200 bg-indigo-50 p-3">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-indigo-700">AI summary</p>
                  <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">{journalForm.journalAiSummary}</p>
                </div>
              ) : null}

              <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto pr-1">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500">Journal Entry</label>
                  <div className="flex rounded border border-slate-200 bg-slate-50 p-0.5" aria-label="Journal editor mode">
                    {['markdown', 'richtext'].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => {
                          if (mode === journalEditorMode) return;
                          setJournalEditorMode(mode);
                          setJournalForm((current) => mode === 'markdown'
                            ? { ...current, body: convertJournalRichHtmlToMarkdown(current.bodyHtml), bodyHtml: '' }
                            : { ...current, bodyHtml: convertJournalMarkdownToRichHtml(current.body) });
                        }}
                        className={`rounded px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${journalEditorMode === mode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                      >
                        {mode === 'markdown' ? 'Markdown' : 'Rich text'}
                      </button>
                    ))}
                  </div>
                </div>
                {journalEditorMode === 'richtext' ? (
                  <div className="sticky top-0 z-20 bg-white/95 py-1 backdrop-blur-sm">
                    {renderForumFormattingToolbar({
                      editorRef: journalEditorRef,
                      onChange: (nextBodyHtml) => setJournalForm((current) => ({
                        ...current,
                        bodyHtml: nextBodyHtml,
                        body: getPlainTextFromRichHtml(nextBodyHtml)
                      })),
                      showImageButton: false,
                      allowTabHierarchy: true
                    })}
                  </div>
                ) : (
                  <p className="mb-2 text-[10px] text-slate-400">Supports headings, lists, **bold**, *italic*, `code`, links, images, and inline $math$. In a bullet list, use Tab / Shift+Tab to change levels.</p>
                )}
                <div className="mt-2 min-h-0 flex-1">
                  {journalEditorMode === 'richtext' ? renderForumEditableSurface({
                    editorRef: journalEditorRef,
                    value: journalForm.bodyHtml || '',
                    onChange: (nextBodyHtml) => setJournalForm((current) => ({
                      ...current,
                      bodyHtml: nextBodyHtml,
                      body: getPlainTextFromRichHtml(nextBodyHtml)
                    })),
                    placeholder: 'Write your journal entry... use the toolbar for headings, lists, emphasis, links, and inline equations like $F=ma$. Use Tab / Shift+Tab for hierarchy.',
                    allowTabHierarchy: true
                  }) : (
                    <div className="grid min-h-[420px] gap-3 lg:grid-cols-2">
                      <textarea
                        value={journalForm.body}
                        onKeyDown={(event) => {
                          if (event.key !== 'Tab') return;
                          const input = event.currentTarget;
                          const start = input.selectionStart;
                          const end = input.selectionEnd;
                          const lineStart = input.value.lastIndexOf('\n', start - 1) + 1;
                          const block = input.value.slice(lineStart, end);
                          if (!/^\s*(?:[-+*]|\d+\.)\s/.test(block)) return;
                          event.preventDefault();
                          const lines = block.split('\n');
                          const changes = lines.map((line) => event.shiftKey ? -(line.match(/^(?: {1,4}|\t)/)?.[0].length || 0) : 4);
                          const indented = lines.map((line, index) => changes[index] < 0 ? line.slice(-changes[index]) : changes[index] ? '    ' + line : line).join('\n');
                          const nextBody = input.value.slice(0, lineStart) + indented + input.value.slice(end);
                          setJournalForm((current) => ({ ...current, body: nextBody, bodyHtml: '' }));
                          requestAnimationFrame(() => input.setSelectionRange(Math.max(lineStart, start + changes[0]), end + changes.reduce((sum, change) => sum + change, 0)));
                        }}
                        onChange={(event) => setJournalForm((current) => ({ ...current, body: event.target.value, bodyHtml: '' }))}
                        placeholder={'# Today\'s reflection\n\nWrite with **Markdown**, lists, links, and $inline equations$. Pocket • bullets, timelines, and decision trees are supported.'}
                        spellCheck="true"
                        aria-label="Journal Markdown"
                        className="journal-markdown-editor min-h-[420px] w-full resize-y rounded border border-slate-200 bg-white px-4 py-3 font-mono text-sm leading-6 text-slate-800 focus:border-slate-400 focus:outline-none"
                      />
                      <div className="min-h-[220px] overflow-y-auto rounded border border-slate-200 bg-slate-50 p-4">
                        <p className="mb-3 border-b border-slate-200 pb-2 text-[9px] font-bold uppercase tracking-wider text-slate-500">Live preview</p>
                        <div className="space-y-2">{renderJournalContent('', journalForm.body)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleForumEditorPaste = ({ e, editor, onChange }) => {
    const pastedText = e.clipboardData?.getData('text')?.trim();
    if (!pastedText || !/^https?:\/\//i.test(pastedText)) return;
    e.preventDefault();
    const safeUrl = pastedText.replace(/"/g, '&quot;');
    runForumEditorCommand(editor, 'insertHTML', `<a href="${safeUrl}" target="_blank" rel="noreferrer">${safeUrl}</a>`);
    if (onChange && editor) {
      onChange(editor.innerHTML);
    }
  };

  const uploadForumImageFile = async (file) => {
    if (!file || !storage || !selectedForumRoom || !user) {
      throw new Error('Image upload is unavailable.');
    }

    const safeName = String(file.name || 'image').replace(/[^a-zA-Z0-9._-]/g, '-');
    const imagePath = `teachers/${selectedForumRoom.teacherId}/forumRooms/${selectedForumRoom.roomId}/images/${Date.now()}-${safeName}`;
    const imageRef = storageRef(storage, imagePath);
    await uploadBytes(imageRef, file);
    return getDownloadURL(imageRef);
  };

  const handleUploadForumThreadImage = async (file) => {
    if (!file) return;
    try {
      const imageUrl = await uploadForumImageFile(file);
      setForumThreadForm((current) => ({ ...current, imageUrl }));
      showToast('Image attached to post.');
    } catch (error) {
      showToast(error.message || 'Could not upload image.', 'error');
    }
  };

  const handleUploadForumReplyImage = async (postId, file) => {
    if (!file || !postId) return;
    try {
      const imageUrl = await uploadForumImageFile(file);
      setForumReplyDrafts((current) => ({
        ...current,
        [postId]: { ...(current[postId] || {}), imageUrl }
      }));
      showToast('Image attached to reply.');
    } catch (error) {
      showToast(error.message || 'Could not upload image.', 'error');
    }
  };

  const canEditForumPost = (post) => {
    if (!post || !normalizedUserEmail) return false;
    const replies = forumRepliesByParent.get(post.id) || [];
    if (replies.length > 0) return false;
    return String(post.authorEmail || '').trim().toLowerCase() === normalizedUserEmail;
  };

  const handleOpenForumPostEditor = (post) => {
    if (!canEditForumPost(post)) return;
    setEditingForumPostId(post.id);
    const initialRichBody = post.bodyHtml || convertPlainTextToRichHtml(post.body || '');
    setForumPostEditDrafts((current) => ({
      ...current,
      [post.id]: {
        body: initialRichBody,
        imageUrl: post.imageUrl || '',
        externalUrl: post.externalUrl || ''
      }
    }));
  };

  const handleUploadForumPostEditImage = async (postId, file) => {
    if (!file || !postId) return;
    try {
      const imageUrl = await uploadForumImageFile(file);
      setForumPostEditDrafts((current) => ({
        ...current,
        [postId]: { ...(current[postId] || {}), imageUrl }
      }));
      showToast('Image attached to edited post.');
    } catch (error) {
      showToast(error.message || 'Could not upload image.', 'error');
    }
  };

  const handleSaveForumPostEdit = async (post) => {
    if (!selectedForumRoom || !post || !canEditForumPost(post)) {
      showToast('This post can no longer be edited.', 'error');
      return;
    }

    const draft = forumPostEditDrafts[post.id] || {};
    const bodyHtml = String(draft.body || '').trim();
    const body = getPlainTextFromRichHtml(bodyHtml);
    if (!body) {
      showToast('Post content is required.', 'error');
      return;
    }

    try {
      await updateDoc(
        doc(db, 'teachers', selectedForumRoom.teacherId, 'forumRooms', selectedForumRoom.roomId, 'posts', post.id),
        {
          body,
          bodyHtml,
          imageUrl: String(draft.imageUrl || '').trim(),
          externalUrl: String(draft.externalUrl || '').trim(),
          updatedAt: Date.now(),
          editedAt: Date.now()
        }
      );

      setEditingForumPostId('');
      setForumPostEditDrafts((current) => {
        const next = { ...current };
        delete next[post.id];
        return next;
      });
      showToast('Post updated.');
    } catch (error) {
      showToast('Could not update post.', 'error');
    }
  };

  const renderForumFormattingToolbar = ({ editorRef, onImageClick, onChange, showImageButton = true, allowTabHierarchy = false }) => (
    <div onMouseDown={(event) => event.preventDefault()} className="flex flex-wrap items-center gap-1 rounded border border-slate-200 bg-white p-1">
      {allowTabHierarchy && ['outdent', 'indent'].map((command) => (
        <button key={command} type="button" title={command === 'indent' ? 'Increase list level (Tab)' : 'Decrease list level (Shift+Tab)'} aria-label={command === 'indent' ? 'Increase list level' : 'Decrease list level'}
          className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          onClick={() => {
            runForumEditorCommand(editorRef.current, command);
            if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
          }}>
          {command === 'indent' ? '→' : '←'}
        </button>
      ))}
      <button
        type="button"
        onClick={() => {
          runForumEditorCommand(editorRef.current, 'bold');
          if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        }}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        title="Bold"
        aria-label="Bold"
      >
        <Bold className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => {
          runForumEditorCommand(editorRef.current, 'italic');
          if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        }}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        title="Italic"
        aria-label="Italic"
      >
        <Italic className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => {
          runForumEditorCommand(editorRef.current, 'styleWithCSS', true);
          runForumEditorCommand(editorRef.current, 'insertUnorderedList');
          if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        }}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        title="Bullet list"
        aria-label="Bullet list"
      >
        <List className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => {
          runForumEditorCommand(editorRef.current, 'styleWithCSS', true);
          runForumEditorCommand(editorRef.current, 'insertOrderedList');
          if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        }}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        title="Numbered list"
        aria-label="Numbered list"
      >
        <ListOrdered className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => {
          runForumEditorCommand(editorRef.current, 'formatBlock', 'h1');
          if (onChange && editorRef.current) onChange(editorRef.current.innerHTML);
        }}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        title="Heading"
        aria-label="Heading"
      >
        <Heading1 className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => {
          openForumLinkModal(editorRef.current, onChange);
        }}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        title="Insert external link"
        aria-label="Insert external link"
      >
        <Link2 className="w-3.5 h-3.5" />
      </button>
      {showImageButton ? (
        <button
          type="button"
          onClick={onImageClick}
          className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          title="Insert image"
          aria-label="Insert image"
        >
          <ImagePlus className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );

  const renderForumEditableSurface = ({ editorRef, value, onChange, placeholder = 'Write your post...', allowTabHierarchy = false }) => {
    const assignEditorRef = (node) => {
      if (typeof editorRef === 'function') {
        editorRef(node);
      } else if (editorRef) {
        editorRef.current = node;
      }

      if (!node) return;

      // Keep DOM text in sync only when the editor is not focused, preventing cursor jumps.
      const isFocused = typeof document !== 'undefined' && document.activeElement === node;
      const nextHtml = value || '';
      if (!isFocused && node.innerHTML !== nextHtml) {
        node.innerHTML = nextHtml;
      }
    };

    return (
      <div className="relative">
        {(!value || !getPlainTextFromRichHtml(value)) ? (
          <span className="pointer-events-none absolute left-3 top-2 text-xs text-slate-400">{placeholder}</span>
        ) : null}
        <div
          ref={assignEditorRef}
          contentEditable
          suppressContentEditableWarning
          onClick={(e) => {
            const link = e.target.closest('a[href]');
            if (!link || typeof window === 'undefined') return;
            e.preventDefault();
            e.stopPropagation();
            window.open(link.getAttribute('href') || '', '_blank', 'noopener,noreferrer');
          }}
          onKeyDown={(e) => {
            if (!allowTabHierarchy || e.key !== 'Tab') return;

            e.preventDefault();
            const editor = typeof editorRef === 'function' ? e.currentTarget : (editorRef?.current || e.currentTarget);
            focusForumEditor(editor);
            runForumEditorCommand(editor, 'styleWithCSS', true);

            if (e.shiftKey) {
              runForumEditorCommand(editor, 'outdent');
            } else {
              runForumEditorCommand(editor, 'indent');
            }

            if (editor && onChange) {
              onChange(editor.innerHTML);
            }
          }}
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          onBlur={(e) => onChange(e.currentTarget.innerHTML)}
          onPaste={(e) => {
            const editor = typeof editorRef === 'function' ? e.currentTarget : (editorRef?.current || e.currentTarget);
            handleForumEditorPaste({ e, editor, onChange });
          }}
          className="forum-editor min-h-[92px] w-full rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none"
        />
      </div>
    );
  };

  const renderForumThreadComposer = () => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleCreateForumPost({
          body: forumThreadForm.body,
          imageUrl: forumThreadForm.imageUrl,
          externalUrl: forumThreadForm.externalUrl
        });
      }}
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
    >
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Start a new thread (new column)</p>
      {renderForumFormattingToolbar({
        editorRef: forumThreadTextareaRef,
        onChange: (nextBody) => setForumThreadForm((current) => ({ ...current, body: nextBody })),
        onImageClick: () => forumThreadImageInputRef.current?.click()
      })}
      {renderForumEditableSurface({
        editorRef: forumThreadTextareaRef,
        value: forumThreadForm.body,
        onChange: (nextBody) => setForumThreadForm((current) => ({ ...current, body: nextBody })),
        placeholder: 'Share a topic, question, or response'
      })}
      <input
        ref={forumThreadImageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) {
            handleUploadForumThreadImage(file);
          }
        }}
      />
      {forumThreadForm.imageUrl ? (
        <div className="space-y-1 text-[11px] text-slate-600">
          {forumThreadForm.imageUrl ? <p className="truncate">Image attached: {forumThreadForm.imageUrl}</p> : null}
        </div>
      ) : null}
      <div className="flex justify-end">
        <button type="submit" className="inline-flex items-center rounded bg-slate-900 p-2 text-white hover:bg-slate-800" title="Post thread" aria-label="Post thread">
          <SendHorizontal className="w-3.5 h-3.5" />
        </button>
      </div>
    </form>
  );

  const renderForumPostCard = (post, depth = 0) => {
    const replies = forumRepliesByParent.get(post.id) || [];
    const replyDraft = forumReplyDrafts[post.id] || null;
    const postEditDraft = forumPostEditDrafts[post.id] || null;
    const isEditingPost = editingForumPostId === post.id;
    const isExpanded = forumExpandAll || Boolean(expandedForumCards[post.id]);
    const canShowInlineMedia = Boolean(post.imageUrl);
    const canEditPost = canEditForumPost(post);

    return (
      <div key={post.id} className={depth > 0 ? 'ml-4 border-l border-slate-200 pl-3 mt-2' : ''}>
        <div
          className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm cursor-pointer"
          onClick={(e) => {
            const interactiveTarget = e.target.closest('a, button, input, textarea, select, label, [contenteditable="true"]');
            if (interactiveTarget) return;
            setExpandedForumCards((current) => ({ ...current, [post.id]: !current[post.id] }));
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {post.authorName || 'User'} · {post.authorRole || 'participant'}
              </p>
              {isExpanded ? (
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {post.createdAt ? new Date(post.createdAt).toLocaleString() : 'Just now'}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedForumCards((current) => ({ ...current, [post.id]: !current[post.id] }));
              }}
              title={isExpanded ? 'Collapse post' : 'Expand post'}
              aria-label={isExpanded ? 'Collapse post' : 'Expand post'}
            >
              {isExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </button>
          </div>

          {isExpanded ? (
            <div className="mt-2 space-y-1 text-xs leading-relaxed text-slate-700" onClick={(e) => e.stopPropagation()}>
              {post.bodyHtml ? (
                <div className="forum-rich-content whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: injectInlineLatexIntoRichHtml(post.bodyHtml) }} />
              ) : (
                renderMarkdown(post.body || '')
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs leading-relaxed text-slate-700 whitespace-pre-wrap">
              {renderInlineMarkdown(
                getForumPreviewText(post.bodyHtml ? getPlainTextFromRichHtml(post.bodyHtml) : post.body),
                `preview-${post.id}`
              )}
            </p>
          )}

          {isExpanded && canShowInlineMedia ? (
            <div className="mt-2 space-y-2">
              {post.imageUrl ? (
                <a href={post.imageUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="block">
                  <img src={post.imageUrl} alt="Post attachment" className="max-h-52 w-full rounded border border-slate-200 object-cover" />
                </a>
              ) : null}
            </div>
          ) : null}

          <div className="mt-3 flex items-center gap-3">
            {depth === 0 && canManageForumRooms ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openForumAiThreadPrompt(post);
                }}
                className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                title="Ask AI for thread feedback"
                aria-label="Ask AI for thread feedback"
              >
                <Sparkles className="w-3 h-3" />
                AI
              </button>
            ) : null}
            {canEditPost ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isEditingPost) {
                    setEditingForumPostId('');
                    setForumPostEditDrafts((current) => {
                      const next = { ...current };
                      delete next[post.id];
                      return next;
                    });
                  } else {
                    handleOpenForumPostEditor(post);
                  }
                }}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                title={isEditingPost ? 'Cancel editing' : 'Edit post'}
                aria-label={isEditingPost ? 'Cancel editing' : 'Edit post'}
              >
                <Pencil className="w-3 h-3" />
              </button>
            ) : null}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setForumReplyDrafts((current) => ({
                  ...current,
                  [post.id]: current[post.id] || { body: '', imageUrl: '', externalUrl: '' }
                }));
              }}
              className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              title="Reply"
              aria-label="Reply"
            >
              <Reply className="w-3 h-3" />
            </button>
          </div>
        </div>

        {isEditingPost && postEditDraft ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveForumPostEdit(post);
            }}
            className="mt-2 rounded border border-slate-200 bg-slate-50 p-2.5 space-y-2"
          >
            {renderForumFormattingToolbar({
              editorRef: {
                current: forumPostEditTextareaRefs.current[post.id] || null
              },
              onChange: (nextBody) => setForumPostEditDrafts((current) => ({
                ...current,
                [post.id]: { ...(current[post.id] || {}), body: nextBody }
              })),
              onImageClick: () => forumPostEditImageInputRefs.current[post.id]?.click()
            })}
            {renderForumEditableSurface({
              editorRef: {
                get current() {
                  return forumPostEditTextareaRefs.current[post.id] || null;
                },
                set current(element) {
                  if (element) forumPostEditTextareaRefs.current[post.id] = element;
                  else delete forumPostEditTextareaRefs.current[post.id];
                }
              },
              value: postEditDraft.body || '',
              onChange: (nextBody) => setForumPostEditDrafts((current) => ({
                ...current,
                [post.id]: { ...(current[post.id] || {}), body: nextBody }
              })),
              placeholder: 'Edit your post'
            })}
            <input
              ref={(element) => {
                if (element) {
                  forumPostEditImageInputRefs.current[post.id] = element;
                } else {
                  delete forumPostEditImageInputRefs.current[post.id];
                }
              }}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) {
                  handleUploadForumPostEditImage(post.id, file);
                }
              }}
            />
            {postEditDraft.imageUrl ? (
              <div className="space-y-1 text-[11px] text-slate-600">
                {postEditDraft.imageUrl ? <p className="truncate">Image attached: {postEditDraft.imageUrl}</p> : null}
              </div>
            ) : null}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditingForumPostId('');
                  setForumPostEditDrafts((current) => {
                    const next = { ...current };
                    delete next[post.id];
                    return next;
                  });
                }}
                className="rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center rounded bg-slate-900 p-1.5 text-white hover:bg-slate-800"
                title="Save post"
                aria-label="Save post"
              >
                <SendHorizontal className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
        ) : null}

        {replyDraft ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateForumPost({
                parentId: post.id,
                rootId: post.rootId || post.id,
                body: replyDraft.body,
                imageUrl: replyDraft.imageUrl,
                externalUrl: replyDraft.externalUrl
              });
            }}
            className="mt-2 rounded border border-slate-200 bg-slate-50 p-2.5 space-y-2"
          >
            {renderForumFormattingToolbar({
              editorRef: {
                current: forumReplyTextareaRefs.current[post.id] || null
              },
              onChange: (nextBody) => setForumReplyDrafts((current) => ({
                ...current,
                [post.id]: { ...(current[post.id] || {}), body: nextBody }
              })),
              onImageClick: () => forumReplyImageInputRefs.current[post.id]?.click()
            })}
            {renderForumEditableSurface({
              editorRef: {
                get current() {
                  return forumReplyTextareaRefs.current[post.id] || null;
                },
                set current(element) {
                  if (element) forumReplyTextareaRefs.current[post.id] = element;
                  else delete forumReplyTextareaRefs.current[post.id];
                }
              },
              value: replyDraft.body || '',
              onChange: (nextBody) => setForumReplyDrafts((current) => ({
                ...current,
                [post.id]: { ...(current[post.id] || {}), body: nextBody }
              })),
              placeholder: 'Write a reply'
            })}
            <input
              ref={(element) => {
                if (element) {
                  forumReplyImageInputRefs.current[post.id] = element;
                } else {
                  delete forumReplyImageInputRefs.current[post.id];
                }
              }}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) {
                  handleUploadForumReplyImage(post.id, file);
                }
              }}
            />
            {replyDraft.imageUrl ? (
              <div className="space-y-1 text-[11px] text-slate-600">
                {replyDraft.imageUrl ? <p className="truncate">Image attached: {replyDraft.imageUrl}</p> : null}
              </div>
            ) : null}
            <div className="flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={() => setForumReplyDrafts((current) => {
                  const next = { ...current };
                  delete next[post.id];
                  return next;
                })}
                className="rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center rounded bg-slate-900 p-1.5 text-white hover:bg-slate-800"
                title="Send reply"
                aria-label="Send reply"
              >
                <SendHorizontal className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
        ) : null}

        {replies.length > 0 ? (
          <div className="mt-1">
            {replies.map((reply) => renderForumPostCard(reply, depth + 1))}
          </div>
        ) : null}

        {depth === 0 && isStudentUser ? (
          <div className="mt-2 ml-1">
            <button
              type="button"
              disabled={hasStudentUsedThreadAiFeedback(post) || forumStudentAiLoadingRootId === post.id}
              onClick={(e) => {
                e.stopPropagation();
                handleStudentAiFeedbackPost(post);
              }}
              className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
              title="Generate one AI feedback post for this thread"
              aria-label="Generate one AI feedback post for this thread"
            >
              <Sparkles className="w-3 h-3" />
              {hasStudentUsedThreadAiFeedback(post)
                ? 'AI Used'
                : forumStudentAiLoadingRootId === post.id
                ? 'Posting...'
                : 'AI Feedback'}
            </button>
            <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">One AI feedback post per student per thread.</p>
          </div>
        ) : null}
      </div>
    );
  };

  const resetStudentEventModal = () => {
    setShowStudentEventModal(false);
    setEditingStudentEventId('');
    setStudentEventModalForm({ date: getTodayDateValue(), title: '', time: '', description: '' });
  };

  const handleOpenStudentEventModal = (eventItem = null) => {
    if (eventItem) {
      setEditingStudentEventId(eventItem.id || '');
      setStudentEventModalForm({
        date: eventItem.date || getTodayDateValue(),
        title: eventItem.title || '',
        time: eventItem.time || '',
        description: eventItem.description || ''
      });
    } else {
      setEditingStudentEventId('');
      setStudentEventModalForm({ date: getTodayDateValue(), title: '', time: '', description: '' });
    }
    setShowStudentEventModal(true);
  };

  const handleSaveStudentPlannerEvent = async (e) => {
    e.preventDefault();
    if (!user) return;
    const { date, title, time, description } = studentEventModalForm;
    if (!title.trim() || !time.trim() || !date) return;
    try {
      if (editingStudentEventId) {
        await updateDoc(doc(db, 'studentProfiles', user.uid, 'plannerEvents', editingStudentEventId), {
          date,
          title: title.trim(),
          time: time.trim(),
          description: description.trim(),
          updatedAt: Date.now()
        });
        showToast('Personal event updated.');
      } else {
        const docRef = doc(collection(db, 'studentProfiles', user.uid, 'plannerEvents'));
        await setDoc(docRef, {
          date,
          title: title.trim(),
          time: time.trim(),
          description: description.trim(),
          createdAt: Date.now()
        });
        showToast('Personal event added.');
      }

      resetStudentEventModal();
    } catch (error) {
      showToast(`Could not ${editingStudentEventId ? 'update' : 'add'} personal event.`, 'error');
    }
  };

  const handleDeleteStudentPlannerEvent = async (eventId) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'studentProfiles', user.uid, 'plannerEvents', eventId));
      if (editingStudentEventId === eventId) {
        resetStudentEventModal();
      }
      showToast('Personal event removed.');
    } catch (error) {
      showToast('Could not remove personal event.', 'error');
    }
  };

  useEffect(() => {
    if (!user || !isFirebaseEnabled || isStudentUser || classes.length === 0 || lessons.length === 0) return;

    const teacherId = user.uid;
    const backfillTargets = lessons
      .filter((lesson) => !lesson.classId && lesson.calendarSource !== 'google-calendar')
      .map((lesson) => {
        const linkedClass = classes.find((cls) => cls.id === lesson.subject || cls.name === lesson.subject || cls.name === lesson.className);
        if (!linkedClass) return null;
        return {
          lessonId: lesson.id,
          classId: linkedClass.id,
          className: linkedClass.name
        };
      })
      .filter(Boolean);

    if (backfillTargets.length === 0) return;

    Promise.all(
      backfillTargets.map((target) => updateDoc(doc(db, 'teachers', teacherId, 'lessonPlans', target.lessonId), {
        classId: target.classId,
        className: target.className
      }))
    ).catch((error) => console.error('Lesson backfill error:', error));
  }, [user, isFirebaseEnabled, isStudentUser, classes, lessons]);

  useEffect(() => {
    if (!user || !isFirebaseEnabled || isStudentUser) return;

    const teacherId = user.uid;
    const publishableLessons = lessons.filter((lesson) => lesson.classId);
    if (publishableLessons.length === 0) return;

    const changedLessons = publishableLessons
      .map((lesson) => {
        const payload = {
          teacherId,
          lessonId: lesson.id,
          classId: lesson.classId,
          className: lesson.className || lesson.subject,
          topic: lesson.topic || '',
          date: lesson.date || '',
          startTime: lesson.startTime || '',
          endTime: lesson.endTime || '',
          notesForStudents: lesson.notesForStudents || '',
          attachments: lesson.attachments || [],
          exitTicket: sanitizeExitTicketConfig(lesson.exitTicket)
        };

        const fingerprint = JSON.stringify(payload);
        if (plannerPublishFingerprintRef.current[lesson.id] === fingerprint) {
          return null;
        }

        plannerPublishFingerprintRef.current[lesson.id] = fingerprint;
        return payload;
      })
      .filter(Boolean);

    if (changedLessons.length === 0) return;

    Promise.all(
      changedLessons.map((payload) => setDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'plannerEntries', getStudentPlannerEntryId(teacherId, payload.lessonId)),
        {
          ...payload,
          updatedAt: Date.now()
        },
        { merge: true }
      ))
    ).catch((error) => console.error('Planner entry publish error:', error));
  }, [user, isFirebaseEnabled, isStudentUser, lessons]);

  if (isFirebaseEnabled && !authStateChecked) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-800 flex items-center justify-center px-4">
        <div className="fixed right-5 top-5">{renderThemeToggle()}</div>
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 text-center shadow-xl">
          <div className="text-slate-900 text-2xl font-bold mb-3">Preparing secure access...</div>
          <p className="text-slate-500 text-sm">Checking your authentication status.</p>
        </div>
      </div>
    );
  }

  if (isFirebaseEnabled && !user) {
    return (
      <div className="min-h-screen bg-slate-100 text-slate-900 flex items-center justify-center px-4 py-12">
        <div className="fixed right-5 top-5">{renderThemeToggle()}</div>
        <div className="max-w-lg w-full rounded-[32px] overflow-hidden shadow-2xl bg-white">
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <div className="max-w-md mx-auto w-full">
              <div className="mb-8 text-center">
                <p className="text-sm uppercase tracking-[0.35em] text-slate-500 mb-3">Sign in</p>
                <h2 className="text-3xl font-bold text-slate-900">{authPortal === 'teacher' ? 'Welcome back.' : 'Student access.'}</h2>
                <p className="mt-2 text-sm text-slate-500">{authPortal === 'teacher' ? 'Use Google, email or continue as a guest to get started.' : 'Sign in with your registered school email and password.'}</p>
              </div>

              <div className="mb-6 grid grid-cols-2 gap-2 rounded-3xl bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => {
                    setAuthPortal('student');
                    setAuthError('');
                    setAuthNotice('');
                  }}
                  className={`rounded-[20px] px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${authPortal === 'student' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Student Access
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAuthPortal('teacher');
                    setAuthError('');
                    setAuthNotice('');
                  }}
                  className={`rounded-[20px] px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${authPortal === 'teacher' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Teacher Access
                </button>
              </div>

              {authPortal === 'teacher' && (
                <button type="button" onClick={handleSignInWithGoogle} disabled={authLoading} className="w-full inline-flex items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:shadow-md disabled:opacity-60">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5"><path fill="#EA4335" d="M24 9.5c3.54 0 6.23 1.53 8.11 2.83l6-5.86C35.35 3.7 30.98 2 24 2 14.84 2 7.15 7.78 3.9 15.06l7.28 5.64C12.85 16.09 18.85 9.5 24 9.5z"/><path fill="#34A853" d="M46.5 24.5c0-1.62-.16-2.79-.51-4.01H24v8.01h12.78c-.55 3.1-2.62 6.04-6.28 7.84l6.09 4.73C43.73 37.05 46.5 31.28 46.5 24.5z"/><path fill="#4A90E2" d="M12.18 28.7A14.9 14.9 0 0 1 11 24.5c0-1.54.25-3.03.69-4.4l-7.28-5.64C2.37 16.33 0 20.86 0 24.5 0 33.19 6.49 40.9 15.38 43.07l-3.2-6.37z"/><path fill="#FBBC05" d="M24 46c6.98 0 11.35-1.7 15.61-4.33l-6.09-4.73C30.23 36.47 27.54 37.5 24 37.5c-5.15 0-11.15-6.59-12.68-9.2L4.23 32.94C7.75 38.81 15.64 46 24 46z"/></svg>
                  {authLoading ? 'Opening Google…' : 'Sign in with Google'}
                </button>
              )}

              {authPortal === 'teacher' && (
                <div className="relative my-6">
                  <div className="absolute inset-x-10 top-1/2 h-px bg-slate-200"></div>
                  <p className="relative mx-auto w-fit bg-white px-3 text-xs uppercase tracking-[0.3em] text-slate-500">or</p>
                </div>
              )}

              {authPortal === 'student' && (
                <div className="mb-5 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 space-y-2">
                  <p className="font-semibold uppercase tracking-wider text-slate-900">Student onboarding</p>
                  <p>1. Enter your school email.</p>
                  <p>2. Click Set up password to receive a secure email from Firebase.</p>
                  <p>3. Choose your own password, then return here to sign in.</p>
                  <p className="text-slate-500">Passwords are secured by Firebase Authentication and are never visible to teachers.</p>
                </div>
              )}

              <form onSubmit={handleEmailAuth} className="space-y-4">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Email</label>
                <input
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder={authPortal === 'student' ? 'name@students.edu.sg' : 'you@example.com'}
                  className="w-full bg-slate-50 border border-slate-200 rounded-3xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Password</label>
                <input
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-slate-50 border border-slate-200 rounded-3xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full bg-slate-900 text-white uppercase tracking-widest text-xs font-bold py-3 rounded-3xl hover:bg-slate-950 transition-colors disabled:opacity-60"
                >
                  {authLoading ? 'Working…' : authPortal === 'teacher' ? 'Sign in with email' : 'Student sign in'}
                </button>
              </form>

              {authPortal === 'student' ? (
                <div className="mt-4 text-center text-sm space-y-3">
                  <button type="button" onClick={handleStudentActivation} disabled={authLoading} className="font-semibold text-slate-900 hover:text-indigo-600 transition-colors">
                    {authLoading ? 'Working…' : 'Set up password / Forgot password'}
                  </button>
                  <p className="text-xs text-slate-500">Your email must already be registered in your teacher’s class roster.</p>
                </div>
              ) : (
                <div className="mt-6 text-center text-sm">
                  <button type="button" onClick={handleContinueAsGuest} disabled={authLoading} className="font-semibold text-slate-900 hover:text-indigo-600 transition-colors">
                    {authLoading ? 'Working…' : 'Continue as guest'}
                  </button>
                </div>
              )}

              {authNotice && <div className="mt-4 rounded-3xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-700 font-semibold">{authNotice}</div>}
              {authError && <div className="mt-4 rounded-3xl bg-red-50 border border-red-200 p-3 text-xs text-red-700 font-semibold">{authError}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isForumStandalone) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-800">
        <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
          <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Forum Room</p>
              <h1 className="text-sm font-bold text-slate-900">{selectedForumRoom?.title || 'Loading room...'}</h1>
            </div>
            <a
              href={forumBaseUrl}
              className="rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
            >
              Back to app
            </a>
            {renderThemeToggle()}
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl px-6 py-6">
          {!selectedForumRoom ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Room not found or no access.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Description</p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={openForumAiDashboard}
                      className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      title="Open AI dashboard"
                      aria-label="Open AI dashboard"
                    >
                      <Sparkles className="w-3 h-3" />
                      AI
                    </button>
                    {selectedForumRoom.teacherId === user?.uid ? (
                      <button
                        type="button"
                        onClick={() => setIsEditingRoomDescription((current) => !current)}
                        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                        title={isEditingRoomDescription ? 'Cancel editing description' : 'Edit description'}
                        aria-label={isEditingRoomDescription ? 'Cancel editing description' : 'Edit description'}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </div>
                </div>
                {isEditingRoomDescription && selectedForumRoom.teacherId === user?.uid ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      rows="3"
                      value={roomDescriptionDraft}
                      onChange={(e) => setRoomDescriptionDraft(e.target.value)}
                      className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditingRoomDescription(false);
                          setRoomDescriptionDraft(selectedForumRoom.description || '');
                        }}
                        className="rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-white"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveSelectedRoomDescription}
                        className="rounded bg-slate-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-slate-700">{selectedForumRoom.description || 'No room description.'}</p>
                )}
              </div>

              {renderForumThreadComposer()}

              <div className="rounded-lg border border-slate-200 bg-white p-4 overflow-x-auto min-h-[60vh]">
                {forumThreadRoots.length === 0 ? (
                  <p className="py-10 text-center text-xs font-bold uppercase tracking-wider text-slate-400">No threads yet in this room.</p>
                ) : (
                  <div className="flex items-start gap-4 min-w-full">
                    {forumThreadRoots.map((threadRoot) => (
                      <div key={threadRoot.id} className="min-w-[320px] max-w-[420px] flex-1">
                        {renderForumPostCard(threadRoot)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
        {renderForumLinkModal()}
        {renderForumAiThreadPromptModal()}
        {renderForumAiModal()}
      </div>
    );
  }

  if (isStudentUser) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-900">PHYHUB</div>
              <div className="hidden sm:block">
                <h1 className="text-sm font-bold text-slate-900">Student Homework</h1>
                <p className="text-[10px] text-slate-400 uppercase tracking-widest">The World in a Different Light</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-slate-500 hidden md:inline">{user?.email}</span>
              {renderThemeToggle()}
              <button onClick={handleSignOut} className="text-[10px] font-semibold uppercase tracking-wider px-3 py-2 rounded bg-slate-900 text-white hover:bg-slate-950 transition-colors">
                Sign Out
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-6xl w-full mx-auto px-6 py-8 flex-1">
          {(() => {
            const plannerDays = Array.from({ length: 7 }, (_, index) => {
              const date = new Date(studentPlannerWeekStart);
              date.setDate(studentPlannerWeekStart.getDate() + index);
              return date;
            });
            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            const weekLabel = `${plannerDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${plannerDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            const renderStudentPlannerDay = (day, label) => {
              const dateKey = toDateKey(day);
              const isToday = dateKey === toDateKey(new Date());
              const dayLessons = studentPlannerLessons.filter((lesson) => lesson.date === dateKey);
              const dayEvents = studentPlannerEventsByDate.get(dateKey) || [];
              const dayTodos = studentPlannerTodosByDate.get(dateKey) || [];
              const dayItems = [
                ...dayLessons.map((lesson) => ({
                  id: `lesson-${lesson.id}`,
                  type: 'lesson',
                  time: lesson.startTime || '',
                  endTime: lesson.endTime || '',
                  data: lesson
                })),
                ...dayEvents.map((eventItem) => ({
                  id: `event-${eventItem.id}`,
                  type: 'event',
                  time: eventItem.time || '',
                  endTime: '',
                  data: eventItem
                }))
              ].sort((a, b) => {
                const timeDiff = getTimeSortValue(a.time) - getTimeSortValue(b.time);
                if (timeDiff !== 0) return timeDiff;
                return (a.data.title || a.data.topic || '').localeCompare(b.data.title || b.data.topic || '');
              });

              return (
                <div key={dateKey} className="bg-white rounded-lg border border-slate-200 flex flex-col min-h-[420px]">
                  <div className={`px-3 py-2.5 border-b border-slate-200 rounded-t-lg ${isToday ? 'bg-slate-900' : 'bg-slate-50'}`}>
                    <p className={`text-[9px] font-bold uppercase tracking-widest ${isToday ? 'text-slate-300' : 'text-slate-400'}`}>{label}</p>
                    <p className={`text-base font-bold leading-none mt-0.5 ${isToday ? 'text-white' : 'text-slate-800'}`}>{day.getDate()}</p>
                  </div>
                  <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                    {dayItems.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-[9px] text-slate-300 uppercase tracking-wider text-center">No plans</p>
                      </div>
                    ) : (
                      dayItems.map((item) => {
                        if (item.type === 'event') {
                          const eventItem = item.data;
                          const eventDescription = getDisplayEventDescription(eventItem.description);
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleOpenStudentEventModal(eventItem)}
                              className="w-full text-left bg-slate-50 border border-slate-200 rounded-lg p-2.5 relative group hover:border-slate-400 hover:shadow-sm transition-all"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <span className="text-[9px] font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">Personal event</span>
                                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mt-1">{eventItem.time || '--:--'}</p>
                                  <p className="text-[11px] font-bold text-slate-900 mt-1.5 leading-tight">{eventItem.title}</p>
                                  {eventDescription ? <p className="text-[10px] text-slate-600 mt-1 leading-snug">{eventDescription}</p> : null}
                                </div>
                              </div>
                            </button>
                          );
                        }

                        const lesson = item.data;
                        return (
                          <div
                            key={item.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedLesson(lesson)}
                            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedLesson(lesson); }}
                            className="w-full cursor-pointer text-left bg-slate-50 border border-slate-200 rounded-lg p-2.5 relative group hover:border-slate-400 hover:shadow-sm transition-all"
                          >
                            <span className="text-[9px] font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">{lesson.className || lesson.subject}</span>
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mt-1">{lesson.startTime || '--:--'} - {lesson.endTime || '--:--'}</p>
                            <p className="mt-2 line-clamp-3 text-[10px] leading-snug text-slate-600">{getTeacherNotesExcerpt(lesson.notesForTeachers)}</p>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div
                    className="border-t border-slate-200 bg-slate-50/60 p-2"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDropLessonTodo(dateKey)}
                  >
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">To-do</p>
                    <div className="space-y-1 max-h-36 overflow-y-auto pr-0.5">
                      {dayTodos.length === 0 ? (
                        <p className="rounded border border-dashed border-slate-200 bg-white px-2 py-2 text-[9px] uppercase tracking-wider text-slate-300">No tasks</p>
                      ) : (
                        dayTodos.map((todo, todoIndex) => (
                          <div
                            key={todo.id}
                            draggable
                            onDragStart={() => setDraggingTodoId(todo.id)}
                            onDragEnd={() => setDraggingTodoId('')}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleDropLessonTodo(dateKey, todoIndex);
                            }}
                            className={`flex items-center gap-1.5 rounded border px-1.5 py-1 bg-white ${draggingTodoId === todo.id ? 'border-slate-400 opacity-50' : 'border-slate-200'}`}
                          >
                            <span className="text-[10px] text-slate-400 cursor-grab">::</span>
                            <button
                              type="button"
                              onClick={() => handleToggleLessonTodoDone(todo.id, Boolean(todo.done))}
                              className="text-slate-500 hover:text-slate-800"
                              title="Toggle complete"
                            >
                              {todo.done ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                            </button>
                            <input
                              type="text"
                              value={lessonTodoDrafts[todo.id] ?? todo.text}
                              onChange={(e) => setLessonTodoDrafts((prev) => ({ ...prev, [todo.id]: e.target.value }))}
                              onBlur={() => handleSaveLessonTodoText(todo.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveLessonTodoText(todo.id);
                                }
                              }}
                              className={`flex-1 min-w-0 w-0 bg-transparent text-[10px] text-slate-700 focus:outline-none ${todo.done ? 'line-through text-slate-400' : ''}`}
                            />
                            <button
                              type="button"
                              onClick={() => handleDeleteLessonTodo(todo.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete todo"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="mt-1.5 flex gap-1">
                      <input
                        type="text"
                        value={lessonTodoInputByDate[dateKey] || ''}
                        onChange={(e) => setLessonTodoInputByDate((prev) => ({ ...prev, [dateKey]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddLessonTodo(dateKey);
                          }
                        }}
                        placeholder="Add task"
                        className="flex-1 min-w-0 w-0 rounded border border-slate-200 bg-white px-2 py-1 text-[10px] text-slate-700 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddLessonTodo(dateKey)}
                        className="rounded bg-slate-900 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-white hover:bg-slate-800"
                      >
                        Add
                      </button>
                    </div>
                    {/* Personal event entry removed; now handled by modal */}
                  </div>
                </div>
              );
            };

            return (
                <div className="space-y-6">
                  {showStudentEventModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={e => { if (e.target === e.currentTarget) resetStudentEventModal(); }}>
                      <form
                        className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4"
                        onSubmit={handleSaveStudentPlannerEvent}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xs font-bold uppercase tracking-wider text-amber-700">{editingStudentEventId ? 'Edit Personal Event' : 'Add Personal Event'}</h3>
                          <button type="button" onClick={resetStudentEventModal} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
                        </div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Date</label>
                        <input
                          type="date"
                          value={studentEventModalForm.date}
                          onChange={e => setStudentEventModalForm(f => ({ ...f, date: e.target.value }))}
                          className="w-full rounded border border-amber-200 bg-white px-2 py-2 text-[12px] text-slate-700 focus:outline-none mb-2"
                          required
                        />
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Title</label>
                        <input
                          type="text"
                          value={studentEventModalForm.title}
                          onChange={e => setStudentEventModalForm(f => ({ ...f, title: e.target.value }))}
                          placeholder="Event title"
                          className="w-full rounded border border-amber-200 bg-white px-2 py-2 text-[12px] text-slate-700 focus:outline-none mb-2"
                          required
                        />
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Time</label>
                        <input
                          type="text"
                          value={studentEventModalForm.time}
                          onChange={e => setStudentEventModalForm(f => ({ ...f, time: e.target.value }))}
                          placeholder="Time (e.g., 4:00 PM)"
                          className="w-full rounded border border-amber-200 bg-white px-2 py-2 text-[12px] text-slate-700 focus:outline-none mb-2"
                          required
                        />
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Description</label>
                        <input
                          type="text"
                          value={studentEventModalForm.description}
                          onChange={e => setStudentEventModalForm(f => ({ ...f, description: e.target.value }))}
                          placeholder="Description (optional)"
                          className="w-full rounded border border-amber-200 bg-white px-2 py-2 text-[12px] text-slate-700 focus:outline-none mb-2"
                        />
                        <button
                          type="submit"
                          className="w-full rounded bg-amber-500 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-amber-600 mt-2"
                        >
                          {editingStudentEventId ? 'Save Changes' : 'Add Event'}
                        </button>
                        {editingStudentEventId ? (
                          <button
                            type="button"
                            onClick={() => handleDeleteStudentPlannerEvent(editingStudentEventId)}
                            className="w-full rounded border border-red-200 bg-red-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-red-600 hover:bg-red-100"
                          >
                            Delete Event
                          </button>
                        ) : null}
                      </form>
                    </div>
                  )}
                <div className="bg-white rounded-lg border border-slate-200 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">My Personal Planner</h2>
                    <p className="text-sm text-slate-400">Your weekly class schedule, homework, and shared updates.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-nowrap sm:justify-end overflow-x-auto">
                    <button onClick={() => { const date = new Date(studentPlannerWeekStart); date.setDate(date.getDate() - 7); setStudentPlannerWeekStart(date); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-[11px] font-bold text-slate-700 min-w-[160px] text-center">{weekLabel}</span>
                    <button onClick={() => { const date = new Date(studentPlannerWeekStart); date.setDate(date.getDate() + 7); setStudentPlannerWeekStart(date); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"><ChevronRight className="w-4 h-4" /></button>
                    <button onClick={() => setStudentPlannerWeekStart(getWeekStartDate(new Date()))} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors">Today</button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-950 shadow"
                      onClick={() => handleOpenStudentEventModal()}
                    >
                      <Plus className="w-3.5 h-3.5" /> New Event
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-1">
                  <div className="grid grid-cols-7 gap-3 min-w-[140%]">
                    {plannerDays.map((day, index) => renderStudentPlannerDay(day, dayNames[index]))}
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[minmax(0,1.65fr)_minmax(320px,1fr)]">
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="border-b border-slate-150 pb-4 mb-6">
                      <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">My Homework Submissions</h2>
                      <p className="text-sm text-slate-400">Assignments for {studentMemberships[0]?.studentName || user?.email}</p>
                    </div>

                    {studentHomeworkItems.length === 0 ? (
                      <div className="text-center py-16 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                        <p className="text-xs text-slate-400 uppercase tracking-widest">No assignments yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full min-w-[760px] border-collapse bg-white text-left">
                          <thead className="bg-slate-100">
                            <tr>
                              {['Assignment', 'Class', 'Due date', 'Mode', 'Result'].map((heading) => (
                                <th key={heading} className="whitespace-nowrap border-b border-slate-200 px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                  {heading}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {studentHomeworkItems.map((item) => (
                              <tr key={item.studentRowKey || item.id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50">
                                <td className="whitespace-nowrap px-4 py-3 text-xs font-bold text-slate-900">{item.title}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{item.className || '—'}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600">{item.dueDate || '—'}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{item.trackingMode}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-xs font-bold text-slate-900">
                                  {item.trackingMode === 'score'
                                    ? `${item.submission.score || '—'} / ${item.maxPoints || '—'}`
                                    : String(item.submission.status || 'unsubmitted').toUpperCase()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="border-b border-slate-150 pb-4 mb-6">
                      <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Notice Board</h2>
                      <p className="text-sm text-slate-400">Updates shared with your classes.</p>
                    </div>

                    {studentVisibleNotes.length === 0 ? (
                      <div className="text-center py-16 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                        <p className="text-xs text-slate-400 uppercase tracking-widest">No notices for you yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[680px] overflow-y-auto pr-1">
                        {studentVisibleNotes.map((note) => (
                          <div key={note.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <p className="text-sm font-medium text-slate-900 leading-relaxed">{note.text}</p>
                            <div className="mt-3 flex items-center justify-between gap-3 text-[10px] uppercase tracking-wider text-slate-500">
                              <span className="font-semibold">{note.author}</span>
                              <span>{note.date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
                  <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
                    <div>
                      <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Class Forum</h2>
                      <p className="text-sm text-slate-400">Join targeted discussion rooms and reply in nested threads.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedForumRoom ? (
                        <button
                          type="button"
                          onClick={openForumAiDashboard}
                          className="inline-flex items-center gap-1 rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          AI Dashboard
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={handleToggleForumExpandAll}
                        className="inline-flex items-center gap-1 rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
                      >
                        {forumExpandAll ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                        {forumExpandAll ? 'Collapse all' : 'Expand all'}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-[260px_minmax(0,1fr)] gap-4">
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                      <div className="border-b border-slate-200 px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rooms you can access</p>
                      </div>
                      <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-100">
                        {accessibleForumRooms.length === 0 ? (
                          <p className="px-4 py-6 text-center text-xs text-slate-400">No forum rooms available.</p>
                        ) : (
                          accessibleForumRooms.map((room) => (
                            <button
                              key={room.roomKey}
                              type="button"
                              onClick={() => {
                                setSelectedForumRoomId(room.roomKey);
                                openForumRoomInNewTab(room.roomKey);
                              }}
                              className={`w-full px-4 py-3 text-left ${selectedForumRoomId === room.roomKey ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                            >
                              <p className="text-xs font-bold uppercase tracking-wide truncate">{room.title}</p>
                              <p className={`mt-1 text-[10px] ${selectedForumRoomId === room.roomKey ? 'text-slate-300' : 'text-slate-500'} line-clamp-2`}>
                                {room.description || 'No description provided.'}
                              </p>
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="space-y-3 min-w-0">
                      {!selectedForumRoom ? (
                        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Pick a room to join the thread</p>
                        </div>
                      ) : (
                        <>
                          {renderForumThreadComposer()}

                          <div className="rounded-lg border border-slate-200 bg-white p-4 overflow-x-auto">
                            {forumThreadRoots.length === 0 ? (
                              <p className="py-8 text-center text-xs font-bold uppercase tracking-wider text-slate-400">No posts yet in this room.</p>
                            ) : (
                              <div className="flex items-start gap-4 min-w-full">
                                {forumThreadRoots.map((threadRoot) => (
                                  <div key={threadRoot.id} className="min-w-[300px] max-w-[360px] flex-1">
                                    {renderForumPostCard(threadRoot)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {selectedLesson && (
                  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setSelectedLesson(null); }}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
                      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-950" />
                          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Lesson Details</h4>
                        </div>
                        <button onClick={() => setSelectedLesson(null)} className="text-slate-400 hover:text-slate-900 transition-colors"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="p-6 space-y-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold bg-slate-100 text-slate-800 px-2 py-0.5 rounded border border-slate-200 uppercase tracking-wider">{selectedLesson.className || selectedLesson.subject}</span>
                          <span className="text-[10px] text-slate-500 uppercase tracking-wider">{formatDateLabel(selectedLesson.date)}</span>
                          <span className="text-[10px] text-slate-500 uppercase tracking-wider">{selectedLesson.startTime || '--:--'} - {selectedLesson.endTime || '--:--'}</span>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Topic</p>
                          <p className={`text-sm font-bold ${isLessonTopicPlaceholder(selectedLesson.topic) ? 'text-slate-400 italic' : 'text-slate-900'}`}>{getLessonTopicLabel(selectedLesson.topic)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Notes For Students</p>
                          {selectedLesson.notesForStudents ? (
                            <div className="space-y-2 text-xs text-slate-700">{renderMarkdown(selectedLesson.notesForStudents)}</div>
                          ) : (
                            <p className="text-xs text-slate-700">No student notes provided.</p>
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Attachments</p>
                          {(selectedLesson.attachments || []).length === 0 ? (
                            <p className="text-xs text-slate-500">No attachments uploaded.</p>
                          ) : (
                            <div className="space-y-1">
                              {selectedLesson.attachments.map((file, index) => (
                                <a key={`${file.url}-${index}`} href={file.url} target="_blank" rel="noreferrer" className="block text-xs text-blue-600 hover:text-blue-800 underline break-all">
                                  {file.name}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        {(() => {
                          const ticket = sanitizeExitTicketConfig(selectedLesson.exitTicket);
                          if (!ticket.enabled) return null;

                          const savedSubmission = exitTicketResponses[selectedLesson.id] || null;
                          const savedByQuestion = new Map((savedSubmission?.answers || []).map((item) => [item.questionId, item]));
                          const draft = exitTicketDrafts[selectedLesson.id] || { answers: {}, followUps: {}, drawings: {}, reflections: {} };

                          return (
                            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                              <div>
                                <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">{ticket.title || 'Exit Ticket'}</p>
                                {ticket.instructions ? <div className="mt-1 space-y-1 text-xs text-amber-900">{renderMarkdown(ticket.instructions)}</div> : null}
                                {savedSubmission ? (
                                  <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wider mt-1">
                                    Submitted | Score {savedSubmission.correctCount}/{savedSubmission.totalQuestions} ({savedSubmission.scorePercent}%)
                                  </p>
                                ) : null}
                              </div>

                              <div className="space-y-3">
                                {ticket.questions.map((question, index) => {
                                  const saved = savedByQuestion.get(question.id);
                                  const answerValue = draft.answers?.[question.id] ?? saved?.answer ?? '';
                                  const drawingValue = draft.drawings?.[question.id] ?? saved?.drawingDataUrl ?? '';
                                  const allowDrawing = question.type === 'short' && Boolean(question.allowDrawing);
                                  const isAnswered = Boolean(String(answerValue || '').trim() || (allowDrawing && String(drawingValue || '').trim()));
                                  const isCorrect = isAnswered ? isExitTicketQuestionCorrect(question, answerValue) : false;
                                  const savedIsCorrect = saved ? getExitTicketAnswerFinalCorrect(saved) : false;
                                  const followUpQuestion = saved?.followUpQuestion || question.followUpQuestion || '';
                                  const showFollowUp = saved
                                    ? Boolean(followUpQuestion && !savedIsCorrect)
                                    : Boolean(isAnswered && !isCorrect && followUpQuestion);
                                  const followUpValue = draft.followUps?.[question.id] ?? saved?.followUpAnswer ?? '';
                                  const canSubmitSavedFollowUp = Boolean(savedSubmission && saved && followUpQuestion && !savedIsCorrect && !saved.followUpAnswer);

                                  return (
                                    <div key={question.id} className="rounded border border-amber-200 bg-white p-3 space-y-2">
                                      <p className="text-[10px] font-bold text-amber-900 uppercase tracking-wider">Question {index + 1} ({question.type.toUpperCase()})</p>
                                      <div className="space-y-1 text-sm text-slate-900 leading-relaxed">{renderMarkdown(question.prompt)}</div>

                                      {question.type === 'mcq' ? (
                                        <div className="space-y-1.5">
                                          {(question.options || []).map((option, optionIndex) => (
                                            <label key={`${question.id}-choice-${optionIndex}`} className="flex items-start gap-2 text-xs text-slate-700">
                                              <input
                                                type="radio"
                                                name={`lesson-${selectedLesson.id}-question-${question.id}`}
                                                checked={String(answerValue) === String(option)}
                                                onChange={() => handleExitTicketAnswerChange(selectedLesson.id, question.id, option)}
                                                disabled={Boolean(savedSubmission)}
                                              />
                                              <span className="min-w-0 flex-1 space-y-1">{renderMarkdown(option)}</span>
                                            </label>
                                          ))}
                                        </div>
                                      ) : question.type === 'short' ? (
                                        <div className="space-y-2">
                                          <textarea
                                            rows="2"
                                            value={answerValue}
                                            onChange={(e) => handleExitTicketAnswerChange(selectedLesson.id, question.id, e.target.value)}
                                            disabled={Boolean(savedSubmission)}
                                            className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none resize-none"
                                            placeholder="Type your answer"
                                          />
                                          {allowDrawing ? (
                                            <div>
                                              <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Draw your answer</p>
                                              <ExitTicketDrawingCanvas
                                                value={drawingValue}
                                                onChange={(nextValue) => handleExitTicketDrawingChange(selectedLesson.id, question.id, nextValue)}
                                                disabled={Boolean(savedSubmission)}
                                              />
                                            </div>
                                          ) : null}
                                        </div>
                                      ) : (
                                        <input
                                          type="text"
                                          value={answerValue}
                                          onChange={(e) => handleExitTicketAnswerChange(selectedLesson.id, question.id, e.target.value)}
                                          disabled={Boolean(savedSubmission)}
                                          className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none"
                                          placeholder="Type your answer"
                                        />
                                      )}

                                      {saved ? (
                                        <p className={`text-[11px] font-semibold ${getExitTicketAnswerFinalCorrect(saved) ? 'text-emerald-700' : 'text-amber-700'}`}>
                                          {getExitTicketFeedbackText(saved)}
                                        </p>
                                      ) : null}

                                      {showFollowUp ? (
                                        <div>
                                          <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Follow-up for support</p>
                                          <p className="text-xs text-slate-700 mb-1.5">{followUpQuestion}</p>
                                          <textarea
                                            rows="2"
                                            value={followUpValue}
                                            onChange={(e) => handleExitTicketFollowUpChange(selectedLesson.id, question.id, e.target.value)}
                                            disabled={Boolean(savedSubmission) && !canSubmitSavedFollowUp}
                                            className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none resize-none"
                                            placeholder="Write your follow-up response"
                                          />
                                          {canSubmitSavedFollowUp ? (
                                            <button
                                              type="button"
                                              onClick={() => handleSubmitExitTicketFollowUp(selectedLesson, savedSubmission, saved, followUpValue)}
                                              className="mt-1.5 rounded bg-amber-600 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-amber-700"
                                            >
                                              Submit Follow-up
                                            </button>
                                          ) : saved?.followUpAnswer ? (
                                            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Follow-up submitted</p>
                                          ) : null}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>

                              {ticket.reflectionEnabled ? (
                              <div className="rounded border border-amber-200 bg-white p-3 space-y-2">
                                <div>
                                  <p className="text-[10px] font-bold text-amber-900 uppercase tracking-wider">Lesson Reflection</p>
                                  <p className="text-[10px] text-slate-500">Optional — leave any or all of these blank.</p>
                                </div>
                                {Object.entries(normalizeReflectionPrompts(ticket.reflectionPrompts)).map(([field, label]) => (
                                  <label key={field} className="block">
                                    <span className="mb-1 block text-[10px] font-semibold text-slate-700">{label}</span>
                                    <textarea
                                      rows="2"
                                      value={draft.reflections?.[field] ?? savedSubmission?.reflections?.[field] ?? ''}
                                      onChange={(e) => handleExitTicketReflectionChange(selectedLesson.id, field, e.target.value)}
                                      disabled={Boolean(savedSubmission)}
                                      className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none resize-none disabled:bg-slate-50"
                                      placeholder="Optional response"
                                    />
                                  </label>
                                ))}
                              </div>
                              ) : null}

                              {ticket.reflectionEnabled && savedSubmission?.reflectionAiFeedback ? (
                                <div className="rounded border border-indigo-200 bg-indigo-50 p-3 space-y-2">
                                  <p className="text-[10px] font-bold text-indigo-800 uppercase tracking-wider">AI Support & Feedback</p>
                                  {savedSubmission.reflectionAiFeedback.feedback ? (
                                    <p className="text-xs text-slate-700">{savedSubmission.reflectionAiFeedback.feedback}</p>
                                  ) : null}
                                  {(savedSubmission.reflectionAiFeedback.nextSteps || []).length > 0 ? (
                                    <div>
                                      <p className="text-[10px] font-semibold text-indigo-700 mb-1">Suggested next steps</p>
                                      <ul className="list-disc pl-4 space-y-0.5 text-xs text-slate-700">
                                        {savedSubmission.reflectionAiFeedback.nextSteps.map((step, index) => <li key={`reflection-step-${index}`}>{step}</li>)}
                                      </ul>
                                    </div>
                                  ) : null}
                                  {savedSubmission.reflectionAiFeedback.encouragement ? (
                                    <p className="text-xs font-semibold text-indigo-800">{savedSubmission.reflectionAiFeedback.encouragement}</p>
                                  ) : null}
                                  {savedSubmission.reflectionAiFeedback.followUpQuestion ? (
                                    <div className="rounded border border-indigo-200 bg-white px-2.5 py-2">
                                      <p className="text-[9px] font-bold text-indigo-700 uppercase tracking-wider">For your next reflection</p>
                                      <p className="mt-0.5 text-xs text-slate-700">{savedSubmission.reflectionAiFeedback.followUpQuestion}</p>
                                    </div>
                                  ) : null}
                                  <p className="text-[9px] text-slate-500">AI-generated guidance may need teacher review.</p>
                                </div>
                              ) : ticket.reflectionEnabled && savedSubmission?.reflectionAiFeedbackStatus === 'pending' ? (
                                <div className="rounded border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-[10px] text-indigo-700">
                                  Preparing personalized AI support…
                                </div>
                              ) : ticket.reflectionEnabled && savedSubmission?.reflectionAiFeedbackStatus === 'unavailable' ? (
                                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] text-slate-500">
                                  Personalized AI support is currently unavailable; your teacher can still review your responses.
                                </div>
                              ) : null}

                              {!savedSubmission ? (
                                <button
                                  type="button"
                                  onClick={() => handleSubmitExitTicket(selectedLesson)}
                                  className="w-full rounded bg-amber-600 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-amber-700"
                                >
                                  Submit Exit Ticket
                                </button>
                              ) : null}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </main>
        {renderForumLinkModal()}
        {renderForumAiThreadPromptModal()}
        {renderForumAiModal()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans antialiased">
      <style>{`
        @keyframes micro-wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(5deg); }
          75% { transform: rotate(-5deg); }
        }
        @keyframes subtle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes slow-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.95); opacity: 0.5; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(0.95); opacity: 0.5; }
        }
        .anim-wiggle-hover:hover .icon-animate { animation: micro-wiggle 0.5s ease-in-out infinite; }
        .anim-bounce-hover:hover .icon-animate { animation: subtle-bounce 0.6s ease-in-out infinite; }
        .anim-spin-hover:hover .icon-animate { animation: slow-spin 1.5s linear infinite; }
        .anim-pulse-active { animation: pulse-ring 2s infinite ease-in-out; }
        .anim-wiggle-loop { animation: micro-wiggle 1.5s ease-in-out infinite; }
      `}</style>

      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-lg border shadow-sm text-xs transition-all duration-200 ${toast.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-slate-900 text-white border-slate-800'}`}>
          <div className="flex items-center space-x-3">
            {toast.type === 'error' ? (
              <AlertCircle className="w-4 h-4 text-red-600 animate-bounce" />
            ) : (
              <CheckCircle className="w-4 h-4 text-emerald-500 anim-pulse-active" />
            )}
            <span className="font-semibold uppercase tracking-wider">{toast.type === 'error' ? 'Error' : 'Notification'}</span>
            <span className="text-slate-400">|</span>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 border border-slate-950 px-3 py-1 hover:border-indigo-600 transition-colors cursor-pointer group">
              <BookOpen className="w-4 h-4 text-slate-950 group-hover:rotate-6 transition-transform duration-200" />
              <div className="text-xs font-bold uppercase tracking-widest text-slate-950 group-hover:text-indigo-600 transition-colors">
                PHYHUB
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-sm font-bold text-slate-900">Physics Lens</h1>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest">The World in a Different Light</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded text-[10px] font-semibold tracking-wider uppercase bg-slate-100 text-slate-800 border border-slate-200">
              {isSyncing ? (
                <><RefreshCw className="w-3 h-3 animate-spin text-slate-500" /><span>Syncing</span></>
              ) : (
                <><Cloud className="w-3 h-3 text-emerald-600 anim-pulse-active" /><span>Synced</span></>
              )}
            </span>
            <span className="text-[11px] text-slate-400 font-mono hidden md:inline">User: {user?.email || (user?.isAnonymous ? 'Guest' : user?.uid?.substring(0, 8) || 'Unknown')}</span>
            {renderThemeToggle()}
            {user && (
              <button onClick={handleSignOut} className="text-[10px] font-semibold uppercase tracking-wider px-3 py-2 rounded bg-slate-900 text-white hover:bg-slate-950 transition-colors">
                Sign Out
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl w-full mx-auto px-6 py-8 flex flex-col lg:flex-row gap-8">
        <aside className={isSidebarCollapsed ? 'w-full lg:w-20 shrink-0 transition-all duration-300' : 'w-full lg:w-64 shrink-0 transition-all duration-300'}>
          <div className="bg-white rounded-lg border border-slate-200 p-5 sticky top-24 space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className={`px-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider ${isSidebarCollapsed ? 'sr-only' : 'sr-only lg:not-sr-only'}`}>Workspace</p>
                <button
                  type="button"
                  onClick={() => setIsMobileNavigationOpen((current) => !current)}
                  className="w-full flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 lg:hidden"
                  aria-expanded={isMobileNavigationOpen}
                  aria-controls="mobile-main-navigation"
                >
                  <span>Main menu</span>
                  {isMobileNavigationOpen ? <ChevronLeft className="h-3.5 w-3.5 rotate-90" /> : <ChevronRight className="h-3.5 w-3.5 rotate-90" />}
                </button>
                <button
                  type="button"
                  onClick={() => setIsSidebarCollapsed((current) => !current)}
                  className="ml-auto hidden rounded border border-slate-200 p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 lg:block"
                  aria-label={isSidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                  title={isSidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                >
                  {isSidebarCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
                </button>
              </div>
              <nav id="mobile-main-navigation" className={`space-y-1 ${isMobileNavigationOpen ? 'block' : 'hidden'} lg:block`}>
                {[
                  { id: 'dashboard', label: 'Dashboard Hub', icon: Home, hoverClass: 'anim-wiggle-hover' },
                  { id: 'forum', label: 'Live Forum', icon: MessageSquare, hoverClass: 'anim-bounce-hover' },
                  { id: 'users', label: 'User Management', icon: Users, hoverClass: 'anim-spin-hover' },
                  { id: 'lessons', label: 'Lesson Planner', icon: Calendar, hoverClass: 'anim-bounce-hover' },
                  { id: 'exit-tickets', label: 'Exit Tickets', icon: HelpCircle, hoverClass: 'anim-spin-hover' },
                  { id: 'recording', label: 'Recording', icon: Mic, hoverClass: 'anim-bounce-hover' },
                  { id: 'journal', label: 'Daily Journal', icon: CalendarDays, hoverClass: 'anim-spin-hover' },
                  { id: 'attendance', label: 'Attendance Tracker', icon: UserCheck, hoverClass: 'anim-bounce-hover' },
                  { id: 'homework', label: 'Homework Tracker', icon: ClipboardList, hoverClass: 'anim-spin-hover' }
                ].map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setIsMobileNavigationOpen(false);
                      }}
                      className={`w-full flex items-center rounded text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${isSidebarCollapsed ? 'justify-start space-x-3 px-3 py-2 lg:justify-center lg:px-2' : 'space-x-3 px-3 py-2'} ${tab.hoverClass} ${activeTab === tab.id ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'}`}
                      title={isSidebarCollapsed ? tab.label : undefined}
                    >
                      <IconComponent className="h-3.5 w-3.5 icon-animate transition-transform duration-200" />
                      <span className={isSidebarCollapsed ? 'lg:hidden' : ''}>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
            {!isSidebarCollapsed && (
              <div className="pt-5 border-t border-slate-200">
                <p className="px-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Overview</p>
                <div className="space-y-3 px-2">
                  <div className="flex items-center space-x-2.5 group">
                    <Users className="w-3.5 h-3.5 text-slate-400 group-hover:scale-110 transition-transform duration-200" />
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase block leading-none">Total Enrolled</span>
                      <span className="text-xs font-bold text-slate-800">{metrics.totalStudentsCount}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2.5 group">
                    <Award className="w-3.5 h-3.5 text-slate-400 group-hover:rotate-12 transition-transform duration-250" />
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase block leading-none">Attendance Rate</span>
                      <span className="text-xs font-bold text-slate-800">{metrics.currentAttendanceRate}%</span>
                    </div>
                  </div>
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Enrollment by class</p>
                    <div className="mt-2 space-y-1.5">
                      {workspaceOverview.classEnrollment.map((classItem) => (
                        <div key={classItem.id} className="flex items-center justify-between gap-2 text-[10px] text-slate-600">
                          <span className="truncate">{classItem.name}</span>
                          <span className="shrink-0 font-bold text-slate-800">{classItem.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Recent absentees · 7 days</p>
                    {workspaceOverview.recentAbsentees.length > 0 ? (
                      <div className="mt-2 space-y-2">
                        {workspaceOverview.recentAbsentees.slice(0, 5).map((student) => (
                          <div key={`${student.className}-${student.name}`} className="flex items-start justify-between gap-2 text-[10px]">
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-slate-700">{student.name}</p>
                              <p className="truncate text-slate-400">{student.className} · {student.dateLabel}</p>
                            </div>
                            {student.absenceCount > 1 ? <span className="shrink-0 font-bold text-amber-700">×{student.absenceCount}</span> : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-[10px] text-slate-400">No absences recorded.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>
        <main className="flex-1 min-w-0">
          {/* All teacher lesson entry points share the original planner details UI. */}
                {selectedLesson && (() => {
                  const linkedClass = classes.find((c) => c.id === selectedLesson.subject || c.name === selectedLesson.subject);
                  const lessonDetailSectionLabels = ['Teacher Notes', 'Exit Ticket'];
                  const activeLessonSectionIndex = Math.min(selectedLessonSectionIndex, lessonDetailSectionLabels.length - 1);
                  const goToLessonDetailSection = (nextIndex) => {
                    setSelectedLessonSectionIndex(Math.max(0, Math.min(nextIndex, lessonDetailSectionLabels.length - 1)));
                  };
                  return (
                    <div className="fixed inset-0 z-50 flex bg-black/40 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setSelectedLesson(null); }}>
                      <div className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-950" />
                            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Lesson Details</h4>
                          </div>
                          <button onClick={() => setSelectedLesson(null)} className="text-slate-400 hover:text-slate-900 transition-colors"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 space-y-4 overflow-y-auto p-6">
                          <div className="flex flex-col items-start gap-1">
                            <span className="text-[10px] font-bold bg-slate-100 text-slate-800 px-2 py-0.5 rounded border border-slate-200 uppercase tracking-wider">{selectedLesson.subject}</span>
                            <span className="whitespace-nowrap text-[10px] text-slate-500">{selectedLesson.startTime || '--:--'} - {selectedLesson.endTime || '--:--'}</span>
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">{formatDateLabel(selectedLesson.date)}</span>
                          </div>
                          {selectedLesson.calendarSource === 'google-calendar' && (
                            <div>
                              <label htmlFor="calendar-lesson-class" className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Assign to class</label>
                              <select
                                id="calendar-lesson-class"
                                value={selectedLesson.classId || ''}
                                onChange={(event) => handleAssignCalendarLessonClass(selectedLesson, event.target.value)}
                                className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none"
                              >
                                <option value="">Unassigned</option>
                                {classes.map((cls) => <option key={cls.id} value={cls.id}>{cls.name}</option>)}
                              </select>
                              <p className="mt-1 text-[10px] text-slate-500">This applies to all imported lessons with this title.</p>
                            </div>
                          )}
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                            <div className="flex items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => goToLessonDetailSection(activeLessonSectionIndex - 1)}
                                disabled={activeLessonSectionIndex === 0}
                                className="p-1.5 rounded text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Previous lesson section"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <div className="flex flex-wrap justify-center gap-1">
                                {lessonDetailSectionLabels.map((label, index) => (
                                  <button
                                    key={label}
                                    type="button"
                                    onClick={() => goToLessonDetailSection(index)}
                                    className={`px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider transition-colors ${activeLessonSectionIndex === index ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                                    aria-current={activeLessonSectionIndex === index ? 'page' : undefined}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                              <button
                                type="button"
                                onClick={() => goToLessonDetailSection(activeLessonSectionIndex + 1)}
                                disabled={activeLessonSectionIndex === lessonDetailSectionLabels.length - 1}
                                className="p-1.5 rounded text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Next lesson section"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="mt-1 text-center text-[9px] font-bold uppercase tracking-wider text-slate-400">
                              Section {activeLessonSectionIndex + 1} of {lessonDetailSectionLabels.length}
                            </p>
                          </div>
                          <div className="min-h-[260px] space-y-4 pr-1">
                            {activeLessonSectionIndex === 0 && (
                              <TeacherNotes
                                key={selectedLesson.id}
                                lesson={selectedLesson}
                                draft={lessonTeacherNoteDrafts[selectedLesson.id]}
                                onDraftChange={(nextText) => setLessonTeacherNoteDrafts((current) => ({ ...current, [selectedLesson.id]: nextText }))}
                                onSave={() => saveInlineTeacherNotes(selectedLesson)}
                              />
                            )}
                            {activeLessonSectionIndex === 1 && (() => {
                            const ticket = sanitizeExitTicketConfig(selectedLesson.exitTicket);
                            if (!ticket.enabled) {
                              return (
                                <div>
                                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Exit Ticket</p>
                                  <p className="text-xs text-slate-500">No exit ticket configured for this lesson.</p>
                                </div>
                              );
                            }

                            const lessonSubmissions = teacherExitTicketSubmissions
                              .filter((item) => item.lessonId === selectedLesson.id || item.entryId === getStudentPlannerEntryId(user.uid, selectedLesson.id))
                              .sort((a, b) => Number(b.submittedAt || 0) - Number(a.submittedAt || 0));

                            return (
                              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-3">
                                <div>
                                  <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Exit Ticket: {ticket.title || 'Exit Ticket'}</p>
                                  <p className="text-[10px] text-amber-700 mt-1">{ticket.questions.length} question(s) configured.</p>
                                </div>

                                <div className="space-y-1">
                                  {ticket.questions.map((question, index) => (
                                    <div key={question.id} className="text-xs text-slate-700">
                                      <span className="font-semibold text-slate-900">Q{index + 1}.</span>
                                      <div className="mt-1 space-y-1">{renderMarkdown(question.prompt)}</div>
                                    </div>
                                  ))}
                                </div>

                                <div className="border-t border-amber-200 pt-2">
                                  <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Student Responses</p>
                                  {lessonSubmissions.length === 0 ? (
                                    <p className="text-xs text-slate-500">No submissions yet.</p>
                                  ) : (
                                    (() => {
                                      const questionStats = ticket.questions.map((question) => {
                                        let attempts = 0;
                                        let correct = 0;
                                        lessonSubmissions.forEach((submission) => {
                                          const answerItem = (submission.answers || []).find((item) => item.questionId === question.id);
                                          if (!answerItem) return;
                                          attempts += 1;
                                          if (getExitTicketAnswerFinalCorrect(answerItem)) {
                                            correct += 1;
                                          }
                                        });
                                        return {
                                          question,
                                          attempts,
                                          correct,
                                          accuracy: attempts > 0 ? Math.round((correct / attempts) * 100) : 0
                                        };
                                      });

                                      const avgScore = lessonSubmissions.length > 0
                                        ? Math.round(lessonSubmissions.reduce((sum, submission) => sum + Number(submission.scorePercent || 0), 0) / lessonSubmissions.length)
                                        : 0;
                                      const pendingManualCount = lessonSubmissions.reduce((sum, submission) => {
                                        const localPending = (submission.answers || []).filter(
                                          (answerItem) => answerItem.needsManualReview && answerItem.manualReviewStatus !== 'completed'
                                        ).length;
                                        return sum + localPending;
                                      }, 0);

                                      return (
                                        <div className="space-y-3">
                                          <div className="grid grid-cols-3 gap-2">
                                            <div className="rounded border border-amber-200 bg-white px-2 py-1.5">
                                              <p className="text-[9px] uppercase tracking-wider text-slate-500">Submitted</p>
                                              <p className="text-sm font-bold text-slate-900">{lessonSubmissions.length}</p>
                                            </div>
                                            <div className="rounded border border-amber-200 bg-white px-2 py-1.5">
                                              <p className="text-[9px] uppercase tracking-wider text-slate-500">Avg Score</p>
                                              <p className="text-sm font-bold text-slate-900">{avgScore}%</p>
                                            </div>
                                            <div className="rounded border border-amber-200 bg-white px-2 py-1.5">
                                              <p className="text-[9px] uppercase tracking-wider text-slate-500">Pending Manual</p>
                                              <p className="text-sm font-bold text-slate-900">{pendingManualCount}</p>
                                            </div>
                                          </div>

                                          <div className="rounded border border-amber-200 bg-white p-2 space-y-1">
                                            <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Question Accuracy</p>
                                            {questionStats.map((stat, index) => (
                                              <div key={stat.question.id} className="flex items-center justify-between gap-2 text-[10px]">
                                                <span className="text-slate-700">Q{index + 1} ({stat.question.type})</span>
                                                <span className="font-semibold text-slate-900">{stat.accuracy}% ({stat.correct}/{stat.attempts || 0})</span>
                                              </div>
                                            ))}
                                          </div>

                                          {(() => {
                                            const dashboardState = reflectionDashboardByLesson[selectedLesson.id] || {};
                                            const reflectedCount = lessonSubmissions.filter((submission) => (
                                              submission.reflections && Object.values(submission.reflections).some((value) => String(value || '').trim())
                                            )).length;
                                            const sections = [
                                              ['learningHighlights', 'Learning Highlights', 'text-emerald-700'],
                                              ['informationRequests', 'More Information Requested', 'text-blue-700'],
                                              ['revisitTopics', 'Topics to Revisit', 'text-rose-700']
                                            ];
                                            return (
                                              <div className="rounded border border-indigo-200 bg-indigo-50/50 p-3 space-y-3">
                                                <div className="flex items-start justify-between gap-3">
                                                  <div>
                                                    <p className="text-[10px] font-bold text-indigo-800 uppercase tracking-wider">AI Reflection Dashboard</p>
                                                    <p className="text-[10px] text-slate-600 mt-0.5">{reflectedCount} of {lessonSubmissions.length} submitted students included reflections.</p>
                                                  </div>
                                                  <button
                                                    type="button"
                                                    disabled={dashboardState.loading || reflectedCount === 0}
                                                    onClick={() => handleGenerateReflectionDashboard(selectedLesson, lessonSubmissions)}
                                                    className="shrink-0 rounded bg-indigo-600 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider text-white hover:bg-indigo-700 disabled:opacity-50"
                                                  >
                                                    {dashboardState.loading ? 'Analyzing…' : dashboardState.analysis ? 'Refresh Summary' : 'Generate Summary'}
                                                  </button>
                                                </div>

                                                {dashboardState.analysis ? (
                                                  <div className="space-y-3">
                                                    <p className="text-xs text-slate-700">{dashboardState.analysis.overview}</p>
                                                    <div className="grid gap-2 md:grid-cols-3">
                                                      {sections.map(([field, title, titleClass]) => (
                                                        <div key={field} className="rounded border border-indigo-100 bg-white p-2">
                                                          <p className={`text-[9px] font-bold uppercase tracking-wider ${titleClass}`}>{title}</p>
                                                          {(dashboardState.analysis[field] || []).length > 0 ? (
                                                            <div className="mt-1.5 space-y-1.5">
                                                              {dashboardState.analysis[field].map((item, index) => (
                                                                <div key={`${field}-${index}`} className="text-[10px] text-slate-700">
                                                                  <p className="font-semibold text-slate-900">{item.theme} <span className="font-normal text-slate-500">({item.count})</span></p>
                                                                  {item.detail ? <p>{item.detail}</p> : null}
                                                                </div>
                                                              ))}
                                                            </div>
                                                          ) : <p className="mt-1 text-[10px] text-slate-500">No clear themes identified.</p>}
                                                        </div>
                                                      ))}
                                                    </div>
                                                    {(dashboardState.analysis.recommendedActions || []).length > 0 ? (
                                                      <div className="rounded border border-indigo-100 bg-white p-2">
                                                        <p className="text-[9px] font-bold text-indigo-700 uppercase tracking-wider">Recommended Teacher Actions</p>
                                                        <ul className="mt-1 list-disc pl-4 space-y-0.5 text-[10px] text-slate-700">
                                                          {dashboardState.analysis.recommendedActions.map((action, index) => <li key={`reflection-action-${index}`}>{action}</li>)}
                                                        </ul>
                                                      </div>
                                                    ) : null}
                                                    <p className="text-[9px] text-slate-500">AI-generated summary; review against the student responses below.</p>
                                                  </div>
                                                ) : (
                                                  <p className="text-[10px] text-slate-500">Generate a private, anonymous thematic summary for this lesson.</p>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                                            {lessonSubmissions.map((submission) => (
                                              <div key={submission.id} className="rounded border border-amber-200 bg-white p-2 space-y-2">
                                                <div className="flex items-center justify-between gap-2">
                                                  <p className="text-[11px] font-semibold text-slate-900">{submission.studentName || submission.studentUid}</p>
                                                  <p className="text-[10px] text-slate-600">{submission.correctCount}/{submission.totalQuestions} ({submission.scorePercent}%)</p>
                                                </div>

                                                {submission.reflections && Object.values(submission.reflections).some((value) => String(value || '').trim()) ? (
                                                  <div className="rounded border border-indigo-100 bg-indigo-50/60 p-2 space-y-1">
                                                    <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider">Lesson Reflection</p>
                                                    {Object.entries(normalizeReflectionPrompts(submission.reflectionPrompts)).map(([field, label]) => submission.reflections?.[field] ? (
                                                      <p key={field} className="text-[10px] text-slate-700"><span className="font-semibold">{label}:</span> {submission.reflections[field]}</p>
                                                    ) : null)}
                                                  </div>
                                                ) : null}

                                                {(submission.answers || []).map((answerItem) => {
                                                  if (answerItem.type !== 'short') return null;
                                                  const questionConfig = ticket.questions.find((question) => question.id === answerItem.questionId);
                                                  const loadingKey = `${submission.id}:${answerItem.questionId}`;
                                                  const aiLoading = Boolean(exitTicketAiGradingLoading[loadingKey]);
                                                  return (
                                                    <div key={`${submission.id}-${answerItem.questionId}`} className="rounded border border-slate-200 bg-slate-50 p-2 space-y-1.5">
                                                      <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">Short Answer Review</p>
                                                      <p className="text-[10px] text-slate-600"><span className="font-semibold">Prompt:</span> {answerItem.prompt}</p>
                                                      <p className="text-[10px] text-slate-600"><span className="font-semibold">Student:</span> {answerItem.answer || 'No response'}</p>
                                                      {answerItem.drawingDataUrl ? (
                                                        <div>
                                                          <div className="mb-1 flex items-center justify-between gap-2">
                                                            <p className="text-[10px] font-semibold text-slate-600">Student drawing:</p>
                                                            <button
                                                              type="button"
                                                              onClick={() => setExpandedSubmissionImage({ src: answerItem.drawingDataUrl, alt: `Drawing by ${submission.studentName || 'student'}` })}
                                                              className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-slate-600 hover:border-slate-400 hover:text-slate-900"
                                                            >
                                                              <Maximize2 className="h-3 w-3" /> Expand
                                                            </button>
                                                          </div>
                                                          <button type="button" onClick={() => setExpandedSubmissionImage({ src: answerItem.drawingDataUrl, alt: `Drawing by ${submission.studentName || 'student'}` })} className="block w-full cursor-zoom-in">
                                                            <img src={answerItem.drawingDataUrl} alt="Student drawn response" className="max-h-48 w-full rounded border border-slate-200 bg-white object-contain" />
                                                          </button>
                                                        </div>
                                                      ) : null}
                                                      {answerItem.followUpAnswer ? (
                                                        <p className="text-[10px] text-slate-600"><span className="font-semibold">Follow-up:</span> {answerItem.followUpAnswer}</p>
                                                      ) : null}
                                                      {answerItem.followUpQuestion ? (
                                                        <p className="text-[10px] text-slate-600"><span className="font-semibold">AI follow-up question:</span> {answerItem.followUpQuestion}</p>
                                                      ) : null}

                                                      <div className="flex items-center gap-2">
                                                        <button
                                                          type="button"
                                                          onClick={() => handleTeacherUpdateExitTicketAnswer(submission, answerItem.questionId, {
                                                            manualIsCorrect: true,
                                                            manualReviewStatus: 'completed'
                                                          })}
                                                          className={`rounded px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${answerItem.manualIsCorrect === true ? 'bg-emerald-600 text-white' : 'bg-white border border-emerald-300 text-emerald-700'}`}
                                                        >
                                                          Mark Correct
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() => handleTeacherUpdateExitTicketAnswer(submission, answerItem.questionId, {
                                                            manualIsCorrect: false,
                                                            manualReviewStatus: 'completed'
                                                          })}
                                                          className={`rounded px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${answerItem.manualIsCorrect === false ? 'bg-red-600 text-white' : 'bg-white border border-red-300 text-red-700'}`}
                                                        >
                                                          Mark Incorrect
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() => handleTeacherAiGradeShortAnswer({ submission, answerItem, questionConfig })}
                                                          disabled={aiLoading}
                                                          className="rounded border border-amber-300 bg-white px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 hover:bg-amber-100 disabled:opacity-60"
                                                        >
                                                          {aiLoading ? 'AI Feedback...' : 'AI Feedback + Apply'}
                                                        </button>
                                                      </div>

                                                      <textarea
                                                        key={`${answerItem.questionId}-${answerItem.teacherFeedback || answerItem.aiSuggestion?.feedback || ''}`}
                                                        rows="2"
                                                        defaultValue={answerItem.teacherFeedback || answerItem.aiSuggestion?.feedback || ''}
                                                        onBlur={(e) => handleTeacherUpdateExitTicketAnswer(submission, answerItem.questionId, {
                                                          teacherFeedback: e.target.value
                                                        })}
                                                        className="w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-[10px] text-slate-700"
                                                        placeholder="One-sentence feedback for this response"
                                                      />

                                                      {answerItem.teacherFeedback || answerItem.aiSuggestion?.feedback ? (
                                                        <p className="rounded border border-amber-100 bg-amber-50 px-2 py-1.5 text-[10px] font-semibold text-amber-800">
                                                          {answerItem.teacherFeedback || answerItem.aiSuggestion?.feedback}
                                                        </p>
                                                      ) : null}

                                                      {answerItem.aiSuggestion?.rationale ? (
                                                        <p className="text-[10px] text-slate-500">
                                                          AI rationale: {answerItem.aiSuggestion.rationale}
                                                        </p>
                                                      ) : null}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })()
                                  )}
                                </div>
                              </div>
                            );
                            })()}
                          </div>
                          <div className="pt-2 flex flex-col sm:flex-row gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setLessonForm({
                                  subject: selectedLesson.subject || '',
                                  topic: selectedLesson.topic || '',
                                  date: selectedLesson.date || '',
                                  startTime: selectedLesson.startTime || '',
                                  endTime: selectedLesson.endTime || '',
                                  objectives: selectedLesson.objectives || '',
                                  materials: selectedLesson.materials || '',
                                  notesForStudents: selectedLesson.notesForStudents || '',
                                  notesForTeachers: selectedLesson.notesForTeachers || '',
                                  exitTicketId: selectedLesson.exitTicketId || '',
                                  recurrenceFrequency: selectedLesson.recurrenceFrequency || 'none',
                                  recurrenceEndDate: selectedLesson.recurrenceEndDate || ''
                                });
                                setActiveTab('lessons');
                                setEditingLessonId(selectedLesson.id);
                                setDuplicatingLessonDate('');
                                setLessonUploadFiles([]);
                                setRemovedAttachmentUrls([]);
                                setSelectedLesson(null);
                                setShowLessonModal(true);
                              }}
                              className="flex-1 text-xs font-bold py-2 rounded border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors uppercase tracking-wider"
                            >
                              Edit Lesson Plan
                            </button>
                            <button
                              type="button"
                              onClick={() => { setActiveTab('lessons'); openDuplicateLessonModal(selectedLesson); }}
                              className="flex-1 text-xs font-bold py-2 rounded border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors uppercase tracking-wider flex items-center justify-center gap-1"
                            >
                              <Copy className="w-3.5 h-3.5" />
                              <span>Duplicate</span>
                            </button>
                            <button
                              type="button"
                              disabled={!linkedClass}
                              onClick={() => {
                                if (!linkedClass) return;
                                setSelectedClassId(linkedClass.id);
                                setAttendanceDate(selectedLesson.date || getTodayDateValue());
                                setActiveTab('attendance');
                                setSelectedLesson(null);
                              }}
                              className="flex-1 text-xs font-bold py-2 rounded border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Open in Attendance Tracker
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm('Delete this lesson plan?')) return;
                                await handleDeleteLesson(selectedLesson.id);
                                setSelectedLesson(null);
                              }}
                              className="flex-1 text-xs font-bold py-2 rounded bg-red-600 hover:bg-red-700 text-white transition-colors uppercase tracking-wider flex items-center justify-center gap-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Delete Plan</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
          {activeTab === 'recording' && <RecordingWorkspace key={user?.uid} userId={user?.uid} />}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <div className="bg-slate-900 text-white rounded-lg p-6 md:p-8 border border-slate-800 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <BookOpen className="w-48 h-48 animate-pulse" />
                </div>
                <h2 className="text-xl font-bold uppercase tracking-wider relative z-10">Classroom Administration</h2>
                <p className="mt-2 text-slate-400 text-xs max-w-xl leading-relaxed relative z-10">Real-time database sync is active. Updates published below instantly propagate to the student view.</p>
                <div className="mt-8 grid grid-cols-3 gap-4 border-t border-slate-800 pt-6 relative z-10">
                  <div className="group cursor-pointer">
                    <span className="text-[10px] text-slate-500 uppercase block tracking-wider group-hover:text-indigo-400 transition-colors">Classes</span>
                    <span className="text-base font-bold text-white group-hover:translate-x-1 inline-block transition-transform duration-200">{metrics.totalClassesCount}</span>
                  </div>
                  <div className="group cursor-pointer">
                    <span className="text-[10px] text-slate-500 uppercase block tracking-wider group-hover:text-indigo-400 transition-colors">Schedule</span>
                    <span className="text-base font-bold text-white group-hover:translate-x-1 inline-block transition-transform duration-200">{todayScheduleItems.length} Today</span>
                  </div>
                  <div className="group cursor-pointer">
                    <span className="text-[10px] text-slate-500 uppercase block tracking-wider group-hover:text-indigo-400 transition-colors">Announcements</span>
                    <span className="text-base font-bold text-white group-hover:translate-x-1 inline-block transition-transform duration-200">{sortedNotes.length} Active</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                <div className="xl:col-span-7 bg-white rounded-lg border border-slate-200 p-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-150 pb-3">
                    <div className="flex items-center space-x-2 group cursor-pointer">
                      <Calendar className="w-4 h-4 text-slate-950 group-hover:scale-110 transition-transform duration-200" />
                      <div>
                        <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Schedule of the Day</h3>
                        <p className="text-[10px] text-slate-400">Today&apos;s lessons and additional events in one timeline</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {todayScheduleItems.length === 0 ? (
                      <p className="text-center text-slate-400 py-12 text-xs">No active events scheduled.</p>
                    ) : (
                      todayScheduleItems.map((item) => (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedLesson(item.lesson)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              setSelectedLesson(item.lesson);
                            }
                          }}
                          className="group flex cursor-pointer items-start space-x-4 rounded border border-slate-200 bg-slate-50 p-3 transition-colors hover:border-slate-400 hover:bg-slate-100/50"
                        >
                          {item.type === 'lesson' ? (
                            <div className="min-w-0 flex-1">
                              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider leading-none text-slate-700">
                                {item.lesson.className || item.lesson.subject || 'Unassigned'}
                              </span>
                              <p className="mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                {item.lesson.startTime || '--:--'} - {item.lesson.endTime || '--:--'}
                              </p>
                              <p className="mt-2 line-clamp-3 text-[10px] leading-snug text-slate-600">{getTeacherNotesExcerpt(item.lesson.notesForTeachers)}</p>
                            </div>
                          ) : (
                            <>
                              <div className="bg-white px-2 py-1 rounded border border-slate-200 text-center min-w-[70px]">
                                <span className="text-[10px] font-bold text-slate-900">{item.time || 'TBA'}</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide">{item.title}</h4>
                                  <span className="bg-slate-200 text-slate-700 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">{item.subtitle}</span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-0.5">{item.description}</p>
                              </div>
                            </>
                          )}
                          {item.type === 'event' ? (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteEvent(item.sourceId);
                              }}
                              className="text-slate-400 hover:text-red-600 transition-colors px-1 self-center hover:scale-110 duration-150"
                              title="Delete Event"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Today&apos;s To-do</h4>
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                        {todayLessonTodos.filter((todo) => todo.done).length}/{todayLessonTodos.length} complete
                      </span>
                    </div>
                    {todayLessonTodos.length === 0 ? (
                      <p className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[10px] uppercase tracking-wider text-slate-400">
                        No tasks for today
                      </p>
                    ) : (
                      <div className="max-h-36 space-y-1.5 overflow-y-auto pr-1">
                        {todayLessonTodos.map((todo) => (
                          <div key={todo.id} className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2">
                            <button
                              type="button"
                              onClick={() => handleToggleLessonTodoDone(todo.id, Boolean(todo.done))}
                              className="shrink-0 text-slate-500 hover:text-slate-900"
                              title="Toggle complete"
                            >
                              {todo.done ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                            </button>
                            <span className={`min-w-0 flex-1 text-xs ${todo.done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{todo.text}</span>
                            <button
                              type="button"
                              onClick={() => handleDeleteLessonTodo(todo.id)}
                              className="shrink-0 text-slate-400 hover:text-red-600"
                              title="Delete task"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        handleAddLessonTodo(todayDateKey);
                      }}
                      className="mt-2 flex gap-2"
                    >
                      <input
                        type="text"
                        value={lessonTodoInputByDate[todayDateKey] || ''}
                        onChange={(event) => setLessonTodoInputByDate((prev) => ({
                          ...prev,
                          [todayDateKey]: event.target.value
                        }))}
                        placeholder="Add a task for today..."
                        aria-label="New task for today"
                        className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none"
                      />
                      <button
                        type="submit"
                        disabled={!(lessonTodoInputByDate[todayDateKey] || '').trim()}
                        className="flex shrink-0 items-center gap-1 rounded bg-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white transition-colors hover:bg-slate-950 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>Add</span>
                      </button>
                    </form>
                  </div>
                  <form onSubmit={handleAddEvent} className="pt-4 border-t border-slate-200 space-y-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Create Event</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" placeholder="Title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className="bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-900 focus:outline-none" required />
                      <input type="text" placeholder="Time (e.g., 10:00 AM)" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} className="bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-900 focus:outline-none" required />
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Detailed description..." value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} className="flex-1 bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-900 focus:outline-none" />
                      <button type="submit" className="bg-slate-900 hover:bg-slate-950 text-white text-xs px-4 py-2 rounded font-bold uppercase tracking-wider flex items-center space-x-1 group"><Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-250" /><span>Publish</span></button>
                    </div>
                  </form>
                </div>
                <div className="xl:col-span-5 bg-white rounded-lg border border-slate-200 p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center space-x-2 border-b border-slate-150 pb-3 mb-4 group cursor-pointer">
                      <Bell className="w-4 h-4 text-slate-950 anim-wiggle-loop" />
                      <div>
                        <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Notice Board</h3>
                        <p className="text-[10px] text-slate-400">Class announcements and pinboard</p>
                      </div>
                    </div>
                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                      {sortedNotes.length === 0 ? (
                        <p className="text-center text-slate-400 py-12 text-xs">No notices posted.</p>
                      ) : (
                        sortedNotes.map((nt) => (
                          <div key={nt.id} className="relative bg-slate-50 border-l-2 border-slate-900 p-3 rounded-r group hover:bg-slate-100/50 transition-colors">
                            <button onClick={() => handleDeleteNote(nt.id)} className="absolute top-2 right-2 text-slate-400 hover:text-red-600 transition-colors hover:rotate-90"><X className="w-3 h-3" /></button>
                            <p className="text-xs text-slate-800 leading-relaxed font-medium">{nt.text}</p>
                            <p className="mt-2 text-[9px] text-slate-400 uppercase tracking-wider">For {formatNoteAudienceSummary(nt.audiences, classes)}</p>
                            <div className="mt-2 flex items-center justify-between text-[9px] text-slate-400 uppercase tracking-wider"><span className="font-semibold">{nt.author}</span><span>{nt.date}</span></div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <form onSubmit={handleAddNote} className="pt-4 border-t border-slate-200 mt-4 space-y-2">
                    <textarea placeholder="Add announcement details..." value={noteText} onChange={(e) => setNoteText(e.target.value)} rows="2" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-900 focus:outline-none" required />
                    <div className="relative">
                      <button type="button" onClick={() => setIsNoteAudienceMenuOpen((current) => !current)} className="w-full flex items-center justify-between bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-900 focus:outline-none">
                        <span className="truncate text-left">Visible to: {formatNoteAudienceSummary(selectedNoteAudiences, classes)}</span>
                        <span className="text-slate-400">{isNoteAudienceMenuOpen ? 'Hide' : 'Choose'}</span>
                      </button>
                      {isNoteAudienceMenuOpen ? (
                        <div className="absolute z-20 mt-2 w-full rounded border border-slate-200 bg-white p-2 shadow-lg space-y-1 max-h-52 overflow-y-auto">
                          {noteAudienceOptions.map((option) => {
                            const isChecked = selectedNoteAudiences.includes(option.id);
                            return (
                              <label key={option.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    setSelectedNoteAudiences((current) => {
                                      if (current.includes(option.id)) {
                                        return current.filter((entry) => entry !== option.id);
                                      }
                                      return [...current, option.id];
                                    });
                                  }}
                                  className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Author / Sign-off" value={noteAuthor} onChange={(e) => setNoteAuthor(e.target.value)} className="bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-900 focus:outline-none w-1/2" />
                      <button type="submit" className="w-1/2 bg-slate-900 hover:bg-slate-950 text-white text-xs py-2 rounded font-bold uppercase tracking-wider flex items-center justify-center space-x-1 group"><Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-250" /><span>Post</span></button>
                    </div>
                  </form>
                </div>
              </div>


            </div>
          )}

          {activeTab === 'forum' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-4 mb-6">
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Live Discussion Forum</h3>
                    <p className="text-[11px] text-slate-400 mt-1">Create rooms, target specific users or classes, and manage real-time threaded discussions.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedForumRoom ? (
                      <button
                        type="button"
                        onClick={openForumAiDashboard}
                        className="inline-flex items-center gap-1 rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        AI Dashboard
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={handleToggleForumExpandAll}
                      className="inline-flex items-center gap-1 rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
                    >
                      {forumExpandAll ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                      {forumExpandAll ? 'Collapse all' : 'Expand all'}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[330px_minmax(0,1fr)] gap-6">
                  <div className="space-y-4">
                    {canManageForumRooms ? (
                      <form onSubmit={handleSaveForumRoom} className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-900">{editingForumRoomId ? 'Edit room' : 'Create room'}</h4>
                          {editingForumRoomId ? (
                            <button type="button" onClick={handleResetForumRoomForm} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900">Cancel</button>
                          ) : null}
                        </div>
                        <input
                          type="text"
                          value={forumRoomForm.title}
                          onChange={(e) => setForumRoomForm((current) => ({ ...current, title: e.target.value }))}
                          placeholder="Room title"
                          className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-slate-500"
                          required
                        />
                        <textarea
                          rows="3"
                          value={forumRoomForm.description}
                          onChange={(e) => setForumRoomForm((current) => ({ ...current, description: e.target.value }))}
                          placeholder="Room description"
                          className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-slate-500"
                        />

                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Visible classes</p>
                          <div className="max-h-28 overflow-y-auto rounded border border-slate-200 bg-white p-2 space-y-1.5">
                            {classes.length === 0 ? (
                              <p className="text-[10px] text-slate-400 uppercase tracking-wider">No classes yet</p>
                            ) : (
                              classes.map((cls) => (
                                <label key={cls.id} className="flex items-center gap-2 text-xs text-slate-700">
                                  <input
                                    type="checkbox"
                                    checked={forumRoomForm.allowedClassIds.includes(cls.id)}
                                    onChange={() => {
                                      setForumRoomForm((current) => {
                                        const hasClass = current.allowedClassIds.includes(cls.id);
                                        return {
                                          ...current,
                                          allowedClassIds: hasClass
                                            ? current.allowedClassIds.filter((entry) => entry !== cls.id)
                                            : [...current.allowedClassIds, cls.id]
                                        };
                                      });
                                    }}
                                    className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900"
                                  />
                                  <span>{cls.name}</span>
                                </label>
                              ))
                            )}
                          </div>
                        </div>

                        <textarea
                          rows="2"
                          value={forumRoomForm.allowedEmails}
                          onChange={(e) => setForumRoomForm((current) => ({ ...current, allowedEmails: e.target.value }))}
                          placeholder="Specific users (emails, comma-separated)"
                          className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-slate-500"
                        />
                        <button type="submit" className="inline-flex w-full items-center justify-center gap-1 rounded bg-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800">
                          <Plus className="w-3.5 h-3.5" />
                          {editingForumRoomId ? 'Update room' : 'Create room'}
                        </button>
                      </form>
                    ) : (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Create room</p>
                        <p className="mt-1 text-xs text-slate-600">Only teacher accounts can create forum rooms.</p>
                      </div>
                    )}

                    <div className="rounded-lg border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Discussion rooms</p>
                      </div>
                      <div className="max-h-[430px] overflow-y-auto divide-y divide-slate-100">
                        {accessibleForumRooms.length === 0 ? (
                          <p className="px-4 py-6 text-center text-xs text-slate-400">No rooms yet.</p>
                        ) : (
                          accessibleForumRooms.map((room) => {
                            const audienceSummary = [
                              ...(room.allowedClassIds || []).map((classId) => forumClassNameById.get(`${room.teacherId || ''}:${classId}`) || classId),
                              ...(room.allowedEmails || [])
                            ];
                            return (
                              <button
                                key={room.roomKey}
                                type="button"
                                onClick={() => {
                                  setSelectedForumRoomId(room.roomKey);
                                  openForumRoomInNewTab(room.roomKey);
                                }}
                                className={`w-full px-4 py-3 text-left transition-colors ${selectedForumRoomId === room.roomKey ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-xs font-bold uppercase tracking-wide truncate">{room.title}</p>
                                    <p className={`mt-1 text-[10px] ${selectedForumRoomId === room.roomKey ? 'text-slate-300' : 'text-slate-500'} line-clamp-2`}>{room.description || 'No description provided.'}</p>
                                    <p className={`mt-1 text-[10px] uppercase tracking-wider ${selectedForumRoomId === room.roomKey ? 'text-slate-300' : 'text-slate-400'}`}>
                                      Audience: {audienceSummary.length > 0 ? audienceSummary.join(', ') : 'All participants'}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <span className={`text-[9px] font-bold uppercase tracking-wider ${selectedForumRoomId === room.roomKey ? 'text-slate-200' : 'text-slate-400'}`}>
                                      {(forumPostsByRoom[room.roomKey] || []).length} posts
                                    </span>
                                    {room.teacherId === user?.uid ? (
                                      <>
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditForumRoom(room);
                                          }}
                                          className={`cursor-pointer ${selectedForumRoomId === room.roomKey ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:text-slate-700'}`}
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </span>
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteForumRoom(room);
                                          }}
                                          className={`cursor-pointer ${selectedForumRoomId === room.roomKey ? 'text-slate-300 hover:text-red-300' : 'text-slate-400 hover:text-red-600'}`}
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </span>
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 min-w-0">
                    {!selectedForumRoom ? (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
                        <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Select a room to open the live forum</p>
                      </div>
                    ) : (
                      <>
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active room</p>
                              <h4 className="mt-1 text-sm font-bold text-slate-900 uppercase tracking-wide">{selectedForumRoom.title}</h4>
                            </div>
                            {selectedForumRoom.teacherId === user?.uid ? (
                              <button
                                type="button"
                                onClick={() => setIsEditingRoomDescription((current) => !current)}
                                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                                title={isEditingRoomDescription ? 'Cancel editing description' : 'Edit description'}
                                aria-label={isEditingRoomDescription ? 'Cancel editing description' : 'Edit description'}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            ) : null}
                          </div>
                          {isEditingRoomDescription && selectedForumRoom.teacherId === user?.uid ? (
                            <div className="mt-2 space-y-2">
                              <textarea
                                rows="3"
                                value={roomDescriptionDraft}
                                onChange={(e) => setRoomDescriptionDraft(e.target.value)}
                                className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:outline-none"
                              />
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsEditingRoomDescription(false);
                                    setRoomDescriptionDraft(selectedForumRoom.description || '');
                                  }}
                                  className="rounded border border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-white"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleSaveSelectedRoomDescription}
                                  className="rounded bg-slate-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="mt-1 text-xs text-slate-600">{selectedForumRoom.description || 'No room description.'}</p>
                          )}
                        </div>

                        {renderForumThreadComposer()}

                        <div className="rounded-lg border border-slate-200 bg-white p-4 overflow-x-auto">
                          {forumThreadRoots.length === 0 ? (
                            <div className="py-10 text-center">
                              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">No threads yet. Post the first one.</p>
                            </div>
                          ) : (
                            <div className="flex items-start gap-4 min-w-full">
                              {forumThreadRoots.map((threadRoot) => (
                                <div key={threadRoot.id} className="min-w-[300px] max-w-[360px] flex-1">
                                  {renderForumPostCard(threadRoot)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="border-b border-slate-150 pb-4 mb-6">
                  <div className="flex items-center space-x-2 group cursor-pointer">
                    <Users className="w-4 h-4 text-slate-950 group-hover:translate-x-0.5 transition-transform" />
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">User Management</h3>
                      <p className="text-sm text-slate-400">Manage class rosters, students, and bulk imports.</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-5 space-y-4">
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center space-x-2"><UserPlus className="w-4 h-4 text-slate-950" /><span>Add Student to Class</span></h4>
                      <form onSubmit={handleAddStudent} className="space-y-3">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Target Class</label>
                          <select value={selectedClassId || activeClass?.id || ''} onChange={(e) => setSelectedClassId(e.target.value)} className="w-full bg-white border border-slate-200 text-slate-800 py-2 px-3 rounded text-xs font-bold focus:outline-none transition-colors" disabled={classes.length === 0}>
                            {classes.length === 0 ? (
                              <option value="">No classes available</option>
                            ) : (
                              classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
                            )}
                          </select>
                        </div>
                        <input type="text" placeholder="Student name" value={studentName} onChange={(e) => setStudentName(e.target.value)} className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required />
                        <input type="email" pattern=".+@(students\.edu\.sg|myhub\.test)" title="Use an email ending in @students.edu.sg or @myhub.test" placeholder="name@students.edu.sg or name@myhub.test" value={studentEmail} onChange={(e) => setStudentEmail(e.target.value)} className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required />
                        <button type="submit" className="w-full bg-slate-900 text-white text-xs font-bold py-2 rounded hover:bg-slate-950 transition-colors uppercase tracking-widest flex items-center justify-center space-x-1 group"><Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-250" /><span>Add Student</span></button>
                      </form>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3">Create Class</h4>
                      <form onSubmit={handleAddClass} className="space-y-3">
                        <input type="text" placeholder="Class name (e.g., Grade 10 Math)" value={newClassName} onChange={(e) => setNewClassName(e.target.value)} className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required />
                        <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2 rounded uppercase tracking-widest">Create Class</button>
                      </form>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3">Bulk Import Students</h4>
                      <p className="text-[10px] text-slate-500 mb-3">CSV format: <span className="font-semibold">email,name,class</span></p>
                      <input type="file" accept=".csv" onChange={handleBulkCsvFile} className="block w-full text-[10px] text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-700 mb-2" />
                      <button type="button" onClick={downloadCsvTemplate} className="w-full text-[10px] font-bold uppercase tracking-widest text-slate-900 px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-100 transition-colors mb-3">Download Template</button>
                      <textarea value={bulkCsvText} onChange={(e) => setBulkCsvText(e.target.value)} rows="4" placeholder="Paste CSV or upload file above" className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none mb-3" />
                      {bulkImportErrors.length > 0 && <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2"><strong>Errors:</strong> {bulkImportErrors.join('; ')}</div>}
                      {bulkImportLog && <div className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-2">{bulkImportLog}</div>}
                      <button onClick={handleBulkImportStudents} className="w-full bg-slate-900 text-white text-xs font-bold py-2 rounded hover:bg-slate-950 transition-colors uppercase tracking-widest">Import Students</button>
                    </div>
                  </div>
                  <div className="lg:col-span-7 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Class Rosters</h4>
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Selected class</label>
                        <select
                          value={selectedClassId || activeClass?.id || ''}
                          onChange={(e) => setSelectedClassId(e.target.value)}
                          className="bg-white border border-slate-200 text-slate-800 py-1.5 px-2.5 rounded text-xs font-bold focus:outline-none"
                          disabled={classes.length === 0}
                        >
                          {classes.length === 0 ? (
                            <option value="">No classes</option>
                          ) : (
                            classes.map((cls) => <option key={cls.id} value={cls.id}>{cls.name}</option>)
                          )}
                        </select>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">{classes.length} classes</span>
                      </div>
                    </div>
                    <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2">
                      {activeClass ? (
                        <div key={activeClass.id} className="border border-slate-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <h5 className="text-xs font-bold text-slate-900 uppercase tracking-wide">{activeClass.name}</h5>
                              <p className="text-[10px] text-slate-400 mt-0.5">{activeClass.students?.length || 0} student{activeClass.students?.length !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded whitespace-nowrap bg-slate-900 text-white">Selected</span>
                              <button type="button" onClick={() => handleDeleteClass(activeClass.id)} className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap">Delete</button>
                            </div>
                          </div>
                          <div className="mb-3 rounded border border-indigo-200 bg-indigo-50 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-800">One-hour class password</p>
                            <p className="mt-0.5 text-[10px] text-indigo-700">Students sign in with their own email and this shared password. Personal passwords are unchanged.</p>
                            {Number(activeClass.temporaryAccessExpiresAt || 0) > Date.now() ? (
                              <p className="mt-1 text-[10px] font-semibold text-emerald-700">Active until {new Date(activeClass.temporaryAccessExpiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                            ) : null}
                            <div className="mt-2 flex gap-2">
                              <input
                                type="password"
                                minLength="8"
                                value={classTemporaryPasswords[activeClass.id] || ''}
                                onChange={(e) => setClassTemporaryPasswords((prev) => ({ ...prev, [activeClass.id]: e.target.value }))}
                                placeholder="At least 8 characters"
                                className="min-w-0 flex-1 rounded border border-indigo-200 bg-white px-2.5 py-1.5 text-xs"
                              />
                              <button
                                type="button"
                                disabled={classTemporaryPasswordLoading === activeClass.id}
                                onClick={() => handleSetClassTemporaryPassword(activeClass)}
                                className="rounded bg-indigo-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
                              >
                                {classTemporaryPasswordLoading === activeClass.id ? 'Setting…' : 'Set for 1 hour'}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            {(activeClass.students || []).map((student) => (
                              <div key={student.id} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-[10px]">
                                <div className="min-w-0 flex-1">
                                  <div className="font-semibold text-slate-900 truncate">{student.name}</div>
                                  <div className="text-slate-400 truncate">{student.email || 'no email'}</div>
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                  <button
                                    type="button"
                                    onClick={() => handleProvisionStudentAccess(activeClass, student)}
                                    disabled={!student.email || studentProvisioningEmail === (student.email || '').trim().toLowerCase()}
                                    className="text-slate-700 hover:text-slate-950 font-semibold uppercase tracking-wider whitespace-nowrap disabled:text-slate-300 disabled:cursor-not-allowed"
                                  >
                                    {studentProvisioningEmail === (student.email || '').trim().toLowerCase() ? 'Sending…' : 'Send activation'}
                                  </button>
                                  <button type="button" onClick={() => { setSelectedClassId(activeClass.id); handleRemoveStudent(activeClass.id, student.id, student.email || ''); }} className="text-red-600 hover:text-red-800 font-semibold uppercase tracking-wider whitespace-nowrap">Remove</button>
                                </div>
                              </div>
                            ))}
                            {(activeClass.students?.length || 0) === 0 && <p className="text-[10px] text-slate-400 uppercase tracking-wider text-center py-2">No students yet</p>}
                          </div>
                        </div>
                      ) : (
                        <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center bg-white">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">No classes yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'exit-tickets' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-200 pb-4 mb-6">
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Exit Ticket Library</h3>
                    <p className="text-sm text-slate-400">Create exit tickets once and organize them by topic for reuse in lessons.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleStartNewExitTicketTemplate}
                    className="inline-flex items-center gap-1 rounded bg-slate-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-950"
                  >
                    <Plus className="w-3.5 h-3.5" /> New Exit Ticket
                  </button>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,420px)_minmax(0,1fr)] gap-6">
                  <form ref={exitTicketEditorRef} onSubmit={handleSaveExitTicketTemplate} className="scroll-mt-4 rounded-lg border border-amber-200 bg-amber-50/50 p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">{selectedExitTicketLibraryId ? 'Edit Exit Ticket' : 'Create Exit Ticket'}</p>
                      {selectedExitTicketLibraryId ? (
                        <button type="button" onClick={handleStartNewExitTicketTemplate} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900">Cancel</button>
                      ) : null}
                    </div>

                    <label className="flex items-start gap-2 rounded border border-violet-200 bg-violet-50 p-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(exitTicketLibraryForm.reflectionOnly)}
                        onChange={(e) => setExitTicketLibraryForm((prev) => ({
                          ...prev,
                          reflectionOnly: e.target.checked,
                          reflectionEnabled: e.target.checked ? true : prev.reflectionEnabled
                        }))}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-[10px] font-bold text-violet-800 uppercase tracking-wider">Reflection-only ticket</span>
                        <span className="mt-0.5 block text-[10px] text-violet-700">A standalone reflection with no quiz questions or curriculum topic.</span>
                      </span>
                    </label>

                    {!exitTicketLibraryForm.reflectionOnly ? <div>
                      <label className="block text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Topic</label>
                      <input
                        ref={exitTicketTopicInputRef}
                        type="text"
                        value={exitTicketLibraryForm.topic}
                        onChange={(e) => setExitTicketLibraryForm((prev) => ({ ...prev, topic: e.target.value }))}
                        className="w-full rounded border border-amber-200 bg-white px-3 py-2 text-xs text-slate-800"
                        placeholder="e.g., Newton's Laws"
                        required
                      />
                    </div> : null}
                    <div>
                      <label className="block text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Title</label>
                      <input
                        type="text"
                        value={exitTicketLibraryForm.title}
                        onChange={(e) => setExitTicketLibraryForm((prev) => ({ ...prev, title: e.target.value }))}
                        className="w-full rounded border border-amber-200 bg-white px-3 py-2 text-xs text-slate-800"
                        placeholder="Exit Ticket 1"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Instructions</label>
                      <textarea
                        rows="2"
                        value={exitTicketLibraryForm.instructions}
                        onChange={(e) => setExitTicketLibraryForm((prev) => ({ ...prev, instructions: e.target.value }))}
                        onPaste={(e) => {
                          handleExitTicketImagePaste(e, ({ markdown, selectionStart, selectionEnd }) => {
                            setExitTicketLibraryForm((prev) => ({
                              ...prev,
                              instructions: insertTextAtSelection(prev.instructions, selectionStart, selectionEnd, markdown)
                            }));
                          });
                        }}
                        className="w-full rounded border border-amber-200 bg-white px-3 py-2 text-xs text-slate-800"
                        placeholder="Optional instructions for students"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">AI Feedback Prompt</label>
                      <textarea
                        rows="3"
                        value={exitTicketLibraryForm.aiSystemPrompt}
                        onChange={(e) => setExitTicketLibraryForm((prev) => ({ ...prev, aiSystemPrompt: e.target.value }))}
                        className="w-full rounded border border-amber-200 bg-white px-3 py-2 text-xs text-slate-800"
                        placeholder="Optional teacher instructions for the AI feedback style, focus, or misconceptions to watch for."
                      />
                    </div>

                    <label className="flex items-start gap-2 rounded border border-indigo-200 bg-indigo-50 p-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(exitTicketLibraryForm.reflectionEnabled)}
                        onChange={(e) => setExitTicketLibraryForm((prev) => ({ ...prev, reflectionEnabled: e.target.checked }))}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-[10px] font-bold text-indigo-800 uppercase tracking-wider">Include lesson reflection</span>
                        <span className="mt-0.5 block text-[10px] text-slate-600">Adds the three optional reflection prompts and lets AI use prior sessions for personalized follow-up.</span>
                      </span>
                    </label>

                    {exitTicketLibraryForm.reflectionEnabled ? (
                      <div className="rounded border border-indigo-200 bg-white p-3 space-y-2">
                        <p className="text-[10px] font-bold text-indigo-800 uppercase tracking-wider">Reflection prompts</p>
                        {Object.entries(DEFAULT_REFLECTION_PROMPTS).map(([field, fallback], index) => (
                          <label key={field} className="block">
                            <span className="mb-1 block text-[10px] font-semibold text-slate-600">Prompt {index + 1}</span>
                            <input
                              type="text"
                              value={exitTicketLibraryForm.reflectionPrompts?.[field] || ''}
                              onChange={(e) => setExitTicketLibraryForm((prev) => ({
                                ...prev,
                                reflectionPrompts: { ...prev.reflectionPrompts, [field]: e.target.value }
                              }))}
                              className="w-full rounded border border-indigo-200 px-2.5 py-2 text-xs text-slate-700"
                              placeholder={fallback}
                            />
                          </label>
                        ))}
                        <p className="text-[10px] text-slate-500">All student responses remain optional.</p>
                      </div>
                    ) : null}

                    {!exitTicketLibraryForm.reflectionOnly ? <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                      {exitTicketLibraryForm.questions.map((question, index) => (
                        <div key={question.id} className="rounded border border-amber-200 bg-white p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Question {index + 1}</p>
                            <button
                              type="button"
                              onClick={() => setExitTicketLibraryForm((prev) => ({
                                ...prev,
                                questions: prev.questions.filter((item) => item.id !== question.id)
                              }))}
                              className="text-[9px] font-bold uppercase tracking-wider text-red-600"
                            >
                              Remove
                            </button>
                          </div>

                          <select
                            value={question.type}
                            onChange={(e) => {
                              const nextType = e.target.value;
                              setExitTicketLibraryForm((prev) => ({
                                ...prev,
                                questions: prev.questions.map((item) => (item.id === question.id
                                  ? {
                                    ...createExitTicketQuestion(nextType),
                                    id: item.id,
                                    prompt: item.prompt,
                                    feedbackCorrect: item.feedbackCorrect,
                                    feedbackIncorrect: item.feedbackIncorrect,
                                    followUpQuestion: item.followUpQuestion,
                                    allowDrawing: nextType === 'short' ? Boolean(item.allowDrawing) : false
                                  }
                                  : item))
                              }));
                            }}
                            className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-800"
                          >
                            <option value="mcq">Multiple Choice</option>
                            <option value="fib">Fill in the Blank</option>
                            <option value="short">Short Answer</option>
                          </select>

                          <textarea
                            rows="2"
                            value={question.prompt}
                            onChange={(e) => setExitTicketLibraryForm((prev) => ({
                              ...prev,
                              questions: prev.questions.map((item) => (item.id === question.id ? { ...item, prompt: e.target.value } : item))
                            }))}
                            onPaste={(e) => {
                              handleExitTicketImagePaste(e, ({ markdown, selectionStart, selectionEnd }) => {
                                setExitTicketLibraryForm((prev) => ({
                                  ...prev,
                                  questions: prev.questions.map((item) => {
                                    if (item.id !== question.id) return item;
                                    return {
                                      ...item,
                                      prompt: insertTextAtSelection(item.prompt, selectionStart, selectionEnd, markdown)
                                    };
                                  })
                                }));
                              });
                            }}
                            className="w-full rounded border border-amber-200 bg-white px-2 py-2 text-xs text-slate-800"
                            placeholder="Question prompt. Paste an image if useful."
                          />
                          {hasMarkdownImage(question.prompt) ? (
                            <div className="rounded border border-amber-100 bg-amber-50/60 p-2 text-xs">
                              {renderMarkdown(question.prompt)}
                            </div>
                          ) : null}

                          {question.type === 'mcq' ? (
                            <div className="space-y-1.5">
                              {(question.options || []).map((option, optionIndex) => (
                                <div key={`${question.id}-opt-${optionIndex}`} className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`template-correct-${question.id}`}
                                    checked={Number(question.correctOptionIndex) === optionIndex}
                                    onChange={() => setExitTicketLibraryForm((prev) => ({
                                      ...prev,
                                      questions: prev.questions.map((item) => (item.id === question.id ? { ...item, correctOptionIndex: optionIndex } : item))
                                    }))}
                                  />
                                  <div className="flex-1 space-y-1">
                                    <textarea
                                      rows="1"
                                      value={option}
                                      onChange={(e) => setExitTicketLibraryForm((prev) => ({
                                        ...prev,
                                        questions: prev.questions.map((item) => {
                                          if (item.id !== question.id) return item;
                                          const nextOptions = [...(item.options || [])];
                                          nextOptions[optionIndex] = e.target.value;
                                          return { ...item, options: nextOptions };
                                        })
                                      }))}
                                      onPaste={(e) => {
                                        handleExitTicketImagePaste(e, ({ markdown, selectionStart, selectionEnd }) => {
                                          setExitTicketLibraryForm((prev) => ({
                                            ...prev,
                                            questions: prev.questions.map((item) => {
                                              if (item.id !== question.id) return item;
                                              const nextOptions = [...(item.options || [])];
                                              nextOptions[optionIndex] = insertTextAtSelection(nextOptions[optionIndex], selectionStart, selectionEnd, markdown);
                                              return { ...item, options: nextOptions };
                                            })
                                          }));
                                        });
                                      }}
                                      className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-800 resize-y"
                                      placeholder={`Option ${optionIndex + 1}. Paste an image if useful.`}
                                    />
                                    {hasMarkdownImage(option) ? (
                                      <div className="rounded border border-amber-100 bg-amber-50/60 p-2 text-xs">
                                        {renderMarkdown(option)}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              <textarea
                                rows="2"
                                value={question.answerKey}
                                onChange={(e) => setExitTicketLibraryForm((prev) => ({
                                  ...prev,
                                  questions: prev.questions.map((item) => (item.id === question.id ? { ...item, answerKey: e.target.value } : item))
                                }))}
                                onPaste={(e) => {
                                  handleExitTicketImagePaste(e, ({ markdown, selectionStart, selectionEnd }) => {
                                    setExitTicketLibraryForm((prev) => ({
                                      ...prev,
                                      questions: prev.questions.map((item) => {
                                        if (item.id !== question.id) return item;
                                        return {
                                          ...item,
                                          answerKey: insertTextAtSelection(item.answerKey, selectionStart, selectionEnd, markdown)
                                        };
                                      })
                                    }));
                                  });
                                }}
                                className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-800"
                                placeholder="Suggested answer or accepted answer(s). Paste an image if useful."
                              />
                              {hasMarkdownImage(question.answerKey) ? (
                                <div className="rounded border border-amber-100 bg-amber-50/60 p-2 text-xs">
                                  {renderMarkdown(question.answerKey)}
                                </div>
                              ) : null}
                              {question.type === 'short' ? (
                                <label className="flex items-center gap-2 rounded border border-amber-100 bg-amber-50/60 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(question.allowDrawing)}
                                    onChange={(e) => setExitTicketLibraryForm((prev) => ({
                                      ...prev,
                                      questions: prev.questions.map((item) => (item.id === question.id ? { ...item, allowDrawing: e.target.checked } : item))
                                    }))}
                                  />
                                  Allow drawn responses
                                </label>
                              ) : null}
                            </div>
                          )}

                          <input
                            type="text"
                            value={question.feedbackCorrect}
                            onChange={(e) => setExitTicketLibraryForm((prev) => ({
                              ...prev,
                              questions: prev.questions.map((item) => (item.id === question.id ? { ...item, feedbackCorrect: e.target.value } : item))
                            }))}
                            className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-800"
                            placeholder="Feedback if correct"
                          />
                          <input
                            type="text"
                            value={question.feedbackIncorrect}
                            onChange={(e) => setExitTicketLibraryForm((prev) => ({
                              ...prev,
                              questions: prev.questions.map((item) => (item.id === question.id ? { ...item, feedbackIncorrect: e.target.value } : item))
                            }))}
                            className="w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-800"
                            placeholder="Feedback if incorrect"
                          />
                        </div>
                      ))}
                    </div> : null}

                    {!exitTicketLibraryForm.reflectionOnly ? <div className="flex gap-2">
                      <button type="button" onClick={() => setExitTicketLibraryForm((prev) => ({ ...prev, questions: [...prev.questions, createExitTicketQuestion('mcq')] }))} className="rounded border border-amber-300 bg-white px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">Add MCQ</button>
                      <button type="button" onClick={() => setExitTicketLibraryForm((prev) => ({ ...prev, questions: [...prev.questions, createExitTicketQuestion('fib')] }))} className="rounded border border-amber-300 bg-white px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">Add Fill-in</button>
                      <button type="button" onClick={() => setExitTicketLibraryForm((prev) => ({ ...prev, questions: [...prev.questions, createExitTicketQuestion('short')] }))} className="rounded border border-amber-300 bg-white px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">Add Short</button>
                    </div> : null}

                    <button type="submit" className="w-full rounded bg-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-950">
                      {selectedExitTicketLibraryId ? 'Update Exit Ticket' : 'Save Exit Ticket'}
                    </button>
                  </form>

                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-[11px] font-bold text-slate-900 uppercase tracking-wider">By Topic</h4>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">{exitTickets.length} total</span>
                    </div>

                    {exitTicketsByTopic.length === 0 ? (
                      <div className="rounded border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">No exit tickets yet</p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-[760px] overflow-y-auto pr-1">
                        {exitTicketsByTopic.map((group) => (
                          <div key={group.topic} className="rounded border border-slate-200 bg-slate-50 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{group.topic}</p>
                            <div className="mt-2 space-y-2">
                              {group.items.map((ticket) => (
                                <div key={ticket.id} className="rounded border border-slate-200 bg-white p-3">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <p className="text-xs font-bold text-slate-900">{ticket.title || 'Untitled'}</p>
                                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{ticket.reflectionOnly ? 'Reflection only' : `${(ticket.questions || []).length} question(s)`}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button type="button" onClick={() => handleEditExitTicketTemplate(ticket)} className="text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900">Edit</button>
                                      <button type="button" onClick={() => handleDeleteExitTicketTemplate(ticket.id)} className="text-[10px] font-bold uppercase tracking-wider text-red-600 hover:text-red-700">Delete</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'lessons' && (() => {
            const monthlyPlannerDayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            const displayedWeekStart = getWeekStartDate(lessonWeekStart);
            const weekDays = Array.from({ length: 5 }, (_, i) => {
              const d = new Date(displayedWeekStart);
              d.setDate(displayedWeekStart.getDate() + i);
              return d;
            });
            const toDateKey = (d) => {
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${y}-${m}-${day}`;
            };
            const formatDateLabel = (dateKey) => {
              if (!dateKey) return '';
              const [y, m, day] = dateKey.split('-').map(Number);
              if (!y || !m || !day) return dateKey;
              return new Date(y, m - 1, day).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
            };
            const formatDayLabel = (dateKey) => {
              if (!dateKey) return '';
              const [y, m, day] = dateKey.split('-').map(Number);
              if (!y || !m || !day) return '';
              return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short' });
            };
            const weekLabel = (() => {
              const s = weekDays[0];
              const e = weekDays[4];
              const mo = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const en = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              return `${mo} – ${en}`;
            })();
            const monthStart = new Date(lessonWeekStart.getFullYear(), lessonWeekStart.getMonth(), 1);
            const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            const calendarDayNames = monthlyPlannerDayNames;
            const monthStartOffset = (monthStart.getDay() + 6) % 7;
            const monthCalendarDays = Array.from({ length: 42 }, (_, index) => {
              const day = new Date(monthStart);
              day.setDate(monthStart.getDate() - monthStartOffset + index);
              return day;
            });
            const listViewWeekSections = Array.from({ length: 10 }, (_, weekOffset) => {
              const sectionStart = new Date(displayedWeekStart);
              sectionStart.setDate(displayedWeekStart.getDate() + (weekOffset * 7));
              const sectionDays = Array.from({ length: 5 }, (_, dayOffset) => {
                const d = new Date(sectionStart);
                d.setDate(sectionStart.getDate() + dayOffset);
                return d;
              });
              const sectionDateKeys = new Set(sectionDays.map((day) => toDateKey(day)));
              const sectionLessons = lessons
                .filter((lesson) => sectionDateKeys.has(lesson.date))
                .sort((a, b) => {
                  const dateDiff = (a.date || '').localeCompare(b.date || '');
                  if (dateDiff !== 0) return dateDiff;
                  return (a.startTime || '99:99').localeCompare(b.startTime || '99:99');
                });
              const sectionStartLabel = sectionDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const sectionEndLabel = sectionDays[4].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              return {
                id: toDateKey(sectionStart),
                title: `${sectionStartLabel} – ${sectionEndLabel}`,
                lessons: sectionLessons
              };
            });
            const getLessonPlannerClassName = (lesson) => {
              if (!lesson.classId) return '';
              if (lesson.calendarSource === 'google-calendar' && !lesson.classAssignedByTeacher) return '';
              return lesson.className || classes.find((cls) => cls.id === lesson.classId)?.name || '';
            };
            const listViewClassNames = Array.from(new Set([
              ...classes.map((cls) => cls.name),
              ...listViewWeekSections.flatMap((section) => section.lessons.map(getLessonPlannerClassName)).filter(Boolean)
            ]));
            const listViewUsesHorizontalScroll = listViewClassNames.length > 5;
            return (
              <div className="space-y-4">
                {/* Header */}
                <div className="bg-white rounded-lg border border-slate-200 px-5 py-4 flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Lesson Planner</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">{lessonPlannerView === 'week' ? '5-day weekly view' : lessonPlannerView === 'month' ? 'Monthly calendar view' : 'Weekly timeline grouped by class columns'}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="inline-flex rounded border border-slate-200 bg-slate-50 p-0.5">
                        <button
                          type="button"
                          onClick={() => setLessonPlannerView('week')}
                          className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded ${lessonPlannerView === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                          Weekly Grid
                        </button>
                        <button
                          type="button"
                          onClick={() => setLessonPlannerView('month')}
                          className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded ${lessonPlannerView === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                          Monthly View
                        </button>
                        <button
                          type="button"
                          onClick={() => setLessonPlannerView('list')}
                          className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded ${lessonPlannerView === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                          Vertical List
                        </button>
                      </div>
                      <button onClick={() => handleAddLessonNoteCard(toDateKey(lessonPlannerView === 'month' ? lessonWeekStart : displayedWeekStart))} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded transition-colors"><Plus className="w-3.5 h-3.5" /><span>New Note</span></button>
                      <button onClick={() => openNewLessonModal()} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-slate-900 hover:bg-slate-950 text-white px-3 py-1.5 rounded transition-colors"><Plus className="w-3.5 h-3.5" /><span>New Plan</span></button>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 flex-wrap sm:justify-end">
                    <button onClick={() => { const d = new Date(lessonPlannerView === 'month' ? lessonWeekStart : displayedWeekStart); if (lessonPlannerView === 'month') { d.setMonth(d.getMonth() - 1); } else { d.setDate(d.getDate() - 7); } setLessonWeekStart(d); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-[11px] font-bold text-slate-700 min-w-[160px] text-center">{lessonPlannerView === 'month' ? monthLabel : weekLabel}</span>
                    <button onClick={() => { const d = new Date(lessonPlannerView === 'month' ? lessonWeekStart : displayedWeekStart); if (lessonPlannerView === 'month') { d.setMonth(d.getMonth() + 1); } else { d.setDate(d.getDate() + 7); } setLessonWeekStart(d); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"><ChevronRight className="w-4 h-4" /></button>
                    <button onClick={() => { const today = new Date(); if (lessonPlannerView === 'month') { today.setDate(1); setLessonWeekStart(today); } else { const day = today.getDay(); const diff = day === 0 ? -6 : 1 - day; const monday = new Date(today); monday.setDate(today.getDate() + diff); monday.setHours(0,0,0,0); setLessonWeekStart(monday); } }} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors">Today</button>
                  </div>
                </div>

                <details className="rounded-lg border border-slate-200 bg-white px-5 py-4">
                  <summary className="cursor-pointer text-xs font-bold text-slate-800">Google Calendar connection</summary>
                  <form className="mt-3 space-y-3" onSubmit={(event) => { event.preventDefault(); if (!isImportingGoogleCalendar) handleImportGoogleCalendarLessons(); }}>
                    <p id="google-calendar-import-help" className="text-xs text-slate-500">Connect the “Secret address in iCal format” from your Google Calendar settings. Events refresh when you open this planner, return to this window, and every five minutes while it is visible. Shows the past 14 days and next 120 days.</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600" aria-live="polite">
                      <span>{isImportingGoogleCalendar ? 'Checking calendar…' : googleCalendarConnected ? `Connected${googleCalendarLastSynced ? ' · Last synced ' + new Date(googleCalendarLastSynced).toLocaleString() : ''}` : 'No calendar connected'}</span>
                      {googleCalendarConnected && <>
                        <button type="button" disabled={isImportingGoogleCalendar} onClick={() => handleImportGoogleCalendarLessons({ refresh: true })} className="font-semibold underline disabled:opacity-50">Refresh now</button>
                        <button type="button" disabled={isImportingGoogleCalendar} onClick={handleDisconnectGoogleCalendar} className="font-semibold underline disabled:opacity-50">Disconnect</button>
                      </>}
                    </div>
                    {googleCalendarSyncError && <p role="alert" className="text-xs text-rose-700">{googleCalendarSyncError}</p>}
                    <label htmlFor="google-calendar-secret-url" className="block text-xs font-semibold text-slate-700">Secret iCal address</label>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        id="google-calendar-secret-url"
                        type="password"
                        autoComplete="off"
                        spellCheck={false}
                        aria-describedby="google-calendar-import-help google-calendar-import-privacy"
                        value={googleCalendarUrl}
                        onChange={(event) => setGoogleCalendarUrl(event.target.value)}
                        disabled={isImportingGoogleCalendar}
                        placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
                        className="min-w-0 flex-1 rounded border border-slate-200 px-3 py-2 text-xs text-slate-800 focus:border-slate-400 focus:outline-none"
                      />
                      <button type="submit" disabled={!googleCalendarUrl.trim() || isImportingGoogleCalendar} className="rounded bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-950 disabled:opacity-50">
                        {isImportingGoogleCalendar ? 'Syncing…' : googleCalendarConnected ? 'Replace calendar' : 'Connect calendar'}
                      </button>
                    </div>
                    <p id="google-calendar-import-privacy" className="text-[11px] text-slate-500">Your secret address is saved privately to your teacher account so the site can fetch updates. It is cleared from this field after connecting.</p>
                  </form>
                </details>

                {lessonPlannerView === 'week' ? (
                  <div className="overflow-x-auto pb-2">
                    <div className="grid min-w-[900px] grid-cols-5 gap-3 lg:min-w-[720px]">
                      {weekDays.map((day) => {
                      const dateStr = toDateKey(day);
                      const isToday = dateStr === toDateKey(new Date());
                      const dayLessons = sortLessonPlannerCards(lessons.filter((l) => l.date === dateStr));
                      const dayTodos = lessonTodos
                        .filter((todo) => todo.date === dateStr)
                        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
                      return (
                        <div key={dateStr} className="bg-white rounded-lg border border-slate-200 flex flex-col min-h-[420px]">
                          {/* Day header */}
                          <div className={`px-3 py-2.5 border-b border-slate-200 rounded-t-lg ${isToday ? 'bg-slate-900' : 'bg-slate-50'}`}>
                            <p className={`text-[9px] font-bold uppercase tracking-widest ${isToday ? 'text-slate-300' : 'text-slate-400'}`}>{day.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                            <p className={`text-base font-bold leading-none mt-0.5 ${isToday ? 'text-white' : 'text-slate-800'}`}>{day.getDate()}</p>
                          </div>
                          {/* Cards */}
                          <div
                            className="flex-1 p-2 space-y-2 overflow-y-auto"
                            onDragOver={(event) => { if (draggingLessonNoteId) event.preventDefault(); }}
                            onDrop={(event) => {
                              if (!draggingLessonNoteId) return;
                              event.preventDefault();
                              handleDropLessonNote(dateStr, dayLessons.filter((lesson) => lesson.id !== draggingLessonNoteId).length);
                            }}
                          >
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => handleAddLessonNoteCard(dateStr)}
                                className="rounded border border-dashed border-slate-200 bg-white px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-slate-500 hover:border-slate-400 hover:text-slate-800"
                              >
                                + Note
                              </button>
                            </div>
                            {dayLessons.length === 0 ? (
                              <button
                                type="button"
                                onClick={() => openNewLessonModal({ date: dateStr })}
                                className="w-full h-full min-h-[120px] flex items-center justify-center rounded border border-dashed border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50 transition-colors"
                                aria-label="Add lesson plan"
                              />
                            ) : (
                              <>
                                {dayLessons.map((les) => {
                                  if (les.cardType === 'note') {
                                    const noteText = lessonNoteDrafts[les.id] ?? les.noteText ?? '';
                                    return (
                                      <div
                                        key={les.id}
                                        onDragOver={(event) => { if (draggingLessonNoteId) event.preventDefault(); }}
                                        onDrop={(event) => {
                                          if (!draggingLessonNoteId) return;
                                          event.preventDefault();
                                          event.stopPropagation();
                                          handleDropLessonNote(dateStr, dayLessons.filter((lesson) => lesson.id !== draggingLessonNoteId).findIndex((lesson) => lesson.id === les.id));
                                        }}
                                        className={`w-full rounded-lg border border-dashed bg-white p-2.5 shadow-sm ${draggingLessonNoteId === les.id ? 'border-slate-500 opacity-50' : 'border-slate-300'}`}
                                      >
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">
                                            <span
                                              draggable
                                              onDragStart={(event) => { event.stopPropagation(); setDraggingLessonNoteId(les.id); }}
                                              onDragEnd={() => setDraggingLessonNoteId('')}
                                              className="cursor-grab rounded p-0.5 hover:bg-slate-100 active:cursor-grabbing"
                                              title="Drag note between lesson cards"
                                              aria-label="Drag note between lesson cards"
                                            ><GripVertical className="h-3.5 w-3.5" /></span>
                                            Note
                                          </span>
                                          <button type="button" onClick={() => handleDeleteLesson(les.id)} className="text-slate-400 hover:text-red-600" title="Delete note">
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                        <LessonNotePreview value={noteText} onChange={(nextText) => handleUpdateLessonNoteCard(les, nextText)} />
                                      </div>
                                    );
                                  }
                                  return (
                                    <div
                                      key={les.id}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setSelectedLesson(les)}
                                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedLesson(les); }}
                                      onDragOver={(event) => { if (draggingLessonNoteId) event.preventDefault(); }}
                                      onDrop={(event) => {
                                        if (!draggingLessonNoteId) return;
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleDropLessonNote(dateStr, dayLessons.filter((lesson) => lesson.id !== draggingLessonNoteId).findIndex((lesson) => lesson.id === les.id));
                                      }}
                                      className="w-full cursor-pointer text-left bg-slate-50 border border-slate-200 rounded-lg p-2.5 relative group hover:border-slate-400 hover:shadow-sm transition-all"
                                    >
                                      <div className="flex flex-col items-start gap-1">
                                        <span className="text-[9px] font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">{les.subject}</span>
                                        <p className="whitespace-nowrap text-[9px] font-bold text-slate-500 uppercase tracking-wider">{les.startTime || '--:--'} - {les.endTime || '--:--'}</p>
                                      </div>
                                      <p className="mt-2 line-clamp-3 text-[11px] leading-relaxed text-slate-600">{getTeacherNotesExcerpt(les.notesForTeachers)}</p>
                                    </div>
                                  );
                                })}
                                <button
                                  type="button"
                                  onClick={() => openNewLessonModal({ date: dateStr })}
                                  className="w-full rounded border border-dashed border-slate-200 bg-white px-2 py-3 hover:border-slate-400 hover:bg-slate-50 transition-colors"
                                  aria-label="Add lesson plan"
                                />
                              </>
                            )}
                          </div>

                          <div
                            className="border-t border-slate-200 bg-slate-50/60 p-2"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => handleDropLessonTodo(dateStr)}
                          >
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">To-do</p>
                            <div className="space-y-1 max-h-36 overflow-y-auto pr-0.5">
                              {dayTodos.map((todo, index) => (
                                <div
                                  key={todo.id}
                                  draggable
                                  onDragStart={() => setDraggingTodoId(todo.id)}
                                  onDragEnd={() => setDraggingTodoId('')}
                                  onDragOver={(e) => e.preventDefault()}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    handleDropLessonTodo(dateStr, index);
                                  }}
                                  className={`flex items-center gap-1.5 rounded border px-1.5 py-1 bg-white ${draggingTodoId === todo.id ? 'border-slate-400 opacity-50' : 'border-slate-200'}`}
                                >
                                  <span className="text-[10px] text-slate-400 cursor-grab">::</span>
                                  <button
                                    type="button"
                                    onClick={() => handleToggleLessonTodoDone(todo.id, Boolean(todo.done))}
                                    className="text-slate-500 hover:text-slate-800"
                                    title="Toggle complete"
                                  >
                                    {todo.done ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                                  </button>
                                  <input
                                    type="text"
                                    value={lessonTodoDrafts[todo.id] ?? todo.text}
                                    onChange={(e) => setLessonTodoDrafts((prev) => ({ ...prev, [todo.id]: e.target.value }))}
                                    onBlur={() => handleSaveLessonTodoText(todo.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSaveLessonTodoText(todo.id);
                                      }
                                    }}
                                    className={`flex-1 min-w-0 w-0 bg-transparent text-[10px] text-slate-700 focus:outline-none ${todo.done ? 'line-through text-slate-400' : ''}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteLessonTodo(todo.id)}
                                    className="text-red-500 hover:text-red-700"
                                    title="Delete todo"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="mt-1.5 flex gap-1">
                              <input
                                type="text"
                                value={lessonTodoInputByDate[dateStr] || ''}
                                onChange={(e) => setLessonTodoInputByDate((prev) => ({ ...prev, [dateStr]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddLessonTodo(dateStr);
                                  }
                                }}
                                placeholder="Add task"
                                className="flex-1 min-w-0 w-0 rounded border border-slate-200 bg-white px-2 py-1 text-[10px] text-slate-700 focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddLessonTodo(dateStr)}
                                className="rounded bg-slate-900 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-white hover:bg-slate-800"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                ) : lessonPlannerView === 'month' ? (
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="overflow-x-auto pb-2">
                      <div className="grid grid-cols-[repeat(7,minmax(180px,1fr))] gap-2 min-w-[1260px]">
                        {calendarDayNames.map((dayName) => (
                          <div key={dayName} className="px-2 py-1 text-center text-[9px] font-bold uppercase tracking-wider text-slate-500">
                            {dayName}
                          </div>
                        ))}
                        {monthCalendarDays.map((day, index) => {
                          const dateStr = toDateKey(day);
                          const isCurrentMonth = day.getMonth() === monthStart.getMonth();
                          const isToday = dateStr === toDateKey(new Date());
                          const dayLessons = sortLessonPlannerCards(lessons.filter((lesson) => lesson.date === dateStr));
                          return (
                            <div key={`${dateStr}-${index}`} className={`min-h-[320px] rounded-lg border p-2 ${isCurrentMonth ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/70'} ${isToday ? 'ring-1 ring-slate-900' : ''}`}>
                              <div className="flex items-center justify-between gap-2">
                                <span className={`text-[10px] font-bold ${isCurrentMonth ? 'text-slate-700' : 'text-slate-400'}`}>{day.getDate()}</span>
                                <button type="button" onClick={() => handleAddLessonNoteCard(dateStr)} className="text-[8px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800">+ Note</button>
                              </div>
                              <div
                                className="mt-2 flex-1 space-y-1.5 overflow-visible pr-0.5"
                                onDragOver={(event) => { if (draggingLessonNoteId) event.preventDefault(); }}
                                onDrop={(event) => {
                                  if (!draggingLessonNoteId) return;
                                  event.preventDefault();
                                  handleDropLessonNote(dateStr, dayLessons.filter((lesson) => lesson.id !== draggingLessonNoteId).length);
                                }}
                              >
                                {dayLessons.map((lesson) => {
                                  const targetIndex = dayLessons.filter((item) => item.id !== draggingLessonNoteId).findIndex((item) => item.id === lesson.id);
                                  if (lesson.cardType === 'note') {
                                      const noteText = lessonNoteDrafts[lesson.id] ?? lesson.noteText ?? '';
                                      return (
                                        <div
                                          key={lesson.id}
                                          onDragOver={(event) => { if (draggingLessonNoteId) event.preventDefault(); }}
                                          onDrop={(event) => {
                                            if (!draggingLessonNoteId) return;
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleDropLessonNote(dateStr, targetIndex);
                                          }}
                                          className={`rounded border border-dashed bg-white px-1.5 py-1.5 text-[9px] leading-tight text-slate-700 shadow-sm ${draggingLessonNoteId === lesson.id ? 'border-slate-500 opacity-50' : 'border-slate-300'}`}
                                        >
                                          <div className="flex items-center justify-between gap-2">
                                            <p className="flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider text-slate-400">
                                              <span
                                                draggable
                                                onDragStart={(event) => { event.stopPropagation(); setDraggingLessonNoteId(lesson.id); }}
                                                onDragEnd={() => setDraggingLessonNoteId('')}
                                                className="cursor-grab rounded p-0.5 hover:bg-slate-100 active:cursor-grabbing"
                                                title="Drag note between lesson cards"
                                                aria-label="Drag note between lesson cards"
                                              ><GripVertical className="h-3 w-3" /></span>
                                              Note
                                            </p>
                                            <button type="button" onClick={() => handleDeleteLesson(lesson.id)} className="text-slate-400 hover:text-red-600" title="Delete note">
                                              <Trash2 className="w-3 h-3" />
                                            </button>
                                          </div>
                                          <LessonNotePreview value={noteText} onChange={(nextText) => handleUpdateLessonNoteCard(lesson, nextText)} />
                                        </div>
                                      );
                                  }
                                  return (
                                  <div
                                    key={lesson.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedLesson(lesson)}
                                    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedLesson(lesson); }}
                                    onDragOver={(event) => { if (draggingLessonNoteId) event.preventDefault(); }}
                                    onDrop={(event) => {
                                      if (!draggingLessonNoteId) return;
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleDropLessonNote(dateStr, targetIndex);
                                    }}
                                    className="rounded border border-slate-200 bg-slate-50 px-1.5 py-1 text-[9px] leading-tight text-slate-700 shadow-sm"
                                  >
                                    <div className="flex flex-col items-start gap-1">
                                      <p className="font-semibold">{lesson.subject || 'Unassigned'}</p>
                                      <p className="whitespace-nowrap text-[8px] text-slate-500">{lesson.startTime || '--:--'} - {lesson.endTime || '--:--'}</p>
                                    </div>
                                    <p className="mt-1 line-clamp-3 text-[10px] leading-relaxed text-slate-600">{getTeacherNotesExcerpt(lesson.notesForTeachers, 90)}</p>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="max-h-[72vh] overflow-y-auto pr-1 space-y-4">
                    {listViewWeekSections.map((section, sectionIndex) => (
                      <div key={section.id} className="bg-white rounded-lg border border-slate-200">
                        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
                          <div>
                            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Week {getWeekOfYear(section.id)}</p>
                            <p className="text-xs font-bold text-slate-900 uppercase tracking-wider mt-0.5">{section.title}</p>
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{section.lessons.length} plans</span>
                        </div>
                        <div className="p-3">
                          <div className={listViewUsesHorizontalScroll ? 'overflow-x-auto pb-2' : ''}>
                            <div className={listViewUsesHorizontalScroll ? 'grid w-max grid-flow-col auto-cols-[15rem] gap-3' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3'}>
                            {listViewClassNames.map((className) => {
                              const classLessons = section.lessons.filter((lesson) => getLessonPlannerClassName(lesson) === className);
                              return (
                                <div key={`${section.id}-${className}`} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 space-y-2">
                                  <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
                                    <p className="text-[10px] font-bold text-slate-900 uppercase tracking-wider truncate">{className}</p>
                                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider shrink-0">{classLessons.length} plans</span>
                                  </div>
                                  <div className="space-y-2 min-h-[120px]">
                                    {classLessons.length === 0 ? (
                                      <button
                                        type="button"
                                        onClick={() => openNewLessonModal({
                                          subject: className === 'Unassigned' ? '' : className,
                                          date: section.id
                                        })}
                                        className="w-full rounded border border-dashed border-slate-200 bg-white px-2 py-3 text-center hover:border-slate-400 hover:bg-slate-50 transition-colors"
                                        aria-label="Add lesson plan"
                                      />
                                    ) : (
                                      <>
                                        {classLessons.map((les) => {
                                          return (
                                            <div
                                              key={les.id}
                                              role="button"
                                              tabIndex={0}
                                              onClick={() => setSelectedLesson(les)}
                                              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedLesson(les); }}
                                              className="w-full cursor-pointer text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-slate-400 hover:shadow-sm transition-all"
                                            >
                                              <div className="min-w-0">
                                                <div className="flex flex-col items-start gap-1">
                                                  <span className="text-[9px] font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">{getLessonPlannerClassName(les)}</span>
                                                  <p className="whitespace-nowrap text-[9px] font-bold text-slate-500 uppercase tracking-wider">{formatDayLabel(les.date)} · {les.startTime || '--:--'} - {les.endTime || '--:--'}</p>
                                                </div>
                                                <p className="mt-2 line-clamp-3 text-[11px] leading-relaxed text-slate-600">{getTeacherNotesExcerpt(les.notesForTeachers)}</p>
                                              </div>
                                            </div>
                                          );
                                        })}
                                        <button
                                          type="button"
                                          onClick={() => openNewLessonModal({
                                            subject: className === 'Unassigned' ? '' : className,
                                            date: section.id
                                          })}
                                          className="w-full rounded border border-dashed border-slate-200 bg-white px-2 py-3 text-center hover:border-slate-400 hover:bg-slate-50 transition-colors"
                                          aria-label="Add lesson plan"
                                        />
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Modal */}
                {showLessonModal && (
                  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex" onClick={(e) => { if (e.target === e.currentTarget) { setShowLessonModal(false); setDuplicatingLessonDate(''); setLessonUploadFiles([]); setRemovedAttachmentUrls([]); } }}>
                    <div className="bg-white shadow-2xl w-full h-full flex flex-col overflow-hidden">
                      <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-slate-200">
                        <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-950" /><h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">{editingLessonId ? 'Edit Lesson Plan' : 'Create Lesson Plan'}</h4></div>
                        <button type="button" aria-label="Close lesson plan editor" onClick={() => { setShowLessonModal(false); setDuplicatingLessonDate(''); setLessonUploadFiles([]); setRemovedAttachmentUrls([]); }} className="text-slate-400 hover:text-slate-900 transition-colors"><X className="w-4 h-4" /></button>
                      </div>
                      <form onSubmit={handleAddLesson} className="flex min-h-0 flex-1 flex-col">
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                        <div className="mx-auto max-w-5xl space-y-6">
                        <section aria-labelledby="lesson-form-0" className="space-y-3">
                          <h5 id="lesson-form-0" className="text-xs font-bold text-slate-900 uppercase tracking-wider">Lesson details</h5>
                          <div className="space-y-4">
                          <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Subject</label>
                          <select value={lessonForm.subject} onChange={(e) => setLessonForm({ ...lessonForm, subject: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required>
                            <option value="">Choose Course Context</option>
                            {lessonForm.subject && !classes.some((c) => c.name === lessonForm.subject) && (
                              <option value={lessonForm.subject}>{lessonForm.subject}</option>
                            )}
                            {classes.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Date</label>
                            <input
                              type="date"
                              value={lessonForm.date}
                              onChange={(e) => {
                                const nextDate = e.target.value;
                                setLessonForm({
                                  ...lessonForm,
                                  date: nextDate,
                                  recurrenceEndDate: lessonForm.recurrenceEndDate && lessonForm.recurrenceEndDate < nextDate ? nextDate : lessonForm.recurrenceEndDate
                                });
                              }}
                              className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Start</label>
                            <input type="time" value={lessonForm.startTime} onChange={(e) => setLessonForm({ ...lessonForm, startTime: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">End</label>
                            <input type="time" value={lessonForm.endTime} onChange={(e) => setLessonForm({ ...lessonForm, endTime: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Topic</label>
                          <input type="text" placeholder="e.g., Trigonometry" value={lessonForm.topic} onChange={(e) => setLessonForm({ ...lessonForm, topic: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required />
                        </div>                          </div>
                        </section>
                        <div>
                            {editingLessonId ? (
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                                                <p className="mt-1 text-xs text-slate-500">This edit applies only to the selected lesson card. Create a new plan to make a weekly or fortnightly set.</p>
                              </div>
                            ) : (
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                                <div>
                                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Repeat</label>
                                  <select
                                    value={lessonForm.recurrenceFrequency || 'none'}
                                    onChange={(e) => {
                                      const nextFrequency = e.target.value;
                                      const duplicateDateOffset = nextFrequency === 'biweekly' ? 14 : 7;
                                      const duplicateStartDate = duplicatingLessonDate && nextFrequency !== 'none'
                                        ? addDaysToDateValue(duplicatingLessonDate, duplicateDateOffset)
                                        : lessonForm.date;
                                      const nextDate = duplicateStartDate || lessonForm.date;
                                      const shouldMoveRepeatEndDate = !lessonForm.recurrenceEndDate
                                        || lessonForm.recurrenceEndDate === lessonForm.date
                                        || lessonForm.recurrenceEndDate < nextDate;
                                      setLessonForm({
                                        ...lessonForm,
                                        date: nextDate,
                                        recurrenceFrequency: nextFrequency,
                                        recurrenceEndDate: nextFrequency === 'none'
                                          ? ''
                                          : (shouldMoveRepeatEndDate ? nextDate : lessonForm.recurrenceEndDate)
                                      });
                                    }}
                                    className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none"
                                  >
                                    <option value="none">Does not repeat</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="biweekly">Fortnightly</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Repeat Until</label>
                                  <input
                                    type="date"
                                    value={lessonForm.recurrenceEndDate || ''}
                                    min={lessonForm.date || undefined}
                                    onChange={(e) => setLessonForm({ ...lessonForm, recurrenceEndDate: e.target.value })}
                                    disabled={(lessonForm.recurrenceFrequency || 'none') === 'none'}
                                    required={(lessonForm.recurrenceFrequency || 'none') !== 'none'}
                                    className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none disabled:opacity-50"
                                  />
                                </div>
                                <p className="text-[10px] text-slate-500">Each occurrence is saved as its own lesson card, so you can delete individual cards later.</p>
                              </div>
                            )}</div>
                        <section aria-labelledby="lesson-form-teacher-notes" className="space-y-3">
                          <h5 id="lesson-form-teacher-notes" className="text-xs font-bold text-slate-900 uppercase tracking-wider">Teacher notes</h5>
                          <textarea aria-labelledby="lesson-form-teacher-notes" placeholder="Private teaching notes, reminders, or facilitation guidance..." value={lessonForm.notesForTeachers} onChange={(e) => setLessonForm({ ...lessonForm, notesForTeachers: e.target.value })} rows="5" className="w-full bg-indigo-50 border border-indigo-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-indigo-600 focus:outline-none resize-y" />
                        </section>
                        <section aria-labelledby="lesson-form-4" className="space-y-3">
                          <h5 id="lesson-form-4" className="text-xs font-bold text-slate-900 uppercase tracking-wider">Exit ticket & attachments</h5>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                          <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Exit Ticket</p>
                          <p className="text-[10px] text-amber-700">Select from your pre-created exit ticket library.</p>
                          <select
                            value={lessonForm.exitTicketId || ''}
                            onChange={(e) => setLessonForm({ ...lessonForm, exitTicketId: e.target.value })}
                            className="w-full bg-white border border-amber-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-amber-500 focus:outline-none"
                          >
                            <option value="">No exit ticket</option>
                            {exitTickets.map((ticket) => (
                              <option key={ticket.id} value={ticket.id}>{ticket.topic || 'Uncategorized'} | {ticket.title || 'Untitled'}</option>
                            ))}
                          </select>
                          {lessonForm.exitTicketId ? (
                            (() => {
                              const selectedTicket = exitTickets.find((ticket) => ticket.id === lessonForm.exitTicketId);
                              if (!selectedTicket) return <p className="text-[10px] text-amber-700">Selected ticket no longer exists. Choose another.</p>;
                              return (
                                <div className="rounded border border-amber-200 bg-white p-2">
                                  <p className="text-[10px] font-semibold text-slate-900">{selectedTicket.title}</p>
                                  <p className="text-[10px] text-slate-500">Topic: {selectedTicket.topic || 'Uncategorized'} | Questions: {(selectedTicket.questions || []).length}</p>
                                </div>
                              );
                            })()
                          ) : null}
                          <button
                            type="button"
                            onClick={() => {
                              setShowLessonModal(false);
                              setEditingLessonId('');
                              setDuplicatingLessonDate('');
                              setLessonUploadFiles([]);
                              setRemovedAttachmentUrls([]);
                              setActiveTab('exit-tickets');
                            }}
                            className="text-[10px] font-bold uppercase tracking-wider text-amber-800 hover:text-amber-900"
                          >
                            Manage Exit Tickets
                          </button>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Attach Files / Images</label>
                          <input
                            type="file"
                            multiple
                            onChange={(e) => setLessonUploadFiles(Array.from(e.target.files || []))}
                            className="block w-full text-[10px] text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-700"
                          />
                          {lessonUploadFiles.length > 0 && (
                            <p className="mt-1 text-[10px] text-slate-500">{lessonUploadFiles.length} file(s) selected</p>
                          )}
                        </div>
                        {editingLessonId && (() => {
                          const editingLesson = lessons.find((item) => item.id === editingLessonId);
                          const remainingAttachments = (editingLesson?.attachments || []).filter(
                            (attachment) => !removedAttachmentUrls.includes(attachment.url)
                          );
                          return (
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Existing Attachments</label>
                              {remainingAttachments.length === 0 ? (
                                <p className="text-[10px] text-slate-400">No attachments retained.</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {remainingAttachments.map((attachment, index) => (
                                    <div key={`${attachment.url}-${index}`} className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                                      <a href={attachment.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:text-blue-800 underline break-all">
                                        {attachment.name || 'Attachment'}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveAttachmentFromLesson(attachment.url)}
                                        className="text-[9px] font-bold uppercase tracking-wider text-red-600 hover:text-red-700"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}                          </div>
                        </section>
                        </div>
                        </div>
                        <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
                          <button type="button" onClick={() => { setShowLessonModal(false); setEditingLessonId(''); setDuplicatingLessonDate(''); setLessonUploadFiles([]); setRemovedAttachmentUrls([]); }} className="px-6 text-xs font-bold py-2 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors uppercase tracking-widest">Cancel</button>
                          <button type="submit" className="px-6 bg-slate-900 hover:bg-slate-950 text-white font-bold text-xs py-2 rounded transition-colors uppercase tracking-widest flex items-center justify-center gap-1"><Plus className="w-3.5 h-3.5" /><span>{editingLessonId ? 'Update Plan' : 'Save Plan'}</span></button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}




              </div>
            );
          })()}

          {activeTab === 'journal' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-150 pb-5 mb-6">
                  <div className="flex items-center space-x-2 group cursor-pointer">
                    <FileText className="w-4 h-4 text-slate-950 group-hover:rotate-3 transition-transform duration-200" />
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Teacher Journal</h3>
                      <p className="text-[11px] text-slate-400">Daily notes with a full-page WYSIWYG editor and searchable tags</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleNewJournalDraft}
                    className="self-start rounded bg-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800 transition-colors"
                  >
                    New Entry
                  </button>
                </div>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                  <div className="xl:col-span-4 space-y-4">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                      <div>
                        <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Search Journals</label>
                        <input
                          type="text"
                          value={journalSearch}
                          onChange={(e) => setJournalSearch(e.target.value)}
                          placeholder="Search text or tags"
                          className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-slate-400"
                        />
                      </div>
                      <div>
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-2">Tags</p>
                        <div className="flex flex-wrap gap-2">
                          {journalTags.length === 0 ? (
                            <span className="text-[11px] text-slate-400">No tags saved yet.</span>
                          ) : (
                            journalTags.map((tag) => (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => setJournalSearch((current) => current === tag ? '' : tag)}
                                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${journalSearch === tag ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900'}`}
                              >
                                #{tag}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                      <div className="border-b border-slate-200 px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Entries</p>
                      </div>
                      <div className="max-h-[580px] overflow-y-auto divide-y divide-slate-150">
                        {visibleJournals.length === 0 ? (
                          <p className="px-4 py-8 text-center text-xs text-slate-400">No journal entries match this search.</p>
                        ) : (
                          visibleJournals.map((entry) => (
                            <button
                              key={entry.id}
                              type="button"
                              onClick={() => handleSelectJournal(entry)}
                              className={`w-full px-4 py-3 text-left transition-colors ${selectedJournalId === entry.id ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className={`text-[10px] font-bold uppercase tracking-wider ${selectedJournalId === entry.id ? 'text-slate-300' : 'text-slate-400'}`}>{entry.date || 'Undated'}</p>
                                  <h4 className="mt-1 truncate text-xs font-bold uppercase tracking-wide">{entry.title || 'Untitled entry'}</h4>
                                  <p className={`mt-1 line-clamp-2 text-[11px] ${selectedJournalId === entry.id ? 'text-slate-200' : 'text-slate-500'}`}>{entry.body || 'No content yet.'}</p>
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {(entry.tags || []).map((tag) => (
                                      <span key={`${entry.id}-${tag}`} className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${selectedJournalId === entry.id ? 'border-slate-600 text-slate-200' : 'border-slate-200 text-slate-500'}`}>
                                        #{tag}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteJournal(entry.id);
                                  }}
                                  className={`shrink-0 cursor-pointer text-[10px] font-bold uppercase tracking-wider ${selectedJournalId === entry.id ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:text-red-600'}`}
                                >
                                  Delete
                                </span>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="xl:col-span-8 space-y-6">
                    <div className="rounded-lg border border-slate-200 bg-white p-5">
                      <div className="flex items-center justify-between gap-3 border-b border-slate-150 pb-4">
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">{selectedJournalId ? 'Selected Journal Entry' : 'Current Draft'}</h4>
                          <p className="text-[11px] text-slate-400 mt-1">Open the full-page editor to write with the WYSIWYG toolbar.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setIsJournalEditorOpen(true)}
                            className="rounded border border-slate-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            Open Full Editor
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveJournal}
                            className="rounded bg-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800 transition-colors"
                          >
                            Save Journal
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Title</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{journalForm.title || 'Untitled journal entry'}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Date</p>
                          <p className="mt-1 text-slate-700">{journalForm.date || 'No date selected'}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Tags</p>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {normalizeJournalTags(journalForm.tags).length === 0 ? (
                              <span className="text-slate-400">No tags yet.</span>
                            ) : normalizeJournalTags(journalForm.tags).map((tag) => (
                              <span key={tag} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <div className="border-b border-slate-200 pb-3 mb-4 flex items-center justify-between gap-3">
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Preview</h4>
                          <p className="text-[11px] text-slate-400 mt-1">Rendered journal content for the current draft.</p>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          {normalizeJournalTags(journalForm.tags).map((tag) => (
                            <span key={tag} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-3 min-h-[220px]">
                        {Array.isArray(journalForm.transcriptChunks) && journalForm.transcriptChunks.length ? (
                          <details open className="rounded border border-slate-200 bg-white p-3">
                            <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-wider text-slate-600">
                              Google transcript · {journalForm.transcriptChunks.length} intervals
                            </summary>
                            <div className="mt-3 max-h-56 space-y-3 overflow-y-auto">
                              {journalForm.transcriptChunks.map((chunk) => (
                                <div key={`journal-preview-transcript-${chunk.index}`} className="border-l-2 border-slate-200 pl-3">
                                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                                    {formatRecordingTime(chunk.startMs)}–{formatRecordingTime(chunk.endMs)}
                                  </p>
                                  <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">{chunk.text}</p>
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}
                        {journalForm.journalAiSummary ? (
                          <div className="rounded border border-indigo-200 bg-white p-3">
                            <p className="text-[9px] font-bold uppercase tracking-wider text-indigo-700">AI summary</p>
                            <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">{journalForm.journalAiSummary}</p>
                          </div>
                        ) : null}
                        {renderJournalContent(journalForm.bodyHtml, journalForm.body)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          )}

          {activeTab === 'attendance' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-150 pb-5 mb-6">
                  <div className="flex items-center space-x-2 group cursor-pointer">
                    <UserCheck className="w-4 h-4 text-slate-950 group-hover:scale-110 transition-transform duration-250" />
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Attendance Register</h3>
                      <p className="text-[11px] text-slate-400">Class ledger and daily check-in logs</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div>
                      <label className="block text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-1">Class</label>
                      <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)} className="bg-slate-50 border border-slate-200 text-slate-800 py-1.5 px-3 rounded text-xs font-bold focus:outline-none transition-colors">
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-1">Session Date</label>
                      <input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} className="bg-slate-50 border border-slate-200 text-slate-800 py-1 px-3 rounded text-xs font-bold focus:outline-none" />
                    </div>
                    <button type="button" onClick={handleMarkAllPresent} className="self-start text-[10px] font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded transition-colors">Mark all present</button>
                    <button type="button" onClick={handleUnmarkAll} className="self-start text-[10px] font-bold uppercase tracking-wider bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded transition-colors">Unmark all</button>
                  </div>
                </div>
                {!activeClass ? (
                  <div className="text-center py-12"><p className="text-xs text-slate-400 uppercase tracking-widest">No active class selection.</p></div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 grid grid-cols-3 gap-4 text-center">
                      <div className="group cursor-pointer"><span className="text-[9px] font-bold text-slate-400 block uppercase tracking-wider group-hover:text-emerald-600 transition-colors">Present</span><span className="text-lg font-bold text-slate-800 group-hover:scale-105 inline-block transition-transform">{activeClass.students.filter((s) => getAttendanceStatus(s.id) === 'present').length}</span></div>
                      <div className="group cursor-pointer"><span className="text-[9px] font-bold text-slate-400 block uppercase tracking-wider group-hover:text-amber-500 transition-colors">Late</span><span className="text-lg font-bold text-slate-800 group-hover:scale-105 inline-block transition-transform">{activeClass.students.filter((s) => getAttendanceStatus(s.id) === 'late').length}</span></div>
                      <div className="group cursor-pointer"><span className="text-[9px] font-bold text-slate-400 block uppercase tracking-wider group-hover:text-red-500 transition-colors">Absent</span><span className="text-lg font-bold text-slate-800 group-hover:scale-105 inline-block transition-transform">{activeClass.students.filter((s) => getAttendanceStatus(s.id) === 'absent').length}</span></div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-slate-200 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            <th className="py-3 px-4">Student Name</th>
                            <th className="py-3 px-4">Status</th>
                            <th className="py-3 px-4">Comment</th>
                            <th className="py-3 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-150 text-xs">
                          {activeClass.students.map((student) => {
                            const currentStatus = getAttendanceStatus(student.id);
                            const currentComment = getAttendanceComment(student.id);
                            return (
                              <tr key={student.id} className="hover:bg-slate-50/50">
                                <td className="py-3 px-4 font-bold text-slate-800">{student.name}</td>
                                <td className="py-3 px-4">
                                  {currentStatus === 'present' && (<span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 anim-pulse-active">Present</span>)}
                                  {currentStatus === 'late' && (<span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">Late</span>)}
                                  {currentStatus === 'absent' && (<span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-800 border border-red-200">Absent</span>)}
                                  {currentStatus === 'pending' && (<span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500">Unmarked</span>)}
                                </td>
                                <td className="py-3 px-4 min-w-[220px]">
                                  <textarea
                                    value={currentComment}
                                    onChange={(e) => handleAttendanceCommentChange(student.id, e.target.value)}
                                    rows="1"
                                    placeholder="Add a note for this student on this day"
                                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] leading-5 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-400"
                                  />
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <div className="inline-flex rounded shadow-sm">
                                    <button type="button" onClick={() => handleMarkAttendance(student.id, 'present')} className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${currentStatus === 'present' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>Present</button>
                                    <button type="button" onClick={() => handleMarkAttendance(student.id, 'late')} className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider border-t border-b transition-colors ${currentStatus === 'late' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>Late</button>
                                    <button type="button" onClick={() => handleMarkAttendance(student.id, 'absent')} className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors ${currentStatus === 'absent' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>Absent</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-end pt-2 border-t border-slate-200"><p className="text-[10px] text-slate-400 uppercase tracking-widest">Updates sync in real-time.</p></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'homework' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="border-b border-slate-150 pb-4 mb-6">
                  <div className="flex items-center space-x-2 group cursor-pointer">
                    <ClipboardList className="w-4 h-4 text-slate-950 group-hover:translate-x-0.5 transition-transform" />
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Homework Deliverables</h3>
                      <p className="text-sm text-slate-400">Assignment distribution lists and hand-in status</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-4 bg-slate-50 p-5 rounded-lg border border-slate-200 h-fit">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center space-x-2"><Plus className="w-3.5 h-3.5 text-slate-950" /><span>Issue Assignment</span></h4>
                    <form onSubmit={handleCreateAssignment} className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Target Class</label>
                        <select value={selectedClassId || activeClass?.id || ''} onChange={(e) => setSelectedClassId(e.target.value)} className="w-full bg-white border border-slate-200 text-slate-800 py-2 px-3 rounded text-xs font-bold focus:outline-none transition-colors" disabled={classes.length === 0}>
                          {classes.length === 0 ? (
                            <option value="">No classes available</option>
                          ) : (
                            classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Title</label>
                        <input type="text" placeholder="e.g., Chapter 4 Assessment" value={homeworkForm.title} onChange={(e) => setHomeworkForm({ ...homeworkForm, title: e.target.value })} className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" required />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Max Score</label>
                          <input type="number" placeholder="Points" value={homeworkForm.maxPoints} onChange={(e) => setHomeworkForm({ ...homeworkForm, maxPoints: e.target.value })} className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Due Date (Optional)</label>
                          <input type="date" value={homeworkForm.dueDate} onChange={(e) => setHomeworkForm({ ...homeworkForm, dueDate: e.target.value })} className="w-full bg-white border border-slate-200 text-slate-800 text-xs px-3 py-2 rounded focus:border-slate-950 focus:outline-none" />
                        </div>
                      </div>
                      <button type="submit" className="w-full bg-slate-900 text-white text-xs font-bold py-2 rounded hover:bg-slate-950 transition-colors uppercase tracking-widest flex items-center justify-center space-x-1 group"><Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-250" /><span>Publish Task</span></button>
                    </form>
                  </div>
                  <div className="lg:col-span-8 space-y-4">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                      <div>
                        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Import Homework from Google Sheets</h4>
                        <p className="mt-1 text-[10px] text-slate-500">Matches classes by the Class column and students by email. Numeric-majority columns use score mode; text cells in those columns become N.A.</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="url"
                          value={homeworkSheetUrl}
                          onChange={(e) => setHomeworkSheetUrl(e.target.value)}
                          className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-3 py-2 text-[10px] text-slate-700 focus:outline-none focus:border-slate-400"
                          placeholder="Google Sheets URL"
                        />
                        <button type="button" disabled={isImportingHomeworkSheet} onClick={handleLoadHomeworkSheet} className="rounded border border-slate-300 bg-white px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-100 disabled:opacity-50">
                          Load Sheet
                        </button>
                      </div>
                      <input type="file" accept=".csv,text/csv" onChange={handleHomeworkSheetFile} className="block w-full text-[10px] text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-700" />
                      {homeworkSheetImportErrors.length > 0 && <div className="rounded border border-red-200 bg-red-50 p-2 text-[10px] text-red-700">{homeworkSheetImportErrors.join(' ')}</div>}
                      {homeworkSheetImportLog && <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-[10px] text-emerald-700">{homeworkSheetImportLog}</div>}
                      <button type="button" disabled={isImportingHomeworkSheet || !homeworkSheetCsv.trim()} onClick={handleImportHomeworkSheet} className="w-full rounded bg-slate-900 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800 disabled:opacity-50">
                        {isImportingHomeworkSheet ? 'Working…' : 'Import into Database'}
                      </button>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Assigned Tasks: <span className="text-slate-500 font-semibold">{activeClass?.name}</span></h4>
                      <button
                        type="button"
                        onClick={handleDownloadHomeworkCsv}
                        disabled={!activeClass || homework.filter((item) => item.classId === activeClass?.id).length === 0}
                        className="inline-flex items-center justify-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download Class CSV
                      </button>
                    </div>
                    {homework.filter((h) => h.classId === activeClass?.id).length === 0 ? (
                      <div className="text-center py-20 bg-slate-50 rounded-lg border border-dashed border-slate-200"><p className="text-xs text-slate-400 uppercase tracking-widest">No active tasks assigned</p></div>
                    ) : (
                      <div className="space-y-3">
                        <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
                          <h5 className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">Edit One Task</h5>
                          <div>
                            <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Select Task</label>
                            <select
                              value={selectedHomeworkEditId}
                              onChange={(e) => {
                                const nextId = e.target.value;
                                setSelectedHomeworkEditId(nextId);
                                const selected = homework.find((h) => h.id === nextId);
                                if (selected) {
                                  setHomeworkEditForm({
                                    title: selected.title || '',
                                    dueDate: selected.dueDate || '',
                                    maxPoints: selected.maxPoints || '10',
                                    trackingMode: getHomeworkTrackingMode(selected)
                                  });
                                } else {
                                  setHomeworkEditForm({ title: '', dueDate: '', maxPoints: '10', trackingMode: 'score' });
                                }
                              }}
                              className="w-full rounded border border-slate-200 bg-white px-2 py-2 text-[10px] font-bold text-slate-700 uppercase tracking-wider focus:outline-none"
                            >
                              <option value="">Select task to edit</option>
                              {homework.filter((h) => h.classId === activeClass?.id).map((hw) => (
                                <option key={hw.id} value={hw.id}>{hw.title}</option>
                              ))}
                            </select>
                          </div>

                          <form onSubmit={handleSaveHomeworkEdit} className="space-y-2">
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Title</label>
                              <input
                                type="text"
                                value={homeworkEditForm.title}
                                onChange={(e) => setHomeworkEditForm((prev) => ({ ...prev, title: e.target.value }))}
                                className="w-full rounded border border-slate-200 bg-white px-2 py-2 text-[10px] text-slate-800 focus:outline-none"
                                disabled={!selectedHomeworkEditItem}
                                required
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Due Date (Optional)</label>
                                <input
                                  type="date"
                                  value={homeworkEditForm.dueDate}
                                  onChange={(e) => setHomeworkEditForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                                  className="w-full rounded border border-slate-200 bg-white px-2 py-2 text-[10px] text-slate-800 focus:outline-none"
                                  disabled={!selectedHomeworkEditItem}
                                />
                              </div>
                              <div>
                                <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Max Score</label>
                                <input
                                  type="number"
                                  value={homeworkEditForm.maxPoints}
                                  onChange={(e) => setHomeworkEditForm((prev) => ({ ...prev, maxPoints: e.target.value }))}
                                  className="w-full rounded border border-slate-200 bg-white px-2 py-2 text-[10px] text-slate-800 focus:outline-none"
                                  disabled={!selectedHomeworkEditItem}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Tracking Mode</label>
                              <select
                                value={homeworkEditForm.trackingMode}
                                onChange={(e) => setHomeworkEditForm((prev) => ({ ...prev, trackingMode: e.target.value }))}
                                className="w-full rounded border border-slate-200 bg-white px-2 py-2 text-[10px] font-bold text-slate-700 uppercase tracking-wider focus:outline-none"
                                disabled={!selectedHomeworkEditItem}
                              >
                                <option value="score">Score</option>
                                <option value="status">Status</option>
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <button type="submit" disabled={!selectedHomeworkEditItem} className="flex-1 rounded bg-slate-900 px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Save Task</button>
                              <button type="button" disabled={!selectedHomeworkEditItem} onClick={() => selectedHomeworkEditItem && handleDeleteAssignment(selectedHomeworkEditItem.id)} className="rounded border border-red-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Delete</button>
                            </div>
                          </form>
                        </div>
                        {homework.filter((h) => h.classId === activeClass?.id).length > 0 && (
                          <div>
                            <div className="rounded border border-slate-200 bg-white overflow-x-auto">
                              <table className="w-full text-left border-collapse text-[10px]">
                                <thead>
                                  <tr className="border-b border-slate-200 bg-slate-50">
                                    <th className="px-4 py-3 font-bold text-slate-700 uppercase tracking-wider sticky left-0 bg-slate-50 z-10 min-w-[150px]">Student</th>
                                    {homework.filter((h) => h.classId === activeClass?.id).map((hw) => (
                                      <th key={hw.id} className="px-3 py-3 font-bold text-slate-700 uppercase tracking-wider text-center border-l border-slate-200 min-w-[120px]">
                                        <div className="font-bold">{hw.title}</div>
                                        <div className="text-[9px] text-slate-500 font-normal">{getHomeworkTrackingMode(hw) === 'status' ? 'Status' : 'Score'}</div>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {activeClass.students.map((student) => (
                                    <tr key={student.id} className="border-b border-slate-150 hover:bg-slate-50/50">
                                      <td className="px-4 py-3 font-bold text-slate-800 sticky left-0 bg-white hover:bg-slate-50/50 z-10">{student.name}</td>
                                      {homework.filter((h) => h.classId === activeClass?.id).map((hw) => {
                                        const submission = getHomeworkSubmission(hw, student.id);
                                        return (
                                          <td key={`${hw.id}-${student.id}`} className="px-3 py-2 border-l border-slate-200">
                                            {getHomeworkTrackingMode(hw) === 'status' ? (
                                              <select value={submission.status || 'unsubmitted'} onChange={(e) => handleUpdateSubmission(hw.id, student.id, { status: e.target.value, score: submission.score || '' })} className="w-full bg-white border border-slate-200 text-slate-700 rounded px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider focus:outline-none focus:border-slate-400">
                                                <option value="submitted">Submitted</option>
                                                <option value="late">Late</option>
                                                <option value="unsubmitted">Unsubmitted</option>
                                              </select>
                                            ) : (
                                              <div className="flex items-center gap-1">
                                                {submission.score === 'N.A.' ? (
                                                  <span className="inline-flex h-[30px] w-16 items-center justify-center rounded border border-slate-200 bg-slate-100 text-[9px] font-bold text-slate-500">N.A.</span>
                                                ) : (
                                                  <input type="number" min="0" max={hw.maxPoints || 100} value={submission.score ?? ''} onChange={(e) => handleUpdateSubmission(hw.id, student.id, { status: submission.status || 'unsubmitted', score: e.target.value })} className="w-16 bg-white border border-slate-200 text-slate-700 rounded px-2 py-1.5 text-[9px] font-bold text-center focus:outline-none focus:border-slate-400" placeholder="—" />
                                                )}
                                                <span className="text-[9px] text-slate-400 font-normal">/{hw.maxPoints}</span>
                                              </div>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {selectedHomeworkEditItem && getHomeworkTrackingMode(selectedHomeworkEditItem) === 'score' && (
                              <div className="mt-3 space-y-2">
                                <div>
                                  <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Paste scores for {selectedHomeworkEditItem.title}</label>
                                  <textarea
                                    value={homeworkScorePaste[selectedHomeworkEditItem.id] || ''}
                                    onChange={(e) => setHomeworkScorePaste((prev) => ({ ...prev, [selectedHomeworkEditItem.id]: e.target.value }))}
                                    rows="2"
                                    placeholder="Paste scores (one per line) or Name,Score"
                                    className="w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-[9px] text-slate-700 focus:outline-none focus:border-slate-400"
                                  />
                                </div>
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const scoreText = homeworkScorePaste[selectedHomeworkEditItem.id] || '';
                                      if (!scoreText.trim()) {
                                        showToast('Paste scores first.', 'error');
                                        return;
                                      }
                                      handleBatchPasteScores(selectedHomeworkEditItem);
                                    }}
                                    className="rounded bg-slate-900 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-white hover:bg-slate-800 transition-colors"
                                  >
                                    Apply Pasted Scores
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      {renderForumLinkModal()}
      {renderJournalEditorModal()}
      {renderForumAiThreadPromptModal()}
      {renderForumAiModal()}
      <ExpandedImageViewer image={expandedSubmissionImage} onClose={() => setExpandedSubmissionImage(null)} />
      </div>

      <footer className="bg-white border-t border-slate-200 py-6 text-center text-[10px] text-slate-400 uppercase tracking-widest">
        <p>© 2026 PhyHub Physics Lens • The World in a Different Light</p>
      </footer>
    </div>
  );
}
